  import * as THREE from 'three';
  import { OrbitControls }    from 'three/addons/controls/OrbitControls.js';
  import { GLTFLoader }       from 'three/addons/loaders/GLTFLoader.js';
  import { DRACOLoader }      from 'three/addons/loaders/DRACOLoader.js';
  import { RGBELoader }       from 'three/addons/loaders/RGBELoader.js';

  // Config & loaders
  const config = JSON.parse(document.getElementById('fd-config').textContent);
  const SID = config.sectionId;

  // A safe area can't be bigger than its template: anything past the template is
  // cut off and the guide stops at its edge. Clamp max_* to the template once, so
  // layout, guide, preview and export all use the same area.
  for (const b of Object.values(config.bottles || {})) {
    const { engraving: egv, color_print: uv } = b.personalizationSettings || {};
    const clamp = (p, k, limit) => {
      if (!(limit > 0) || !(p[k] > limit)) return;
      console.warn(`[FlexiDesign] ${p.key}: ${k} ${p[k]} exceeds the template (${limit}mm) — using ${limit}mm`);
      p[k] = limit;
    };
    for (const p of egv?.positions || []) { clamp(p, 'max_height_mm', p.height_mm); clamp(p, 'max_width_mm', p.width_mm); }
    for (const p of uv?.positions || []) clamp(p, 'max_height_mm', uv.height_mm);
  }

  const gltfLoader  = new GLTFLoader();
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
  gltfLoader.setDRACOLoader(dracoLoader);
  const texLoader = new THREE.TextureLoader();

  // Three.js globals
  let scene, camera, renderer, controls;
  let bottleGroup = null;
  let _metalEnvMap = null;  // HDR env, metallic materials only.
  let _glossEnvMap = null;  // Synthetic 4-stripe env, gloss overlay only.
  let _artFirstEntry = true; // First Art entry per bottle → first configured position.

  // First selectable colour across ALL bottle products: prefer an available +
  // textured variant; fall back to any textured variant (so the swatch still
  // highlights even when the only colour is out of stock). Searching every
  // product — and returning its productId too — means the initial selection is
  // always a swatch that's actually shown in the grid (it gets the active ring),
  // instead of the product's raw first Shopify variant which may be hidden.
  function firstSelectableVariant() {
    let textured = null;
    for (const [pid, p] of Object.entries(config.bottles || {})) {
      for (const [vid, v] of Object.entries(p.variants || {})) {
        if (!v.hasTexture) continue;
        if (v.available) return { productId: pid, variantId: vid };
        if (!textured) textured = { productId: pid, variantId: vid };
      }
    }
    return textured;
  }

  // Deep-link from a product/quick-view "Customise" button carries ?variant=<id>
  // (appended in bl-custom.js). Pre-select that exact bottle variant when it exists
  // in this configurator AND is customisable (textured) AND in stock; otherwise fall
  // back to the first selectable variant — so an out-of-stock or unknown variant
  // lands on the first option instead.
  function initialBottleSelection() {
    let deepLinkId = null;
    try { deepLinkId = new URLSearchParams(window.location.search).get('variant'); } catch (e) {}
    if (deepLinkId) {
      for (const [pid, p] of Object.entries(config.bottles || {})) {
        const v = p.variants?.[deepLinkId];
        if (v && v.available && v.hasTexture) return { productId: pid, variantId: deepLinkId };
      }
    }
    return firstSelectableVariant();
  }

  // App state
  const _initSel = initialBottleSelection();
  const state = {
    bottleProductId: _initSel?.productId ?? config.initialState.bottleProductId,
    bottleVariantId: _initSel?.variantId ?? config.initialState.bottleVariantId,
    accessories: { ...config.initialState.accessories },
    quantity: 1,
  };

  // Model URL currently attached to each accessory anchor. updateVariant uses it
  // to skip a re-attach with the SAME GLB, so changing a cap/boot (or default
  // accessory) COLOUR — same model, new texture — never reloads the mesh; only
  // changing the TYPE (a different model URL) actually swaps the GLB.
  const _anchorModel = {};

  // Serialise async ops so rapid clicks don't race
  let opQueue = Promise.resolve();
  const enqueue = (fn) => { opQueue = opQueue.then(fn); return opQueue; };

  // Returns accessories config for the currently selected bottle
  function currentAccData() {
    return config.bottles[state.bottleProductId]?.accessories || {};
  }

  // Detects which AR pathway is usable on this device. Returns { type, url } or null.
  // Android → Scene Viewer (GLB). iOS → AR Quick Look (USDZ, when configured).
  function _arEntryPoint() {
    const ua = navigator.userAgent || '';
    const bottle = config.bottles[state.bottleProductId];
    if (/Android/i.test(ua) && bottle?.model) return { type: 'android', url: bottle.model };
    const link = document.createElement('a');
    if (/iPhone|iPad|iPod/i.test(ua) && link.relList?.supports?.('ar') && bottle?.usdzModel) {
      return { type: 'ios', url: bottle.usdzModel };
    }
    return null;
  }
  function launchAR() {
    const ep = _arEntryPoint();
    if (!ep) return;
    if (ep.type === 'android') {
      const absUrl = new URL(ep.url, location.href).href;
      window.location.href = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(absUrl)}&mode=ar_preferred#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(location.href)};end;`;
      return;
    }
    const a = document.createElement('a');
    a.rel = 'ar';
    a.href = ep.url;
    a.appendChild(document.createElement('img'));
    a.click();
  }

  // Returns the correct preview GLB for a given accessory type
  // Priority: bottle-variant-specific preview > block-level preview > null
  function getPreviewGlb(type) {
    const acc = currentAccData()[type];
    if (!acc) return null;
    const variantData = config.bottles[state.bottleProductId]?.variants[state.bottleVariantId];
    return variantData?.accessoryPreview || acc.previewGlb || null;
  }

  // Removes whatever model is attached to an anchor
  function clearAnchor(anchorName) {
    delete _anchorModel[anchorName];
    const root = bottleGroup || scene;
    // Accessories attach to the bottle root as `__sub_<anchor>` (see updateVariant),
    // so remove that node directly wherever it lives.
    const prev = root?.getObjectByName(`__sub_${anchorName}`) || scene.getObjectByName(`__sub_${anchorName}`);
    if (prev) prev.parent?.remove(prev);
  }

  // Reference-counted so overlapping loads (e.g. a colour change whose re-skin also
  // re-renders the engraving) don't switch the spinner off early. Also locks the
  // ADD TO CART while anything is loading — non-clickable (pointer-events), NOT faded
  // (see the .is-atc-locked CSS + the add-to-cart click guard).
  let _loadingCount = 0;
  function toggleLoading(active) {
    _loadingCount = Math.max(0, _loadingCount + (active ? 1 : -1));
    const on = _loadingCount > 0;
    document.getElementById('fd-spinner')?.classList.toggle('is-visible', on);
    document.getElementById('fd-layout')?.classList.toggle('is-loading', on);
    _sectionRoot?.querySelector('.fd-card')?.classList.toggle('is-atc-locked', on);
  }

  // ── First-load screen ───────────────────────────────────────────────────
  let _dotsTimer = null;
  function setLoadingProgress(frac) {
    const pct = Math.max(0, Math.min(100, Math.round(frac * 100)));
    const fill = document.getElementById('fd-loading-fill');
    const lbl  = document.getElementById('fd-loading-pct');
    const brand = document.getElementById('fd-loading-brand');
    if (fill)  fill.style.width = pct + '%';
    if (lbl)   lbl.textContent  = pct + '%';
    // Logo colour tracks the bar's sweep position, not the raw %: it only starts
    // filling once the bar's edge reaches the logo's left edge (≈centre of the
    // track) and completes at its right edge. Geometry is read live so it stays
    // correct at any viewport size.
    if (brand && fill) {
      const barRect  = fill.parentElement.getBoundingClientRect();
      const logoRect = brand.getBoundingClientRect();
      const sweepX = barRect.left + (pct / 100) * barRect.width;
      const lf = (sweepX - logoRect.left) / (logoRect.width || 1);
      brand.style.setProperty('--fd-fill', Math.max(0, Math.min(1, lf)) * 100 + '%');
    }
  }
  // Status text. animated=true cycles a trailing . → .. → ... loop; pass false
  // for a static message (e.g. "Done").
  function setLoadingPhase(text, animated = true) {
    const el = document.getElementById('fd-loading-label');
    clearInterval(_dotsTimer); _dotsTimer = null;
    if (!el) return;
    if (!animated) { el.textContent = text; return; }
    let n = 1;
    const render = () => { el.textContent = text + '.'.repeat(n); n = n % 3 + 1; };
    render();
    _dotsTimer = setInterval(render, 400);
  }
  function hideLoadingScreen() {
    clearInterval(_dotsTimer); _dotsTimer = null;
    setLoadingProgress(1);
    document.getElementById('fd-loading')?.classList.add('is-hidden');
  }

  // Studio HDR → metallic envMap. Promise so the preloader can await it;
  // resolves (not rejects) on failure so a flaky CDN can't trap the loader.
  function loadHDR() {
    return new Promise((resolve) => {
      new RGBELoader().load(
        'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/studio_small_09_1k.hdr',
        (hdrTex) => {
          const metalPmrem = new THREE.PMREMGenerator(renderer);
          _metalEnvMap = metalPmrem.fromEquirectangular(hdrTex).texture;
          metalPmrem.dispose();
          hdrTex.dispose();
          if (bottleGroup) tuneEnvForGroup(bottleGroup);
          scene.traverse(o => { if (o.name?.startsWith('__sub_')) tuneEnvForGroup(o); });
          resolve();
        },
        undefined,
        () => resolve()
      );
    });
  }

  // Preload every GLB (bottle + all cap/boot models & previews) plus the HDR,
  // reporting real per-file progress. Warms _modelCache so later swaps are
  // instant. Fonts / icons / decoration textures stay lazy (loaded on demand).
  function preloadAssets(onProgress, onPhase) {
    const b = config.bottles[state.bottleProductId];
    const urls = new Set();
    if (b?.model) urls.add(b.model);
    for (const acc of Object.values(b?.accessories || {})) {
      if (acc.previewGlb) urls.add(acc.previewGlb);
      for (const g of Object.values(acc.products || {})) {
        if (g.model) urls.add(g.model);
        for (const v of Object.values(g.variants || {})) {
          if (v.accessoryPreview) urls.add(v.accessoryPreview);
        }
      }
    }
    const glbUrls = [...urls];
    const total = glbUrls.length + 1; // +1 for the HDR
    let done = 0;
    const tick = () => { done++; onProgress(done / total); };
    // Phase 1: all GLBs (in parallel). Phase 2: the HDR.
    onPhase('Preparing your bottle');
    return Promise.all(glbUrls.map(u => loadModel(u).then(tick, tick)))
      .then(() => { onPhase('Just a moment'); return loadHDR().then(tick, tick); });
  }

  // GLB cache: raw scenes keyed by URL. loadModel clones on read.
  // In-flight map prevents duplicate concurrent fetches.
  const _modelCache   = new Map(); // url → THREE.Group (raw)
  const _modelPending = new Map(); // url → Promise<THREE.Group> (raw)

  function loadModel(url) {
    if (_modelCache.has(url)) {
      return Promise.resolve(_modelCache.get(url).clone());
    }
    if (_modelPending.has(url)) {
      return _modelPending.get(url).then(raw => raw.clone());
    }
    const promise = new Promise((resolve, reject) => {
      gltfLoader.load(url, (gltf) => {
        _modelCache.set(url, gltf.scene);
        _modelPending.delete(url);
        resolve(gltf.scene);
      }, undefined, (err) => {
        _modelPending.delete(url);
        reject(err);
      });
    });
    _modelPending.set(url, promise);
    return promise.then(raw => raw.clone());
  }

  // Apply the studio HDR envMap to UNIFORMLY metallic materials only so they read
  // as metal. Everything else is cleared to null, which makes three.js fall back to
  // the uniform white scene.environment — a featureless map whose reflection can't
  // slide as the model spins. Two cases fall back: (1) non-metallic materials
  // (metalness < 0.5), and (2) materials with a metalnessMap — the metal is MASKED
  // per-pixel (e.g. a metallic logo on a matte body); a single material shares one
  // envMap, so the world-fixed studio HDR would also light the NON-metal pixels.
  // Falling back to the uniform env keeps the masked non-metal area clean while the
  // map still makes only the metal pixels read as metal. This also keeps a cap's
  // non-metallic body lit consistently (no "white spot following the cap"). Other
  // material params (roughness, envMapIntensity, etc.) are left as authored.
  function tuneEnvForGroup(group) {
    if (!_metalEnvMap) return;
    group.traverse(o => {
      if (!o.isMesh || !o.material?.isMeshStandardMaterial) return;
      const isMetal = !o.material.metalnessMap && (o.material.metalness ?? 0) >= 0.5;
      o.material.envMap = isMetal ? _metalEnvMap : null;
      o.material.needsUpdate = true;
    });
  }

  async function updateVariant(anchorName, modelUrl, force = false) {
    if (!modelUrl) return;
    if (!force && _anchorModel[anchorName] === modelUrl) return; // same GLB already attached → no reload
    const sub = await loadModel(modelUrl);
    sub.name = `__sub_${anchorName}`;
    tuneEnvForGroup(sub);

    const root = bottleGroup || scene;

    // Remove any accessory already attached for this anchor BEFORE resolving the
    // bottle anchor — the old accessory carries its OWN same-named anchor child,
    // which would otherwise make getObjectByName ambiguous.
    const prevSub = root.getObjectByName(`__sub_${anchorName}`);
    if (prevSub) prevSub.parent?.remove(prevSub);

    const bottleAnchor = root.getObjectByName(anchorName);

    if (bottleAnchor && bottleGroup) {
      // Attach to the BOTTLE ROOT — NOT the anchor node — so the accessory keeps
      // its own GLB scale + orientation and only follows the bottle's transform,
      // never the anchor node's local scale/rotation (a scaled anchor empty would
      // otherwise blow the cap/boot up). Then align by POSITION so the accessory's
      // own same-named anchor sits exactly on the bottle's anchor. This mirrors how
      // the standalone GLB inspector attaches accessories (which renders correctly).
      bottleAnchor.updateWorldMatrix(true, false);
      const targetWorld = bottleAnchor.getWorldPosition(new THREE.Vector3());
      bottleGroup.add(sub);
      sub.updateMatrixWorld(true);
      const subAnchor = sub.getObjectByName(anchorName);
      if (subAnchor && subAnchor !== sub) {
        const subAnchorWorld = subAnchor.getWorldPosition(new THREE.Vector3());
        const targetLocal    = bottleGroup.worldToLocal(targetWorld.clone());
        const subAnchorLocal = bottleGroup.worldToLocal(subAnchorWorld.clone());
        sub.position.add(targetLocal.sub(subAnchorLocal));
      } else {
        // No matching anchor inside the accessory → drop its origin on the anchor.
        sub.position.copy(bottleGroup.worldToLocal(targetWorld.clone()));
      }
    } else {
      console.warn(`updateVariant: anchor "${anchorName}" not found - attaching to scene.`);
      scene.add(sub);
    }
    _anchorModel[anchorName] = modelUrl;
  }

  // Restore every skin-managed mesh under `scope` (minus excludeRoots) to the
  // GLB-authored look captured on first paint, so a mesh the NEXT variant omits
  // (or names without a colour) reverts to base instead of keeping the previous
  // variant's colour/roughness/metalness/alpha/envMap. Only materials the texture
  // system owns (_fdHasOrig) are touched; engraving / UV-print host materials
  // (_fdEngraving) and overlay/guide child meshes (no _fdHasOrig) are left alone
  // so the user's personalization is never wiped.
  function resetSkinToOrig(scope, excludeRoots = []) {
    if (!scope) return;
    scope.traverse((child) => {
      if (!child.isMesh) return;
      const m = child.material;
      if (!m?._fdHasOrig || m._fdEngraving) return;
      if (excludeRoots.length) {
        for (let p = child; p; p = p.parent) if (excludeRoots.includes(p)) return;
      }
      m.map = m._origMap;
      m.color.copy(m._origColor);
      m.roughness    = m._origRoughness;
      m.roughnessMap = m._origRoughnessMap;
      m.metalness    = m._origMetalness;
      m.metalnessMap = m._origMetalnessMap;
      m.opacity      = m._origOpacity;
      m.alphaMap     = m._origAlphaMap;
      m.transparent  = m._origTransparent;
      // Keep envMap consistent with the restored metalness (mirrors tuneEnvForGroup
      // / the per-layer rule): UNIFORMLY metallic → studio HDR; non-metal OR masked
      // (metalnessMap present) → uniform scene.environment.
      m.envMap = (_metalEnvMap && !m._origMetalnessMap && (m._origMetalness ?? 0) >= 0.5) ? _metalEnvMap : null;
      m.needsUpdate = true;
    });
  }

  // Loads all layers in parallel; matches meshes by name (layerNames). Colour:
  // texture image when url is set, else hex color. Roughness: roughnessMap image
  // when set, else the roughness scalar — map and value are mutually exclusive,
  // applying one clears the other. Untouched axes stay as authored in the GLB.
  // excludeRoots: subtrees to leave untouched (e.g. a selected accessory whose
  // own variant texture owns its meshes — keeps the bottle pass from repainting it).
  // scopeRoot: confine the apply + reset to one subtree. undefined → whole bottle
  // (bottle skin = bottle meshes + default accessories). A passed-but-MISSING node
  // (null, e.g. an accessory sub not found) means "scope requested but absent" →
  // do nothing, never widen to the whole bottle (which would repaint the bottle
  // with an accessory's textures).
  async function applyTextureLayers(textureArray, excludeRoots = [], scopeRoot) {
    const root = scopeRoot === undefined ? (bottleGroup || scene) : scopeRoot;
    if (!root) return;
    // Reset stale skin FIRST so a mesh the new variant drops returns to base. Runs
    // even when the new variant has no textures, so a textureless variant clears
    // the previous variant's skin instead of leaving it.
    resetSkinToOrig(root, excludeRoots);
    if (!textureArray || textureArray.length === 0) return;

    function parseLayerNames(layerNames) {
      const list = Array.isArray(layerNames) ? layerNames : String(layerNames || '').split(',');
      return list.map((n) => String(n).trim()).filter(Boolean);
    }

    function isExcluded(node) {
      if (!excludeRoots.length) return false;
      for (let p = node; p; p = p.parent) if (excludeRoots.includes(p)) return true;
      return false;
    }

    function patchMaterials(layerNames, patchFn) {
      root.traverse((child) => {
        if (!child.isMesh || !layerNames.includes(child.name) || isExcluded(child)) return;
        // Never let a skin pass paint over an engraving/print host material — it
        // shares the mesh and painting it would wipe the rendered personalization.
        // Disjoint from skin meshes by authoring convention; this is a safety net
        // (and keeps the apply side symmetric with resetSkinToOrig's _fdEngraving skip).
        if (child.material._fdEngraving) return;

        if (!child.material._fdHasOrig) {
          if (!child.material._isCloned) {
            child.material = child.material.clone();
            child.material._isCloned = true;
          }
          // Snapshot the GLB-authored look ONCE, every axis incl. map/colour, so a
          // variant that omits an axis (or drops the mesh entirely) can restore it
          // instead of inheriting the previous variant's value. color is a
          // THREE.Color object → clone it here, restore via .copy() on reset.
          const m = child.material;
          m._fdHasOrig        = true;
          m._origMap          = m.map;
          m._origColor        = m.color.clone();
          m._origRoughness    = m.roughness;
          m._origRoughnessMap = m.roughnessMap;
          m._origMetalness    = m.metalness;
          m._origMetalnessMap = m.metalnessMap;
          m._origOpacity      = m.opacity;
          m._origAlphaMap     = m.alphaMap;
          m._origTransparent  = m.transparent;
        }
        patchFn(child.material);
      });
    }

    // sRGB for colour maps; linear (NoColorSpace) for data maps like roughness.
    function loadTex(url, colorSpace) {
      return new Promise((resolve, reject) => {
        texLoader.load(url, (tex) => {
          tex.flipY = false;
          tex.colorSpace = colorSpace;
          resolve(tex);
        }, undefined, reject);
      });
    }

    await Promise.all(textureArray.map(async ({ layerNames: rawNames, url, hexCode, roughnessMap, roughness, metalnessMap, metalness, alphaMap, opacity }) => {
      const layerNames = parseLayerNames(rawNames);
      if (layerNames.length === 0) return;

      const [colorTex, roughTex, metalTex, alphaTex] = await Promise.all([
        url ? loadTex(url, THREE.SRGBColorSpace) : null,
        roughnessMap ? loadTex(roughnessMap, THREE.NoColorSpace) : null,
        metalnessMap ? loadTex(metalnessMap, THREE.NoColorSpace) : null,
        alphaMap ? loadTex(alphaMap, THREE.NoColorSpace) : null,
      ]);

      patchMaterials(layerNames, (mat) => {
        if (colorTex)     { mat.map = colorTex; mat.color.set(0xffffff); }
        else if (hexCode) { mat.map = null;     mat.color.set(hexCode); }

        // Map wins over value; the loser is cleared. roughnessMap drives fully
        // (1.0 = neutral multiplier); else the 0–1 scalar sets roughness (0 is a
        // valid, fully glossy value). When a variant supplies neither, restore the
        // GLB's authored roughness so a previous variant's value doesn't stick.
        if (roughTex)               { mat.roughnessMap = roughTex; mat.roughness = 1.0; }
        else if (roughness != null) { mat.roughnessMap = null;     mat.roughness = roughness; }
        else                        { mat.roughnessMap = mat._origRoughnessMap; mat.roughness = mat._origRoughness; }

        // Metalness COEXISTS with the scalar (rendered = scalar × map.B): a
        // black/white mask + a live scalar set the strength without re-baking the
        // map. The scalar defaults to 1.0 (passthrough) when a variant supplies a
        // map but no scalar, so legacy entries render exactly as before. A scalar
        // alone sets metalness directly; neither restores the GLB-authored value.
        if (metalTex)               { mat.metalnessMap = metalTex; mat.metalness = metalness != null ? metalness : 1.0; }
        else if (metalness != null) { mat.metalnessMap = null;     mat.metalness = metalness; }
        else                        { mat.metalnessMap = mat._origMetalnessMap; mat.metalness = mat._origMetalness; }

        // Alpha: map wins over the opacity value (mutually exclusive, like
        // roughness). alphaMap drives the alpha (opacity = 1.0 neutral multiplier);
        // else the 0–1 opacity scalar sets material.opacity. Either needs
        // transparent=true to take effect; opacity 1 (fully opaque) stays opaque.
        // When a variant supplies neither, restore the GLB's authored alpha so a
        // previous variant's value doesn't stick.
        if (alphaTex)             { mat.alphaMap = alphaTex; mat.opacity = 1.0;     mat.transparent = true; }
        else if (opacity != null) { mat.alphaMap = null;     mat.opacity = opacity; mat.transparent = opacity < 1; }
        else                      { mat.alphaMap = mat._origAlphaMap; mat.opacity = mat._origOpacity; mat.transparent = mat._origTransparent; }

        // World-fixed studio HDR on UNIFORMLY metallic layers ONLY, using the
        // metalness just resolved above (not the GLB-authored value). Falls back to
        // the uniform scene.environment when the layer is (a) non-metallic, or (b)
        // uses a metalnessMap — masked metal (e.g. a metallic logo on a matte body):
        // one material shares one envMap, so the studio HDR would otherwise light the
        // NON-metal pixels too. With the uniform env the masked metal still reads as
        // metal while the non-metal area stays clean (no studio reflection on it).
        // (Skipped until the HDR loads — tuneEnvForGroup re-applies once ready.)
        if (_metalEnvMap) mat.envMap = (!mat.metalnessMap && (mat.metalness ?? 0) >= 0.5) ? _metalEnvMap : null;

        mat.needsUpdate = true;
      });
    }));
  }

  // Padding when camera first fits the bottle (also the min visible size).
  // 1.0=no padding, 1.25=current, 1.5=lots of empty space.
  const BOTTLE_BASELINE_PADDING = 1.25;

  // World-space bounding box of the BOTTLE only — excludes attached accessory
  // sub-models (the `__sub_*` cap/boot nodes). The bottle is the main model and
  // must define the centre/rotation pivot on its own; an off-axis cap (e.g. a
  // flip-top handle jutting to one side) must never shift where the bottle sits.
  function bottleOnlyBox() {
    const box = new THREE.Box3();
    if (!bottleGroup) return box;
    bottleGroup.updateWorldMatrix(true, true);
    bottleGroup.traverse(o => {
      if (!o.isMesh) return;
      for (let p = o; p && p !== bottleGroup; p = p.parent) {
        if (p.name && p.name.startsWith('__sub_')) return; // inside an accessory → skip
      }
      box.expandByObject(o);
    });
    return box;
  }

  function setupCamera() {
    if (!bottleGroup) return;
    // Fit distance uses the FULL group (bottle + attached cap/boot) so nothing
    // gets clipped at the baseline framing…
    const box    = new THREE.Box3().setFromObject(bottleGroup);
    const size   = box.getSize(new THREE.Vector3());
    // …but the bottle is re-centred on its OWN bbox, so the cap can't drag the
    // bottle off the world origin. The bottle's vertical axis lands on x=z=0, so
    // it spins around itself; the cap follows via the anchor.
    const center = bottleOnlyBox().getCenter(new THREE.Vector3());

    bottleGroup.position.sub(center);

    const fov    = camera.fov * (Math.PI / 180);
    const aspect = camera.aspect || 1;
    // Distance needed to fit either dimension within the camera frustum.
    const distY  = (size.y / 2) / Math.tan(fov / 2);
    const distX  = (size.x / 2) / Math.tan(fov / 2) / Math.max(aspect, 0.0001);
    const dist   = Math.max(distY, distX);
    // Baseline framing: bottle fits the view with `BOTTLE_BASELINE_PADDING`
    // multiplier of breathing room. Also the hard zoom-out limit.
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    const fitDist = dist * BOTTLE_BASELINE_PADDING * (isMobile ? 1.2 : 1);

    controls.minDistance = fitDist * (isMobile ? 0.3 : 0.5);
    // Hard-cap zoom-out at the baseline so users can never see the bottle
    // smaller than the initial framing.
    controls.maxDistance = fitDist;
    // Lock vertical pivot to the bottle's centre and disable shift-drag panning
    // so the bottle stays in the middle no matter how the user interacts.
    controls.enablePan = false;
    controls.screenSpacePanning = false;

    camera.position.set(0, 0, fitDist);
    camera.lookAt(0, 0, 0);
    controls.target.set(0, 0, 0);
    controls.update();
  }

  function animate() {
    requestAnimationFrame(animate);
    if (_zoom.t < 1) {
      const now = performance.now();
      _zoom.t = Math.min(1, (now - _zoom.startTime) / _zoom.duration);
      const t = 0.5 - 0.5 * Math.cos(_zoom.t * Math.PI);
      // Target glides linearly between pivots.
      controls.target.lerpVectors(_zoom.from.target, _zoom.target.target, t);
      // Camera orbits around the target spherically — radius/elevation/azimuth
      // interpolate independently so a front↔back rotation stays at the same
      // distance instead of cutting through the origin.
      const fs = _zoom.fromSpherical, ts = _zoom.targetSpherical;
      if (fs && ts) {
        const r   = fs.radius + (ts.radius - fs.radius) * t;
        const phi = fs.phi    + (ts.phi    - fs.phi)    * t;
        let dTheta = ts.theta - fs.theta;
        if (dTheta >  Math.PI) dTheta -= Math.PI * 2;
        if (dTheta < -Math.PI) dTheta += Math.PI * 2;
        const theta = fs.theta + dTheta * t;
        const relPos = new THREE.Vector3().setFromSpherical(new THREE.Spherical(r, phi, theta));
        camera.position.copy(controls.target).add(relPos);
      } else {
        camera.position.lerpVectors(_zoom.from.camera, _zoom.target.camera, t);
      }
    }
    controls.update();
    renderer.render(scene, camera);
  }

  async function init() {
    toggleLoading(true);
    try {
      const container = document.getElementById('fd-canvas-container');

      scene  = new THREE.Scene();
      const fov = 2 * THREE.MathUtils.radToDeg(Math.atan(24 / (2 * 80)));
      camera = new THREE.PerspectiveCamera(fov, container.clientWidth / container.clientHeight, 0.1, 1000);

      const _isMobile = window.matchMedia('(max-width: 1023px)').matches;
      // MSAA smooths jagged edges cheaply (much less memory than raising DPR).
      // Mobile still caps DPR at 1.5 to keep the framebuffer small and avoid
      // the OOM tab reload.
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, _isMobile ? 1.5 : 2));
      renderer.toneMapping = THREE.LinearToneMapping;
      // Overall scene brightness. Scales every light + the environment + reflections
      // uniformly (keeps the lighting balance). Lower = dimmer.
      renderer.toneMappingExposure = 0.85;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.VSMShadowMap;
      container.appendChild(renderer.domElement);

      // Uniform white world environment — gives matte materials a clean,
      // subtle ambient with no features that would shift when rotating.
      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      const envScene = new THREE.Scene();
      envScene.background = new THREE.Color(0xffffff);
      scene.environment = pmremGenerator.fromScene(envScene).texture;
      scene.environmentIntensity = 0.5;
      scene.background = window.matchMedia('(max-width: 1023px)').matches ? new THREE.Color(0xffffff) : null;
      pmremGenerator.dispose();

      // HDR (metallic envMap) is preloaded below with the GLBs — see preloadAssets.

      // Synthetic gloss env: 4 soft vertical stripes around middle of a
      // black equirectangular sphere. Blurred for diffuse highlight;
      // uFracs offset 0.125 to keep stripes off the u=0 seam.
      (() => {
        const c = document.createElement('canvas');
        c.width = 512; c.height = 256;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, c.width, c.height);
        const stripeW   = 30;
        const stripeTop = Math.round(c.height * 0.12);
        const stripeBot = Math.round(c.height * 0.88);
        const stripeH   = stripeBot - stripeTop;
        ctx.filter      = 'blur(4px)';
        ctx.fillStyle   = '#ffffff';
        [0.125, 0.375, 0.625, 0.875].forEach(uFrac => {
          const cx = c.width * uFrac;
          ctx.fillRect(cx - stripeW / 2, stripeTop, stripeW, stripeH);
        });
        ctx.filter = 'none';
        const tex = new THREE.CanvasTexture(c);
        tex.mapping    = THREE.EquirectangularReflectionMapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.needsUpdate = true;
        const glossPmrem = new THREE.PMREMGenerator(renderer);
        _glossEnvMap     = glossPmrem.fromEquirectangular(tex).texture;
        glossPmrem.dispose();
        tex.dispose();
      })();

      // Camera-attached light rig — moves with the view so the camera-facing
      // side of the bottle is always brightest. Values match the "natural"
      // preset from the GLB Inspector tool.
      const keyLight = new THREE.DirectionalLight(0xffffff, 2.0);
      keyLight.position.set(3, 5, 4);
      camera.add(keyLight);

      const fillLight = new THREE.DirectionalLight(0xe8f0ff, 0.5);
      fillLight.position.set(4, 3, 4);
      camera.add(fillLight);

      const rimLight = new THREE.DirectionalLight(0xffffff, 0.8);
      rimLight.position.set(0, 3, -6);
      camera.add(rimLight);

      const topLight = new THREE.DirectionalLight(0xffffff, 0.3);
      topLight.position.set(0, 8, 0);
      camera.add(topLight);

      // Ambient is scene-wide (no direction), kept off the camera.
      scene.add(new THREE.AmbientLight(0xffffff, 0.6));

      // Camera owns lights, so it must be added to the scene for them to participate.
      scene.add(camera);

      // Soft contact shadow on the ground. The dedicated shadow light sits
      // directly overhead with VSM blur tuned for a soft, ambient-occlusion
      // look rather than a hard cast shadow.
      const shadowLight = new THREE.DirectionalLight(0xffffff, 0.0);
      shadowLight.position.set(0, 10, 0);
      shadowLight.castShadow = true;
      shadowLight.shadow.mapSize.set(_isMobile ? 1024 : 2048, _isMobile ? 1024 : 2048);
      shadowLight.shadow.camera.near = 0.1;
      shadowLight.shadow.camera.far = 50;
      shadowLight.shadow.camera.left = -4;
      shadowLight.shadow.camera.right = 4;
      shadowLight.shadow.camera.top = 4;
      shadowLight.shadow.camera.bottom = -4;
      shadowLight.shadow.radius = 6;
      shadowLight.shadow.blurSamples = 20;
      shadowLight.shadow.bias = -0.0005;
      scene.add(shadowLight);

      const shadowFloor = new THREE.Mesh(
        new THREE.PlaneGeometry(30, 30),
        // depthWrite:false — the bottle is centred on the origin, so its lower
        // half sits below this y=0 plane. ShadowMaterial is transparent but still
        // writes depth by default, which makes the plane occlude that sub-floor
        // (bottom) half when viewed from above — the bottom "disappears forever"
        // from top angles. A shadow catcher shouldn't write depth; same shadow,
        // same position, no occlusion.
        new THREE.ShadowMaterial({ opacity: 0.15, depthWrite: false })
      );
      shadowFloor.rotation.x = -Math.PI / 2;
      shadowFloor.receiveShadow = true;
      scene.add(shadowFloor);

      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.maxPolarAngle = Math.PI / 2;
      controls.minPolarAngle = 0;
      controls.minDistance   = 0.5;
      controls.maxDistance   = 50;
      // Lock the bottle's vertical axis as the rotation pivot — no shift-drag
      // / two-finger pan to offset the model. setupCamera() refines this.
      controls.enablePan = false;
      controls.screenSpacePanning = false;
      // Keep the "Front / Back" button label in sync with the view as the user
      // rotates. Throttled inside updateRotateViewLabel; fires only on movement.
      controls.addEventListener('change', updateRotateViewLabel);

      // Block first use on all GLBs + HDR (real progress on the loading screen).
      await preloadAssets(setLoadingProgress, setLoadingPhase);

      await loadBottleModel(state.bottleProductId, state.bottleVariantId);

      for (const [type, acc] of Object.entries(currentAccData())) {
        const previewGlb = getPreviewGlb(type);
        if (previewGlb) await updateVariant(acc.anchor, previewGlb);
      }
      await applyBottleTextures();

      setupCamera();
      if (!_arEntryPoint()) {
        document.querySelectorAll(`#shopify-section-${SID} .fd-canvas-ar`).forEach(b => b.hidden = true);
      }
      ui.activeTab = visibleTabs()[0] || 'bottle';
      renderTabStrip();
      showActivePane();
      renderBottlePane();
      renderCapPane();
      renderBootPane();
      renderArtPane();
      refreshPurchaseBar();
      animate();
      zoomToTab(ui.activeTab);
      // Everything ready → show the ready message at 100%, then dismiss after 1s.
      setLoadingProgress(1);
      setLoadingPhase("Let's customise!", false);
      setTimeout(hideLoadingScreen, 1000);
    } catch (err) {
      console.error('init() failed:', err);
      hideLoadingScreen();
    } finally {
      toggleLoading(false);
    }
  }

  // (re)load the bottle base model
  async function loadBottleModel(productId, variantId) {
    const bottleData  = config.bottles[productId];
    const variantData = bottleData?.variants[variantId];
    const modelUrl    = bottleData?.model;
    if (!modelUrl) return;

    if (bottleGroup) scene.remove(bottleGroup);
    bottleGroup = await loadModel(modelUrl);
    scene.add(bottleGroup);
    _artFirstEntry = true;
    tuneEnvForGroup(bottleGroup);
    // Fresh bottleGroup → empty anchors; forget what was attached so accessories
    // re-attach instead of being skipped by updateVariant's same-model guard.
    for (const k in _anchorModel) delete _anchorModel[k];

    // Hide mesh layers configured on the product type (hide_mesh_layers) —
    // part of the model setup, e.g. layers authored in the GLB but unused.
    for (const layerName of (bottleData?.hideMeshLayers || [])) {
      bottleGroup.traverse((child) => {
        if (child.isMesh && child.name === layerName) child.visible = false;
      });
    }

    if (variantData?.textures?.length) {
      await applyTextureLayers(variantData.textures);
    }
    await applyEngravingPreview();
    // Attach the dashed editing guides to the new bottle's engraving meshes
    // and sync visibility to the active tab.
    await ensureGuideOverlays();
    updateGuideVisibility();
  }

  // Bottle variant textures, applied over the whole bottleGroup (incl. attached
  // accessories) so any accessory mesh named in the bottle variant's
  // flexidesign_textures (e.g. the DEFAULT cap) is coloured too. A SELECTED
  // accessory is excluded — its own variant texture owns its meshes (higher
  // priority) — so changing the bottle colour never repaints a chosen cap/boot
  // (no override, no flicker). Run AFTER accessories are attached.
  async function applyBottleTextures() {
    const tex = config.bottles[state.bottleProductId]?.variants[state.bottleVariantId]?.textures;
    if (!tex?.length) return;
    const root = bottleGroup || scene;
    const excludeRoots = [];
    for (const [type, acc] of Object.entries(currentAccData())) {
      if (state.accessories[type]?.variantId == null) continue;
      // Accessories now live under `__sub_<anchor>` on the bottle root (not the
      // anchor node), so exclude THAT subtree from the bottle re-skin.
      const subNode = root?.getObjectByName(`__sub_${acc.anchor}`);
      if (subNode) excludeRoots.push(subNode);
    }
    await applyTextureLayers(tex, excludeRoots);
  }

  // Re-attach all current accessories to the (newly loaded) bottle
  // UI navigation state (active tab, collapse, mobile popover)
  const ui = {
    activeTab: 'bottle',
    collapsed: Object.create(null),
    mobilePopover: { open: false, tab: null, stepIdx: 0, artSubPill: 'text', error: '', posElement: null, posDetailTab: 'layout', artModeAcknowledged: false },
    artCardOpen: 'text',
    guideHidden: true,
    iconViewedSeries: null // which series' icons the row shows (browse only)
  };
  const _isUsable = (v) => !!(v?.available && v?.hasTexture);
  const bottleHasValidVariant = () =>
    _isUsable(config.bottles[state.bottleProductId]?.variants[state.bottleVariantId]);
  const accHasValidVariant = (type) => {
    const cur = state.accessories[type];
    return !!(cur?.productId && cur?.variantId &&
      _isUsable(currentAccData()[type]?.products[cur.productId]?.variants[cur.variantId]));
  };
  function tabHasCheck(tab) {
    if (tab === 'art') return artHasBadge() ? artBadgeDone() : (artHasText() || artHasIcon());
    if (tab === 'bottle') return bottleHasValidVariant();
    return accHasValidVariant(tab);
  }

  // HTML escaping
  function escapeAttr(s) { return String(s ?? '').replace(/[&"<>]/g, c => ({ '&': '&amp;', '"': '&quot;', '<': '&lt;', '>': '&gt;' }[c])); }
  function escapeText(s) { return String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); }

  // Inline SVG placeholders (used where no icon asset is provided yet)
  const PLACEHOLDER_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/></svg>`;
  const ICON_CHEVRON       = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
  const ICON_DIAGONAL      = `<svg viewBox="0 0 47 47" fill="none" stroke="currentColor" stroke-width="2"><line x1="40.12" y1="6.88" x2="6.88" y2="40.12"/></svg>`;
  const ICON_ARROW_RIGHT   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`;
  const ICON_ARROW_LEFT_S  = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
  const ICON_ARROW_RIGHT_S = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>`;

  function iconImgOrFallback(url, fallbackKey) {
    if (url) return `<img src="${escapeAttr(url)}" alt="" loading="lazy">`;
    return (fallbackKey && config.fallbackIcons?.[fallbackKey]) || PLACEHOLDER_ICON;
  }

  // Tab availability + config
  function tabAvailability() {
    const acc = currentAccData();
    // A cap/boot block can exist purely to carry the preview GLB (which ships
    // with the bottle) with an empty products list — that must NOT surface a
    // selectable tab. The tab shows only when there are products to choose.
    const hasProducts = (a) => !!a && Object.keys(a.products || {}).length > 0;
    return {
      bottle: true,
      cap:  hasProducts(acc.cap),
      boot: hasProducts(acc.boot),
      art:  artAvailableModes().length > 0
    };
  }
  function visibleTabs() {
    const av = tabAvailability();
    return ['bottle','cap','boot','art'].filter(t => av[t]);
  }
  function tabConfig(tab) {
    if (tab === 'bottle') return { label: config.bodyTab?.label || 'Bottle Type', icon: config.bodyTab?.icon || null, pillLabel: 'Bottle' };
    if (tab === 'cap')    return { label: currentAccData().cap?.label  || 'Cap',  icon: currentAccData().cap?.icon  || null, pillLabel: 'Cap' };
    if (tab === 'boot')   return { label: currentAccData().boot?.label || 'Boot', icon: currentAccData().boot?.icon || null, pillLabel: 'Boot' };
    if (tab === 'art')    return { label: 'Art', icon: null, pillLabel: 'Art' };
    return { label: tab, icon: null, pillLabel: tab };
  }
  function nextVisibleTab(tab) {
    const tabs = visibleTabs();
    const idx  = tabs.indexOf(tab);
    return idx >= 0 && idx < tabs.length - 1 ? tabs[idx + 1] : null;
  }

  // Tab strip + mobile bar renderers
  function tabFallbackKey(tab) {
    // Maps tab id → key in config.fallbackIcons. 'bottle' tab is the body tab.
    return tab === 'bottle' ? 'bottle' : tab;
  }

  function renderTabStrip() {
    const strip = document.getElementById('fd-tab-strip');
    const bar   = document.getElementById('fd-mobile-tabbar');
    const tabs  = visibleTabs();
    const desk = tabs.map(t => {
      const c = tabConfig(t);
      const ck = tabHasCheck(t) ? '<span class="fd-tab__check"></span>' : '';
      return `<button class="fd-tab${t === ui.activeTab ? ' is-active' : ''}" data-tab="${t}" role="tab">
        <span class="fd-tab__icon">${iconImgOrFallback(c.icon, tabFallbackKey(t))}</span>
        <span class="fd-tab__label">${escapeText(c.label)}</span>${ck}
      </button>`;
    }).join('');
    if (strip) strip.innerHTML = desk;
    const mob = tabs.map(t => {
      const c = tabConfig(t);
      const ck = tabHasCheck(t) ? '<span class="fd-mobile-tab__check"></span>' : '';
      return `<button class="fd-mobile-tab${t === ui.activeTab ? ' is-active' : ''}" data-tab="${t}">
        <span class="fd-mobile-tab__icon">${iconImgOrFallback(c.icon, tabFallbackKey(t))}</span>
        <span class="fd-mobile-tab__label">${escapeText(c.label)}</span>${ck}
      </button>`;
    }).join('');
    if (bar) {
      // Tabs + a single sliding indicator. The indicator is positioned over
      // the active tab via positionMobileTabIndicator() and slides between
      // tabs on subsequent renders (CSS transition on left/width).
      bar.innerHTML = mob + `<div class="fd-mobile-tabbar__indicator" id="fd-mobile-tabbar-indicator"></div>`;
      requestAnimationFrame(() => positionMobileTabIndicator());
    }
  }

  // Slides the mobile tab bar's underline indicator to sit beneath the
  // currently-active tab. ~40% of the tab's width, horizontally centred.
  function positionMobileTabIndicator() {
    const bar = document.getElementById('fd-mobile-tabbar');
    if (!bar) return;
    const indicator = bar.querySelector('.fd-mobile-tabbar__indicator');
    const activeTab = bar.querySelector('.fd-mobile-tab.is-active');
    if (!indicator || !activeTab) return;
    const barRect = bar.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const widthPct = 0.4; // fraction of the tab's width
    const indW    = tabRect.width * widthPct;
    const indLeft = (tabRect.left - barRect.left) + (tabRect.width - indW) / 2;
    indicator.style.width = `${indW}px`;
    indicator.style.left  = `${indLeft}px`;
  }

  // Section wrapper helper (consistent header / chevron / check)
  function renderSection(key, labelHtml, bodyHtml, opts = {}) {
    const { collapsible = false, hasCheck = false } = opts;
    const isCollapsed = collapsible && ui.collapsed[key];
    const headerClass = collapsible ? 'fd-section__header is-collapsible' : 'fd-section__header';
    const checkHtml   = hasCheck ? '<span class="fd-section__check"></span>' : '';
    const chevronHtml = collapsible ? `<button type="button" class="fd-section__chevron" data-action="toggle-section" data-key="${key}" aria-label="Toggle">${ICON_CHEVRON}</button>` : '';
    const headerAttr  = collapsible ? ` data-action="toggle-section" data-key="${key}"` : '';
    return `<div class="fd-section${isCollapsed ? ' is-collapsed' : ''}" data-section="${key}">
      <div class="${headerClass}"${headerAttr}>
        <span class="fd-section__label">${labelHtml}</span>
        ${checkHtml}${chevronHtml}
      </div>
      <div class="fd-section__body">${bodyHtml}</div>
    </div>`;
  }

  // Section renderers (shared by desktop pane and mobile popover)
  // Combined colour list: every variant of every bottle product is flattened
  // into one grid. The variant's owning product is recovered at click time
  // in selectBodyColor — no productId needs to travel through the DOM.
  function renderBottleColorRow() {
    const colors = [];
    for (const product of Object.values(config.bottles || {})) {
      for (const [id, v] of Object.entries(product.variants || {})) {
        if (!v.hasTexture) continue;
        colors.push({ id, swatchStyle: v.swatchStyle, name: v.title, available: v.available });
      }
    }
    colors.sort((a, b) => Number(b.available) - Number(a.available));
    return `<div class="fd-swatch-grid">${colors.map(c => `
      <button type="button" class="fd-swatch${c.swatchStyle.includes('#ffffff') ? ' fd-swatch--white' : ''}${c.id === state.bottleVariantId ? ' is-active' : ''}"
              style="${escapeAttr(c.swatchStyle || 'background-color:#ccc')}"
              data-action="select-body-color" data-id="${escapeAttr(c.id)}"
              ${c.available ? '' : 'disabled'}
              aria-label="${escapeAttr(c.name)}"></button>
    `).join('')}</div>`;
  }

  function renderAccTypeRow(type) {
    const acc = currentAccData()[type];
    if (!acc) return '';
    const cur = state.accessories[type] || {};
    const groups = Object.entries(acc.products || {})
      .filter(([, g]) => Object.values(g.variants || {}).some(v => v.hasTexture));
    const pills = groups.map(([gid, g]) => `
      <button type="button" class="fd-pill${gid === cur.productId ? ' is-active' : ''}"
              data-action="select-acc-product" data-type="${type}" data-id="${escapeAttr(gid)}">
        <span class="fd-pill__circle${g.thumbnail ? '' : ' fd-pill__circle--placeholder'}">${g.thumbnail ? `<img src="${escapeAttr(g.thumbnail)}" alt="">` : ''}</span>
        <span class="fd-pill__label">${escapeText(g.title)}</span>
      </button>`).join('');
    const noneBtn = `<button type="button" class="fd-pill${cur.productId === null ? ' is-active' : ''}"
                            data-action="select-acc-product" data-type="${type}" data-id="__none__">
      <span class="fd-pill__none-icon">${ICON_DIAGONAL}</span>
      <span class="fd-pill__label">Not Selected</span>
    </button>`;
    return `<div class="fd-pill-row"><div class="fd-pill-row__items">${pills}${noneBtn}</div></div>`;
  }

  function renderAccColorRow(type) {
    const acc = currentAccData()[type];
    if (!acc) return '';
    const cur = state.accessories[type] || {};
    if (cur.productId === null || cur.productId === undefined) return '';
    const group = acc.products[cur.productId];
    const variants = Object.entries(group?.variants || {})
      .filter(([, v]) => v.hasTexture)
      .sort(([, a], [, b]) => Number(b.available) - Number(a.available));
    return `<div class="fd-swatch-grid">${variants.map(([vid, v]) => `
      <button type="button" class="fd-swatch${v.swatchStyle.includes('#ffffff') ? ' fd-swatch--white' : ''}${vid === cur.variantId ? ' is-active' : ''}"
              style="${escapeAttr(v.swatchStyle || 'background-color:#ccc')}"
              data-action="select-acc-color" data-type="${type}" data-id="${escapeAttr(vid)}"
              ${v.available ? '' : 'disabled'}
              aria-label="${escapeAttr(v.title)}"></button>
    `).join('')}</div>`;
  }

  // Mobile-only "COLOUR: <name>" caption shown above the swatch grid in the popover
  // (the desktop pane already shows this in its section header; the popover has none).
  // Kept in sync on selection via _txt('.fd-pop-color-title__val', …).
  function mobileColorTitleHtml(name) {
    return `<div class="fd-pop-color-title">COLOUR:<span class="fd-pop-color-title__val">${escapeText(name || '')}</span></div>`;
  }
  function accColorBodyMobile(type) {
    const cur  = state.accessories[type] || {};
    const name = currentAccData()[type]?.products?.[cur.productId]?.variants?.[cur.variantId]?.title || '';
    return mobileColorTitleHtml(name) + renderAccColorRow(type);
  }
  // After a swatch grid renders in the popover, centre it ONLY when it wraps to more
  // than one row; a single row stays start-aligned. Detect wrap by comparing item
  // offsetTops (any item on a different row than the first → multi-row).
  function alignSwatchGrid(container) {
    const grid = container?.querySelector('.fd-swatch-grid');
    if (!grid) return;
    requestAnimationFrame(() => {
      const items = grid.querySelectorAll('.fd-swatch');
      let multi = false;
      if (items.length) {
        const top0 = items[0].offsetTop;
        for (const el of items) { if (el.offsetTop !== top0) { multi = true; break; } }
      }
      grid.classList.toggle('fd-swatch-grid--center', multi);
    });
  }

  // Art helpers. A mode needs the section toggle, a usable config in the
  // bottle's personalization settings, AND the current VARIANT's allow flag
  // (variant metafields custom.allow_engraving / allow_uv_print: an explicit
  // false disables that mode for the variant; true or blank allows). With
  // both modes disabled the Art tab disappears (tabAvailability reads this).
  function artAvailableModes() {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const bVar = config.bottles[state.bottleProductId]?.variants?.[state.bottleVariantId];
    const out = [];
    if (config.prints.allowEngraving && bVar?.allowEngraving !== false
        && pers?.engraving?.positions?.length) out.push('engraving');
    if (config.prints.allowUvPrint && bVar?.allowUvPrint !== false
        && pers?.color_print?.mesh_name
        && pers.color_print.width_mm && pers.color_print.height_mm
        && pers.color_print.positions?.length) out.push('uv_print');
    return out;
  }
  let _skipPrintRender = false;
  // One-shot suppression for the variant-switch deactivation render: without it,
  // resetting to 'none' with a single surviving mode would be undone immediately
  // (renderPrintsPanel → renderArtPane → auto-select) — silently re-activating
  // the OTHER mode (and its service line) from a mere colour click. Suppressed,
  // the auto-select still happens at view time when the user next enters Art.
  let _skipArtAutoDefault = false;
  function ensureArtModeDefault() {
    if (printState.mode !== 'none') return;
    if (_skipArtAutoDefault) return;
    const modes = artAvailableModes();
    // Only auto-select when there's a SINGLE mode (no Engrave/Print choice to
    // make). With two modes nothing is preselected — the user must pick one,
    // and only then do the text/icon options appear.
    if (modes.length !== 1) return;
    _skipPrintRender = true;
    try { handlePrintAction('set-mode', { mode: modes[0] }); }
    finally { _skipPrintRender = false; }
  }
  function artCurrentPs()  { return printState.mode === 'engraving' ? printState.engraving : printState.mode === 'uv_print' ? printState.uvPrint : null; }
  function artCurrentCfg() { return printState.mode === 'engraving' ? config.prints.engraving : printState.mode === 'uv_print' ? config.prints.uvPrint : null; }
  function artHasText() { return (artCurrentPs()?.text || '').trim().length > 0; }
  function artHasIcon() { return !!artCurrentPs()?.icon; }
  // Badges missing their SVG, a font (file or Adobe kit) or font size are skipped.
  function artBadgeDesigns() {
    if (printState.mode !== 'engraving') return [];
    return (config.prints.engraving?.badges || []).filter(b => b.svg && (b.fontFile || _kitHref(b)) && Number(b.fontPt) > 0);
  }
  // Adobe Fonts embed (`<link … href="https://use.typekit.net/xxx.css">`, smart quotes
  // tolerated) → its stylesheet URL. Only used when the badge has no font file.
  function _kitHref(b) {
    if (b.fontFile || !b.fontScript) return null;
    return String(b.fontScript).match(/https:\/\/use\.typekit\.net\/[a-z0-9]+\.css/i)?.[0] || null;
  }
  const BADGE_MAX_CHARS = 10; // same limit as engraving text
  // Special Text (badge) combines with Text OR Icon, never both: once two of the three
  // are chosen, the third is blocked (its card explains why) until one is removed.
  function _artBlocked(which) {
    if (printState.mode !== 'engraving') return false;
    const ps = printState.engraving;
    const has = { text: (ps.text || '').trim().length > 0, icon: ps.icon != null, badge: !!ps.badge };
    return !has[which] && Object.keys(has).filter(k => k !== which).every(k => has[k]);
  }
  const _blockedNoteHtml = which => _artBlocked(which)
    ? `<div class="fd-art-blocked" style="margin:0 0 12px;font-size:12px;color:#d0342c;">Special Text can be combined with either Text or Icon, not both. Remove one to add ${which === 'badge' ? 'Special Text' : which === 'icon' ? 'an icon' : 'text'}.</div>`
    : '';
  function artHasBadge() { return printState.mode === 'engraving' && !!printState.engraving.badge; }
  function artBadgeDone() { return artHasBadge() && (printState.engraving.badgeText || '').trim().length > 0; }
  // A Special Text design without text isn't valid — drop it once the customer leaves it.
  function _dropEmptyBadge() {
    if (!artHasBadge() || artBadgeDone()) return;
    printState.engraving.badge = null;
    renderPrintsPanel();
    renderTabStrip();
    applyEngravingPreview();
  }

  function renderArtModeRow() {
    const modes = artAvailableModes();
    if (modes.length <= 1) return '';
    const labels = { engraving: 'Engrave', uv_print: 'Colour Print' };
    return `<div class="fd-mode-row">${modes.map(m => `
      <button type="button" class="fd-mode-btn${printState.mode === m ? ' is-active' : ''}"
              data-print-action="set-mode" data-mode="${m}">${labels[m]}</button>
    `).join('')}</div>`;
  }

  function injectFontFaces(fonts) {
    if (!fonts?.length) return;
    let styleEl = document.getElementById('fd-font-faces');
    if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'fd-font-faces'; document.head.appendChild(styleEl); }
    styleEl.textContent = fonts.filter(f => f.file).map(f =>
      `@font-face { font-family: '${f.name}'; src: url('${f.file}'); }`
    ).join('\n');
  }

  // Size (S/M/L) pill group — same look as Position/Orientation.
  function _sizeGroupHtml(which) {
    const ps = artCurrentPs();
    if (!ps) return '';
    const modeKey = printState.mode === 'engraving' ? 'engraving' : 'uv_print';
    const current = which === 'text' ? ps.textSize : ps.iconSize;
    const opts = sizeOptions(modeKey, which);
    if (opts.length < 2) return '';
    return `
      <div class="fd-position-group">
        <div class="fd-position-group__label">Size</div>
        <div class="fd-position-btns">${opts.map(o =>
          `<button type="button" class="fd-position-btn${current === o.key ? ' is-active' : ''}" data-print-action="set-${which}-size" data-size="${escapeAttr(o.key)}">${escapeText(o.label)}</button>`
        ).join('')}</div>
      </div>`;
  }

  function renderArtTextCard(opts = {}) {
    const ps  = artCurrentPs();
    const cfg = artCurrentCfg();
    if (!ps || !cfg) return '';
    const { showInputOnly = false, showFontOnly = false, showPositionOnly = false } = opts;
    const isMobileSubview = showInputOnly || showFontOnly || showPositionOnly;
    const isOpen  = isMobileSubview || ui.artCardOpen === 'text';
    const checkOn = artHasText();
    const fonts = cfg.fonts || [];
    injectFontFaces(fonts);
    const isUV  = printState.mode === 'uv_print';
    const fontColors = isUV ? (config.prints.uvPrint?.fontColors || []) : [];

    const inputHtml = `${_blockedNoteHtml('text')}
      <div class="fd-text-input-wrap">
        <input type="text" class="fd-text-input" placeholder="YOUR NAME" maxlength="10"${_artBlocked('text') ? ' disabled' : ''}
               value="${escapeAttr(ps.text || '')}" data-print-action="set-text">
        <span class="fd-text-counter">${(ps.text || '').length}/10</span>
      </div>`;

    const fontHtml = fonts.length ? `
      <div class="fd-subsection-label">Font Type</div>
      <div class="fd-font-row">${fonts.map(f => `
        <button type="button" class="fd-font-btn${ps.font?.name === f.name ? ' is-active' : ''}"
                style="font-family: '${escapeAttr(f.name)}', sans-serif;"
                data-print-action="set-font" data-font-name="${encodeURIComponent(f.name)}">Aa</button>
      `).join('')}</div>` : '';

    const colorHtml = (isUV && fontColors.length) ? `
      <div class="fd-subsection-label" style="margin-top:14px;">COLOUR: <span class="fd-fontcolor-label" style="text-transform:none;letter-spacing:0;font-weight:500;">${escapeText(ps.fontColor?.label || '')}</span></div>
      <div class="fd-swatch-grid">${fontColors.map(c => `
        <button type="button" class="fd-swatch${c.color === '#ffffff' ? ' fd-swatch--white' : ''}${ps.fontColor?.color === c.color ? ' is-active' : ''}"
                style="background-color:${escapeAttr(c.color)}"
                data-print-action="set-font-color" data-color="${encodeURIComponent(c.color)}"
                aria-label="${escapeAttr(c.label)}" title="${escapeAttr(c.label)}"></button>
      `).join('')}</div>` : '';

    // All position pills stay clickable; UV face exclusivity auto-swaps.
    const textPosHtml = _facingHtml('text', 'Position');

    const orientationHtml   = _orientationHtml();
    const textSizeHtml      = _sizeGroupHtml('text');
    const textPlacementHtml = _placementHtml('text');

    let inner;
    if (showInputOnly)         inner = inputHtml + _artNoticeHtml();
    else if (showFontOnly)     inner = fontHtml + colorHtml;
    else if (showPositionOnly) inner = textPosHtml + orientationHtml + textSizeHtml + textPlacementHtml;
    else {
      // Desktop full card: Position / Orientation / Size in a left column,
      // Vertical Adjustment in a right column. (Mobile sub-views above untouched.)
      const left = textPosHtml + orientationHtml + textSizeHtml;
      const posRow = (left || textPlacementHtml)
        ? `<div class="fd-art-posrow"><div class="fd-art-posrow__left">${left}</div>${textPlacementHtml ? `<div class="fd-art-posrow__right">${textPlacementHtml}</div>` : ''}</div>`
        : '';
      inner = inputHtml + fontHtml + colorHtml + _artNoticeHtml() + posRow;
    }

    // Desktop: header is a toggle. Mobile sub-views: header is decorative.
    const headerAttrs = isMobileSubview ? '' : ' data-action="art-card-toggle" data-card="text"';
    const cardClass = isOpen ? 'fd-art-card' : 'fd-art-card is-collapsed';

    return `<div class="${cardClass}" data-art="text">
      <div class="fd-art-card__header"${headerAttrs}>
        <span class="fd-art-card__title">Text</span>
        <span class="fd-art-card__check${checkOn ? ' is-on' : ''}" data-art-check="text"></span>
      </div>
      <div class="fd-art-card__body">${inner}</div>
    </div>`;
  }

  function renderArtIconCard(opts = {}) {
    const ps  = artCurrentPs();
    const cfg = artCurrentCfg();
    if (!ps || !cfg) return '';
    const { showIconsOnly = false, showPositionOnly = false } = opts;
    const isMobileSubview = showIconsOnly || showPositionOnly;
    const isOpen  = isMobileSubview || ui.artCardOpen === 'icon';
    const checkOn = artHasIcon();
    const series  = cfg.iconSeries || [];
    // Viewed series = which series' icons the row shows (a browse filter only).
    // It's independent of the SELECTED icon (ps.icon) and its series
    // (ps.iconSeries), so switching series never clears the selection.
    const viewedName   = ui.iconViewedSeries || ps.iconSeries?.name || series[0]?.name || null;
    const viewedSeries = series.find(s => s.name === viewedName) || series[0] || null;

    const seriesHtml = series.length > 1 ? `
      <div class="fd-icon-series-row">${series.map(s => `
        <button type="button" class="fd-icon-series-btn${viewedSeries?.name === s.name ? ' is-active' : ''}"
                data-print-action="set-icon-series" data-series-name="${encodeURIComponent(s.name)}">${escapeText(s.name)}</button>
      `).join('')}</div>` : '';

    const iconsHtml = `${_blockedNoteHtml('icon')}<div class="fd-icon-row">${(viewedSeries?.icons || []).map(ic => _iconBtnHtml(ic, ps.icon?.name)).join('')}</div>`;

    const iconPosHtml = _facingHtml('icon', 'Position');

    const iconSizeHtml      = _sizeGroupHtml('icon');
    const iconPlacementHtml = _placementHtml('icon');

    let inner;
    if (showIconsOnly)         inner = seriesHtml + iconsHtml + _artNoticeHtml();
    else if (showPositionOnly) inner = iconPosHtml + iconSizeHtml + iconPlacementHtml;
    else {
      const left = iconPosHtml + iconSizeHtml;
      const posRow = (left || iconPlacementHtml)
        ? `<div class="fd-art-posrow"><div class="fd-art-posrow__left">${left}</div>${iconPlacementHtml ? `<div class="fd-art-posrow__right">${iconPlacementHtml}</div>` : ''}</div>`
        : '';
      inner = seriesHtml + iconsHtml + _artNoticeHtml() + posRow;
    }

    const headerAttrs = isMobileSubview ? '' : ' data-action="art-card-toggle" data-card="icon"';
    const cardClass = isOpen ? 'fd-art-card' : 'fd-art-card is-collapsed';

    return `<div class="${cardClass}" data-art="icon">
      <div class="fd-art-card__header"${headerAttrs}>
        <span class="fd-art-card__title">Icon</span>
        <span class="fd-art-card__check${checkOn ? ' is-on' : ''}" data-art-check="icon"></span>
      </div>
      <div class="fd-art-card__body">${inner}</div>
    </div>`;
  }

  // Badge = fixed-size design + customer text (engraving only), placed like text/icon.
  function renderArtBadgeCard(opts = {}) {
    const ps      = artCurrentPs();
    const designs = artBadgeDesigns();
    if (!ps || !designs.length) return '';
    const { showContentOnly = false } = opts;
    const pers    = config.bottles[state.bottleProductId]?.personalizationSettings;
    const modeCfg = getModeCfg(pers, 'engraving');
    const sp  = ps.badge;
    const max = BADGE_MAX_CHARS;
    // Largest engraving safe area on this bottle — shown when a design can't fit it.
    const area = getPositions(pers, 'engraving').map(p => {
      const { topMm, bottomMm } = safeAreaYMm('engraving', p, modeCfg);
      return `${p.label || p.key} ${+(p.max_width_mm ?? 0).toFixed(1)}×${+(bottomMm - topMm).toFixed(1)}mm`;
    }).join(', ');
    const designsHtml = `<div class="fd-subsection-label">Choose Design</div>${_blockedNoteHtml('badge')}
      <div class="fd-icon-row">${designs.map(d => {
        const ok = getPositions(pers, 'engraving').some(p => canFitBadgeOnFace(p, modeCfg));
        const title = ok ? d.name : `${d.name} needs a ${getBadgeHmm(modeCfg)}mm-tall engraving area — this bottle: ${area}`;
        if (!ok) console.warn(`[FlexiDesign] Badge "${title}"`);
        return `<button type="button" class="fd-icon-btn${sp?.name === d.name ? ' is-active' : ''}"${ok && !_artBlocked('badge') ? '' : ' disabled'} data-print-action="set-badge" data-design-name="${encodeURIComponent(d.name)}" title="${escapeAttr(title)}"><img src="${escapeAttr(d.svg)}" alt="${escapeAttr(d.name)}" loading="lazy"></button>`;
      }).join('')}</div>`;
    const inputHtml = sp ? `
      <div class="fd-subsection-label" style="margin-top:14px;">Your Text</div>
      <div class="fd-text-input-wrap">
        <input type="text" class="fd-text-input fd-badge-input" placeholder="Your Name" maxlength="${max}"
               value="${escapeAttr(ps.badgeText || '')}">
        <span class="fd-text-counter">${(ps.badgeText || '').length}/${max}</span>
      </div>` : '';
    let inner = designsHtml + (ui.badgeMsg ? `<div class="fd-badge-msg" style="margin-top:10px;font-size:12px;color:#d0342c;">${escapeText(ui.badgeMsg)}</div>` : '') + inputHtml + _artNoticeHtml();
    ui.badgeMsg = '';
    if (!showContentOnly && sp) {
      const placement = _placementHtml('badge');
      inner += `<div class="fd-art-posrow"><div class="fd-art-posrow__left">${_facingHtml('badge', 'Position')}</div>${placement ? `<div class="fd-art-posrow__right">${placement}</div>` : ''}</div>`;
    }
    const headerAttrs = showContentOnly ? '' : ' data-action="art-card-toggle" data-card="badge"';
    return `<div class="fd-art-card" data-art="badge">
      <div class="fd-art-card__header"${headerAttrs}>
        <span class="fd-art-card__title">Special Text</span>
        <span class="fd-art-card__check${artBadgeDone() ? ' is-on' : ''}" data-art-check="badge"></span>
      </div>
      <div class="fd-art-card__body">${inner}</div>
    </div>`;
  }

  function bindBadgeInput(scope) {
    const input = scope.querySelector('.fd-badge-input');
    if (!input) return;
    const onInput = (e) => {
      if (e.type === 'input' && e.isComposing) return;
      const ps = printState.engraving;
      if (!ps.badge) return;
      const max = BADGE_MAX_CHARS;
      const had = artBadgeDone();
      const v = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').replace(/ {2,}/g, ' ').replace(/^ /, '').slice(0, max)
        .toLowerCase().replace(/(^| )([a-z])/g, (_, s, c) => s + c.toUpperCase());
      if (v !== e.target.value) {
        const caret = Math.max(0, (e.target.selectionStart || 0) - (e.target.value.length - v.length));
        e.target.value = v;
        e.target.setSelectionRange(caret, caret);
      }
      ps.badgeText = v;
      const counter = e.target.closest('.fd-text-input-wrap')?.querySelector('.fd-text-counter');
      if (counter) counter.textContent = `${v.length}/${max}`;
      if (had !== artBadgeDone()) {
        _chk('[data-art-check="badge"]', !had);
        _q('.fd-art-subtoggle-btn[data-which="badge"]').forEach(b => b.classList.toggle('has-check', !had));
        if (ui.mobilePopover.open && ui.mobilePopover.tab === 'art') renderPopoverChrome();
        renderTabStrip();
        refreshPurchaseBar();
      }
      applyEngravingPreview();
      rotateArtCameraForElement('badge');
    };
    input.addEventListener('input', onInput);
    input.addEventListener('compositionend', onInput);
  }

  // Desktop pane assemblers
  function capTypeChosen()   { return state.accessories.cap?.productId  != null; }
  function bootTypeChosen()  { return state.accessories.boot?.productId != null; }
  function bottleColorComplete() { return !!state.bottleVariantId; }

  // Desktop pane CTA — Add to Cart. Mobile popover keeps its own nav.
  function ctaHtml(_label, pink = false, _arrow = true) {
    const dis = bottleHasValidVariant() ? '' : ' disabled';
    return `<div class="fd-pane__cta">
      <button type="button" class="fd-cta-btn${pink ? ' is-pink' : ''}" data-action="add-to-cart"${dis}>
        <span class="fd-atc-label">ADD TO CART</span>
        <span class="fd-atc-price">${formatMoney(displayTotalCents())}</span>
      </button>
    </div>`;
  }

  // "Personalisation FAQs" underline link — Art tab only (the FAQs are about
  // personalisation). Opens the theme's NATIVE FAQ panel via the OpenableElement
  // .open API (same as document.getElementById('mini-cart').open = true): a
  // drawer-from-right on desktop (target fd-faq-drawer) and a bottom popover on
  // mobile (target fd-faq-popover). Renders nothing unless a FAQ page is configured.
  function faqLinkHtml(target) {
    if (!config.faq?.enabled) return '';
    const raw = config.faq.text || 'FAQs';
    const link = (txt) => `<a href="#" class="fd-faq-link" data-action="open-faq" data-faq-target="${target}">${escapeText(txt)}</a>`;
    // Mirror the T&C agreement: only the [[ ]] segment is the underlined trigger;
    // the surrounding sentence is plain text. No marker → the whole string is the
    // trigger (forgiving fallback, since the link is the whole point of the FAQ).
    const i = raw.indexOf('[['), j = raw.indexOf(']]');
    const inner = (i !== -1 && j > i)
      ? escapeText(raw.slice(0, i)) + link(raw.slice(i + 2, j)) + escapeText(raw.slice(j + 2))
      : link(raw);
    return `<div class="fd-faq-link-wrap">${inner}</div>`;
  }

  function renderBottlePane() {
    const pane = document.getElementById('fd-pane-bottle');
    if (!pane) return;
    const colorLabel = config.bottles[state.bottleProductId]?.variants[state.bottleVariantId]?.title || '';
    pane.innerHTML = `
      ${renderSection('bottle-color', `COLOUR:<span class="fd-section__value">${escapeText(colorLabel)}</span>`, renderBottleColorRow(), { collapsible: false, hasCheck: bottleColorComplete() })}
      ${ctaHtml('')}
    `;
  }

  function _renderAccPane(type, fallback) {
    const pane = document.getElementById(`fd-pane-${type}`);
    if (!pane) return;
    const acc = currentAccData()[type];
    if (!acc) { pane.innerHTML = ''; return; }
    const cur = state.accessories[type] || {};
    const group = cur.productId != null ? acc.products[cur.productId] : null;
    const colorLabel = cur.productId != null ? (group?.variants[cur.variantId]?.title || '') : '';
    const chosen = cur.productId != null;
    pane.innerHTML = `
      ${renderSection(`${type}-type`, `${escapeText((acc.label || fallback).toUpperCase())} TYPE`, renderAccTypeRow(type), { collapsible: true, hasCheck: chosen })}
      ${chosen ? renderSection(`${type}-color`, `COLOUR:<span class="fd-section__value">${escapeText(colorLabel)}</span>`, renderAccColorRow(type), { collapsible: false, hasCheck: cur.variantId != null }) : ''}
      ${ctaHtml('')}
    `;
  }
  function renderCapPane()  { _renderAccPane('cap',  'CAP'); }
  function renderBootPane() { _renderAccPane('boot', 'BOOT'); }

  // Desktop Text/Icon switch — only the active sub-element's card is shown.
  function renderArtSubSwitch(sub) {
    const icons = config.fallbackIcons || {};
    const hasBadge = artBadgeDesigns().length > 0;
    return `<div class="fd-art-seg-wrap">
      <div class="fd-art-seg__intro">${hasBadge ? 'Choose any one application or combine them!' : 'Choose either one application or both!'}</div>
      <div class="fd-art-seg__btns${hasBadge ? ' fd-art-seg__btns--3' : ''}">
        <button type="button" class="fd-art-seg__btn${sub === 'text' ? ' is-active' : ''}" data-action="art-sub-switch" data-which="text"><span class="fd-art-seg__ic">${icons.textArt || ''}</span>Text</button>
        <button type="button" class="fd-art-seg__btn${sub === 'icon' ? ' is-active' : ''}" data-action="art-sub-switch" data-which="icon"><span class="fd-art-seg__ic">${icons.iconArt || ''}</span>Icon</button>
        ${hasBadge ? `<button type="button" class="fd-art-seg__btn${sub === 'badge' ? ' is-active' : ''}" data-action="art-sub-switch" data-which="badge"><span class="fd-art-seg__ic">${icons.badgeArt || ''}</span>Special Text</button>` : ''}
      </div>
    </div>`;
  }
  const _artSubCard = sub => sub === 'icon' ? renderArtIconCard() : sub === 'badge' ? renderArtBadgeCard() : renderArtTextCard();
  const _artSub = () => ui.artCardOpen === 'icon' || (ui.artCardOpen === 'badge' && artBadgeDesigns().length) ? ui.artCardOpen : 'text';

  function renderArtPane() {
    const pane = document.getElementById('fd-pane-art');
    if (!pane) return;
    if (artAvailableModes().length === 0) { pane.innerHTML = ''; return; }
    ensureArtModeDefault();
    const ps = artCurrentPs();
    // No mode picked yet (2 modes available): show only the Engrave/Print buttons
    // — no text/icon options below — but keep the ADD TO CART so the bottle can
    // still be added without personalisation.
    if (!ps) { pane.innerHTML = renderArtModeRow() + faqLinkHtml('fd-faq-drawer') + ctaHtml(''); return; }
    const sub = _artSub();
    pane.innerHTML = `
      ${renderArtModeRow()}
      ${renderArtSubSwitch(sub)}
      <div class="fd-art-card-host">${_artSubCard(sub)}</div>
      ${faqLinkHtml('fd-faq-drawer')}
      ${ctaHtml('')}
    `;
    bindTextInputDebounce(pane);
    bindBadgeInput(pane);
    bindYSliders(pane);
  }

  // Toggling Text/Icon: flip the active segment IN PLACE and swap only the card
  // below — avoids re-rendering the whole pane (no flicker of the mode row).
  function swapArtSub(next) {
    const pane = document.getElementById('fd-pane-art');
    if (!pane) return;
    const host = pane.querySelector('.fd-art-card-host');
    if (!host) { renderArtPane(); return; }
    pane.querySelectorAll('.fd-art-seg__btn').forEach(b =>
      b.classList.toggle('is-active', b.dataset.which === next));
    host.innerHTML = _artSubCard(next);
    bindTextInputDebounce(host);
    bindBadgeInput(host);
    bindYSliders(host);
  }

  // Slider input drives handlePrintAction directly (no re-render); thumb
  // synced to stored Y per-input to prevent overshooting placement clamps.
  function bindYSliders(scope) {
    scope.querySelectorAll('.fd-y-slider').forEach(slider => {
      const syncToStored = () => {
        const cur = artCurrentPs();
        if (!cur) return;
        const yKey = _EGV_EL[slider.dataset.which]?.[1] || 'iconY_mm';
        const stored = cur[yKey];
        if (stored != null && Math.abs(stored - parseFloat(slider.value)) > 0.01) {
          slider.value = stored.toFixed(2);
        }
      };
      // dragActive flips on first input of a drag, resets on change.
      // firstInput fires camera rotation just once per drag.
      slider.dataset.dragActive = '';
      // Snap points at 0/25/50/75/100%. Dragging is FREE — you can rest on any
      // value (e.g. 49). The magnet only nudges to the nearest point on RELEASE,
      // and only if you let go within MAGNET_FRACTION of it (raise/lower this one
      // constant to make snapping stronger/weaker).
      const MAGNET_FRACTION = 0.03;
      const sMin = parseFloat(slider.min), sSpan = parseFloat(slider.max) - sMin;
      const snaps = [0, 0.25, 0.5, 0.75, 1].map(p => sMin + p * sSpan);
      const magnetThreshold = sSpan * MAGNET_FRACTION;
      const magnet = (v) => {
        for (const s of snaps) if (Math.abs(v - s) <= magnetThreshold) return s;
        return v;
      };
      slider.addEventListener('input', () => {
        const firstInput = slider.dataset.dragActive !== '1';
        slider.dataset.dragActive = '1';
        handlePrintAction(slider.dataset.printAction, {
          value: slider.value, which: slider.dataset.which, firstInput,
        });
        syncToStored(); // keep the thumb within the clamped placement range
      });
      slider.addEventListener('change', () => {
        const snapped = magnet(parseFloat(slider.value)).toFixed(2);
        if (snapped !== slider.value) {
          slider.value = snapped;
          handlePrintAction(slider.dataset.printAction, {
            value: snapped, which: slider.dataset.which, firstInput: false,
          });
        }
        syncToStored();
        slider.dataset.dragActive = '';
      });
    });
  }

  // Coalesce async preview: one in flight, one pending. Avoids stale queue.
  let _previewBusy    = false;
  let _previewPending = false;
  async function schedulePreview() {
    if (_previewBusy) { _previewPending = true; return; }
    _previewBusy = true;
    try {
      do {
        _previewPending = false;
        await applyEngravingPreview();
      } while (_previewPending);
    } finally {
      _previewBusy = false;
    }
  }

  // Real-time preview, in-place DOM updates only (don't re-render — kills focus).
  function bindTextInputDebounce(scope) {
    const textInput = scope.querySelector('.fd-text-input:not(.fd-badge-input)');
    if (!textInput) return;
    const handleTextInput = (e) => {
      // Ignore intermediate events fired DURING an IME composition (Chinese /
      // Japanese / etc. keyboards): rewriting the field mid-composition breaks the
      // IME, AND the browser does not enforce maxlength while composing. The value is
      // still enforced on the final (non-composing) input event and on compositionend
      // — both bound below — so the committed text can never bypass the limit.
      if (e.type === 'input' && e.isComposing) return;
      const cur = artCurrentPs();
      if (!cur) return;
      if (_artBlocked('text')) { e.target.value = ''; return; }
      const prevHad = (cur.text || '').trim().length > 0;
      const before  = _layoutSnap();
      // Allow letters and numbers only (plus spaces) — strips symbols and any
      // other-language characters (Chinese and other CJK, emoji, accented letters,
      // etc.). SPACES are normalised: at most one between characters and never a
      // leading one. A lone trailing space is kept while typing (it becomes a
      // separator once the next character lands); consumers trim, so the used
      // value never starts or ends with a space.
      const sanitized = e.target.value
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/ {2,}/g, ' ')
        .replace(/^ /, '')
        .slice(0, 10); // HARD length cap — maxlength is not enforced during IME.
      if (sanitized !== e.target.value) {
        const caret = Math.max(0, (e.target.selectionStart || 0) - (e.target.value.length - sanitized.length));
        e.target.value = sanitized;
        e.target.setSelectionRange(caret, caret);
      }
      cur.text = sanitized;
      const counter = e.target.closest('.fd-text-input-wrap')?.querySelector('.fd-text-counter');
      if (counter) counter.textContent = `${sanitized.length}/10`;
      const has = (cur.text || '').trim().length > 0;
      // empty↔non-empty boundary: update check icons + popover chrome only.
      if (prevHad !== has) {
        if (has) {
          const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
          // Removing the icon in place — a re-render here would kill the input focus.
          if (enforceUvSingleFaceExclusivity(cur, printState.mode, pers, 'text')) {
            _q('.fd-icon-btn').forEach(el => el.classList.remove('is-active'));
            _chk('[data-art-check="icon"]', false);
            _q('.fd-art-subtoggle-btn[data-which="icon"]').forEach(b => b.classList.remove('has-check'));
          }
          avoidOverlapOnAdd(cur, printState.mode, pers, 'text');
          _swap('[data-print-action="set-text-position"]', el => el.dataset.position === cur.textPosition);
          if (printState.mode === 'engraving' && cur.textOrientation === 'vertical'
              && !isVerticalAllowed(cur, pers, printState.mode)) {
            cur.textOrientation = 'horizontal';
          }
        }
        document.querySelectorAll(`#shopify-section-${SID} [data-art-check="text"]`).forEach(el => {
          el.classList.toggle('is-on', has);
        });
        document.querySelectorAll(`#shopify-section-${SID} .fd-art-subtoggle-btn[data-which="text"]`).forEach(btn => {
          btn.classList.toggle('has-check', has);
        });
        _syncArtPosPill();
        if (ui.mobilePopover.open && ui.mobilePopover.tab === 'art') renderPopoverChrome();
        renderTabStrip();
        refreshPurchaseBar();
      }
      _spaceNotice(before, 'set-text', null, _guardLayout(before, 'text'));
      applyEngravingPreview();
      rotateArtCameraForElement('text');
    };
    textInput.addEventListener('input', handleTextInput);
    // compositionend fires when an IME (Chinese / Japanese / …) commits its text —
    // enforce there too, since the intermediate composing events were skipped above.
    textInput.addEventListener('compositionend', handleTextInput);
  }

  function renderActivePane() {
    if (ui.activeTab === 'bottle') renderBottlePane();
    else if (ui.activeTab === 'cap')  renderCapPane();
    else if (ui.activeTab === 'boot') renderBootPane();
    else if (ui.activeTab === 'art')  renderArtPane();
  }
  function showActivePane() {
    document.querySelectorAll(`#shopify-section-${SID} .fd-pane`).forEach(p => p.classList.remove('is-active'));
    document.getElementById(`fd-pane-${ui.activeTab}`)?.classList.add('is-active');
  }

  function activateTab(tab) {
    if (!visibleTabs().includes(tab)) return;
    ui.activeTab = tab;
    renderTabStrip();
    showActivePane();
    renderActivePane();
    zoomToTab(tab);
    updateGuideVisibility();
  }

  // Select handlers
  // Find the bottle product that owns a given variantId across the combined
  // colour list. Returns null if no product claims it.
  function _ownerProductIdForVariant(variantId) {
    for (const [pid, p] of Object.entries(config.bottles || {})) {
      if (p?.variants?.[variantId]) return pid;
    }
    return null;
  }
  function selectBodyColor(variantId) {
    if (variantId === state.bottleVariantId) return;
    const owningProductId = _ownerProductIdForVariant(variantId);
    if (!owningProductId) return;
    enqueue(async () => {
      toggleLoading(true);
      try {
        state.bottleProductId = owningProductId;
        state.bottleVariantId = variantId;
        const variantData = config.bottles[owningProductId]?.variants[variantId];
        // The bottle GLB is the SAME for every colour: a template allows only one
        // product type (valid_products keeps just the first type, dropping the
        // rest), so all colours share one product_type_grouping → one model. A
        // colour change is a re-skin only — never reload the bottle, so the mesh
        // never moves. (Cap/boot differ and keep their own GLB loading.)
        await ensureGuideOverlays();
        for (const [type, acc] of Object.entries(currentAccData())) {
          if (state.accessories[type]?.variantId == null) {
            const previewGlb = getPreviewGlb(type);
            if (previewGlb) await updateVariant(acc.anchor, previewGlb);
            else clearAnchor(acc.anchor);
          }
        }
        // Re-colour the body + default accessories from the new bottle variant;
        // selected accessories are excluded so a chosen cap/boot is never repainted.
        await applyBottleTextures();
        _swap('.fd-swatch[data-action="select-body-color"]', el => el.dataset.id === variantId);
        _txt('[data-section="bottle-color"] .fd-section__value', variantData?.title || '');
        _txt('.fd-pop-color-title__val', variantData?.title || ''); // mobile popover caption
        // Variant-level exclusion (custom.allow_engraving / allow_uv_print):
        // if the new variant disallows the ACTIVE mode, deactivate it. Mode
        // 'none' keeps the text/icon sub-state (switching back restores it)
        // but removes the service from the cart and clears the 3D overlays.
        if (printState.mode !== 'none' && !artAvailableModes().includes(printState.mode)) {
          printState.mode = 'none';
          _skipArtAutoDefault = true;
          try { renderPrintsPanel(); } finally { _skipArtAutoDefault = false; }
          applyEngravingPreview();
          updateGuideVisibility();
        }
        renderTabStrip();
        // Both modes disabled → the Art tab is gone; leave it if we're on it.
        if (!visibleTabs().includes(ui.activeTab)) activateTab(visibleTabs()[0] || 'bottle');
        if (ui.mobilePopover.open && !visibleTabs().includes(ui.mobilePopover.tab)) closePopover();
        // Refresh the open popover's chrome (pill/pager/CTA) against the new
        // visibleTabs so a stale "Next: Art" / "Done" label can't mislead.
        if (ui.mobilePopover.open) renderPopoverChrome();
        refreshPurchaseBar();
      } finally { toggleLoading(false); }
    });
  }

  function selectAccProduct(type, productIdOrNone) {
    const isNone = productIdOrNone === '__none__';
    const newProductId = isNone ? null : productIdOrNone;
    if (state.accessories[type]?.productId === newProductId) return;
    enqueue(async () => {
      toggleLoading(true);
      try {
        const acc = currentAccData()[type];
        if (!acc) return;
        if (!isNone && !acc.products[newProductId]) return;
        const product = isNone ? null : acc.products[newProductId];
        const firstVid = product
          ? (Object.entries(product.variants || {}).find(([, v]) => _isUsable(v))?.[0] || null)
          : null;
        state.accessories[type] = { productId: newProductId, variantId: firstVid };
        if (firstVid && product?.model) {
          await updateVariant(acc.anchor, product.model);
          const variantData = product.variants[firstVid];
          // Scope to THIS accessory's subtree so its textures can't paint bottle or
          // other-accessory meshes by name collision, and the stale-skin reset stays
          // confined to the accessory. Always call (even with no textures) so the
          // reset clears any prior skin.
          const sub = bottleGroup?.getObjectByName(`__sub_${acc.anchor}`);
          if (sub) await applyTextureLayers(variantData?.textures || [], [], sub);
        } else if (isNone) {
          const previewGlb = getPreviewGlb(type);
          // Force a fresh reload even when the GLB is unchanged: the previously
          // selected cap/boot left its own texture on the mesh, and reloading is
          // what resets it so applyBottleTextures can repaint it with the bottle
          // default colour. (Selecting "Not selected" is a type change, not a
          // colour change, so a reload here is expected.)
          if (previewGlb) await updateVariant(acc.anchor, previewGlb, true);
          else clearAnchor(acc.anchor);
          // Default cap/boot is back — fall back to the bottle variant's colour
          // for it (other selected accessories keep their own colour).
          await applyBottleTextures();
        } else {
          clearAnchor(acc.anchor);
        }
        if (type === 'cap')  renderCapPane();
        if (type === 'boot') renderBootPane();
        renderTabStrip();
        if (ui.mobilePopover.open && ui.mobilePopover.tab === type) {
          const steps = tabSteps(type);
          if (ui.mobilePopover.stepIdx >= steps.length) ui.mobilePopover.stepIdx = steps.length - 1;
          renderPopover();
        }
        refreshPurchaseBar();
      } finally { toggleLoading(false); }
    });
  }

  function selectAccColor(type, variantId) {
    const cur = state.accessories[type];
    if (cur?.productId == null) return;
    if (cur?.variantId === variantId) return;
    enqueue(async () => {
      toggleLoading(true);
      try {
        const acc = currentAccData()[type];
        if (!acc) return;
        const product = acc.products[cur.productId];
        if (!product) return;
        const wasNone = cur?.variantId == null;
        state.accessories[type] = { ...cur, variantId };
        const variantData = product.variants[variantId];
        if (wasNone && product.model) await updateVariant(acc.anchor, product.model);
        // Scope the re-skin + stale reset to THIS accessory's subtree only. Always
        // call (even with no textures) so switching to a colour that drops a mesh's
        // layer reverts it to the accessory GLB instead of keeping the prior colour.
        const sub = bottleGroup?.getObjectByName(`__sub_${acc.anchor}`);
        if (sub) await applyTextureLayers(variantData?.textures || [], [], sub);
        _swap(`.fd-swatch[data-action="select-acc-color"][data-type="${type}"]`, el => el.dataset.id === variantId);
        _txt(`[data-section="${type}-color"] .fd-section__value`, variantData?.title || '');
        _txt('.fd-pop-color-title__val', variantData?.title || ''); // mobile popover caption
        if (wasNone) {
          _q(`[data-section="${type}-color"] .fd-section__header`).forEach(h => {
            if (h.querySelector('.fd-section__check')) return;
            const span = document.createElement('span');
            span.className = 'fd-section__check';
            h.querySelector('.fd-section__label').after(span);
          });
        }
        renderTabStrip();
        refreshPurchaseBar();
      } finally { toggleLoading(false); }
    });
  }

  // Mobile popover: step definitions
  function tabSteps(tab) {
    if (tab === 'bottle') return [{ key: 'color', label: 'Colour', cta: null }];
    if (tab === 'cap') {
      const steps = [{ key: 'type', label: 'Cap Type', cta: capTypeChosen() ? 'Choose Colour' : null }];
      if (capTypeChosen()) steps.push({ key: 'color', label: 'Colour', cta: null });
      return steps;
    }
    if (tab === 'boot') {
      const steps = [{ key: 'type', label: 'Boot Type', cta: bootTypeChosen() ? 'Choose Colour' : null }];
      if (bootTypeChosen()) steps.push({ key: 'color', label: 'Colour', cta: null });
      return steps;
    }
    if (tab === 'art') {
      ensureArtModeDefault();
      const steps = [];
      const modes = artAvailableModes();
      if (modes.length > 1) steps.push({ key: 'mode', label: 'Personalisation', cta: 'Continue' });
      // No mode chosen yet → only the mode step; the text/icon steps appear once
      // the user picks Engrave or Print.
      if (printState.mode === 'none') return steps;
      const sub = ui.mobilePopover.artSubPill;
      // The carousel/Next follow the ACTIVE sub-pill's own progress: Text needs
      // text (→ Font → Position), Icon needs an icon (→ Position). The shared
      // Position step is reachable for the other element via the Position pill
      // (art-goto-position), not through this sequential flow.
      if (sub === 'text') {
        steps.push({ key: 'content', label: 'Your Text', cta: artHasText() ? 'Choose Font' : null });
        if (artHasText()) {
          steps.push({ key: 'font', label: 'Font Style', cta: 'Position' });
          // Position only appears once a font is chosen — it auto-selects, so
          // this holds as soon as text is entered (the prerequisite for jumping
          // straight to Position). A mode with no fonts has nothing to pick, so
          // don't make Position unreachable in that case.
          const cfg = artCurrentCfg();
          if (!cfg?.fonts?.length || artCurrentPs()?.font) {
            steps.push({ key: 'position', label: 'Position', cta: null });
          }
        }
      } else if (sub === 'badge') {
        steps.push({ key: 'content', label: 'Your Design', cta: artHasBadge() ? 'Choose Position' : null });
        if (artHasBadge()) steps.push({ key: 'position', label: 'Position', cta: null });
      } else {
        // Icon: Position appears once an icon is picked (its prerequisite).
        steps.push({ key: 'content', label: 'Your Icon', cta: artHasIcon() ? 'Choose Position' : null });
        if (artHasIcon()) steps.push({ key: 'position', label: 'Position', cta: null });
      }
      return steps;
    }
    return [];
  }

  // Facing (position) buttons for the new mobile Position layout view. Reuses
  // the existing set-text/icon-position handlers verbatim — only the label and
  // standalone wrapper differ from the card's inline version.
  function _facingHtml(which, label = 'Facing') {
    const ps = artCurrentPs();
    if (!ps) return '';
    const pers      = config.bottles[state.bottleProductId]?.personalizationSettings;
    const modeKey   = printState.mode === 'engraving' ? 'engraving' : 'uv_print';
    const positions = getPositions(pers, modeKey);
    if (positions.length <= 1) return '';
    const cur    = ps[_EGV_EL[which][0]];
    const action = `set-${which}-position`;
    const modeCfg = getModeCfg(pers, modeKey);
    // Engraving faces too short for the icon box are text-only; Badge needs its full box.
    const blocked = p => which === 'badge' ? !canFitBadgeOnFace(p, modeCfg)
      : which === 'icon' && modeKey === 'engraving' && !canFitIconOnFace(p, modeCfg);
    return `<div class="fd-position-group">
      <div class="fd-position-group__label">${label}</div>
      <div class="fd-position-btns">${positions.map(p =>
        `<button type="button" class="fd-position-btn${cur === p.key ? ' is-active' : ''}"${blocked(p) ? ' disabled' : ''} data-print-action="${action}" data-position="${escapeAttr(p.key)}">${escapeText(p.label || p.key)}</button>`
      ).join('')}</div>
    </div>`;
  }

  // Position settings grouped per the reference: Layout = Facing + Orientation
  // (text only) + Size; Alignment = Vertical Adjustment. All controls are the
  // existing helpers, so behaviour is identical to the old single-card view.
  function renderArtLayoutDetail(which) {
    if (which === 'text') return _facingHtml('text') + _orientationHtml() + _sizeGroupHtml('text');
    if (which === 'badge') return _facingHtml('badge');
    return _facingHtml('icon') + _sizeGroupHtml('icon');
  }
  function renderArtAlignmentDetail(which) {
    return _placementHtml(which);
  }
  // Whether this element has a Vertical Adjustment (Alignment). _placementHtml
  // returns '' when there's none — notably UV-print TEXT, which is always
  // full-height vertical. UV icon and all engraving keep it. Drives whether the
  // Alignment tab shows at all.
  function _posHasAlignment(which) {
    return renderArtAlignmentDetail(which).trim().length > 0;
  }

  // Text / Icon / Position sub-toggle shown on every Art step. `active` is the
  // segment that reads as selected ('text' | 'icon' | 'position'). Position is
  // disabled until text or icon exists; tapping it jumps to the Position step.
  function _artSubtoggle3(active) {
    const hasAny = artHasText() || artHasIcon() || artHasBadge();
    const hasBadge = artBadgeDesigns().length > 0;
    return `<div class="fd-art-subtoggle-wrap"><div class="fd-art-subtoggle fd-art-subtoggle--${hasBadge ? 4 : 3}">
      <button type="button" class="fd-art-subtoggle-btn${active === 'text' ? ' is-active' : ''}${artHasText() ? ' has-check' : ''}" data-action="art-subtoggle" data-which="text">Text</button>
      <button type="button" class="fd-art-subtoggle-btn${active === 'icon' ? ' is-active' : ''}${artHasIcon() ? ' has-check' : ''}" data-action="art-subtoggle" data-which="icon">Icon</button>
      ${hasBadge ? `<button type="button" class="fd-art-subtoggle-btn${active === 'badge' ? ' is-active' : ''}${artBadgeDone() ? ' has-check' : ''}" data-action="art-subtoggle" data-which="badge">Special Text</button>` : ''}
      <button type="button" class="fd-art-subtoggle-btn${active === 'position' ? ' is-active' : ''}" data-action="art-goto-position"${hasAny ? '' : ' disabled'}>Position</button>
    </div></div>`;
  }

  function renderArtPositionBody() {
    const m     = ui.mobilePopover;
    const icons = config.fallbackIcons || {};
    // Screen A — pick which element's position to adjust.
    if (!m.posElement) {
      const tDis = artHasText() ? '' : ' disabled';
      const iDis = artHasIcon() ? '' : ' disabled';
      return `
        ${_artSubtoggle3('position')}
        <div class="fd-pos-intro">Adjust your position at one go!</div>
        <div class="fd-pos-cards">
          <button type="button" class="fd-pos-card" data-action="art-pos-pick" data-which="text"${tDis} aria-label="Adjust text position">${icons.textArt || ''}</button>
          <button type="button" class="fd-pos-card" data-action="art-pos-pick" data-which="icon"${iDis} aria-label="Adjust icon position">${icons.iconArt || ''}</button>
          ${artBadgeDesigns().length ? `<button type="button" class="fd-pos-card" data-action="art-pos-pick" data-which="badge"${artHasBadge() ? '' : ' disabled'} aria-label="Adjust special text position">${icons.badgeArt || ''}</button>` : ''}
        </div>`;
    }
    // Screen B — Layout / Alignment detail for the chosen element.
    const pe  = m.posElement;
    const dt  = m.posDetailTab;
    const elToggle = `<div class="fd-pos-eltoggle">
      <button type="button" class="fd-pos-eltoggle__btn${pe === 'text' ? ' is-active' : ''}"${artHasText() ? '' : ' disabled'} data-action="art-pos-element" data-which="text"><span class="fd-pos-eltoggle__ic">${icons.textArt || ''}</span>Text</button>
      <button type="button" class="fd-pos-eltoggle__btn${pe === 'icon' ? ' is-active' : ''}"${artHasIcon() ? '' : ' disabled'} data-action="art-pos-element" data-which="icon"><span class="fd-pos-eltoggle__ic">${icons.iconArt || ''}</span>Icon</button>
      ${artBadgeDesigns().length ? `<button type="button" class="fd-pos-eltoggle__btn${pe === 'badge' ? ' is-active' : ''}"${artHasBadge() ? '' : ' disabled'} data-action="art-pos-element" data-which="badge"><span class="fd-pos-eltoggle__ic">${icons.badgeArt || ''}</span>Special Text</button>` : ''}
    </div>`;
    // No alignment for this element (e.g. UV-print text) → force the Layout view.
    const effDt   = (dt === 'alignment' && _posHasAlignment(pe)) ? 'alignment' : 'layout';
    const heading = effDt === 'alignment' ? 'Alignment' : 'Layout';
    const detail  = effDt === 'alignment' ? renderArtAlignmentDetail(pe) : renderArtLayoutDetail(pe);
    // Back chevron lives in the body, on the same row as the Text/Icon toggle.
    return `<div class="fd-pos-dethead">
        <button type="button" class="fd-pos-back" data-action="art-pos-back" aria-label="Back">${ICON_ARROW_LEFT_S}</button>
        ${elToggle}
      </div>
      <div class="fd-pos-heading">${heading}</div>
      ${_artNoticeHtml()}
      <div class="fd-pos-detail">${detail}</div>`;
  }

  function renderPopoverBody(tab, stepKey) {
    if (tab === 'bottle') {
      if (stepKey === 'color') {
        const name = config.bottles[state.bottleProductId]?.variants[state.bottleVariantId]?.title || '';
        return mobileColorTitleHtml(name) + renderBottleColorRow();
      }
    }
    if (tab === 'cap') {
      if (stepKey === 'type')  return renderAccTypeRow('cap');
      if (stepKey === 'color') return accColorBodyMobile('cap');
    }
    if (tab === 'boot') {
      if (stepKey === 'type')  return renderAccTypeRow('boot');
      if (stepKey === 'color') return accColorBodyMobile('boot');
    }
    if (tab === 'art') {
      // FAQ link pinned at the top of the Art sheet (under the pager), on every
      // art step. Targets the mobile bottom popover.
      const lead = faqLinkHtml('fd-faq-popover');
      if (stepKey === 'mode') return lead + renderArtModeRow();
      const sub = ui.mobilePopover.artSubPill;
      const toggle = _artSubtoggle3(sub);
      if (stepKey === 'content') {
        if (sub === 'text') return lead + toggle + renderArtTextCard({ showInputOnly: true });
        if (sub === 'badge') return lead + toggle + renderArtBadgeCard({ showContentOnly: true });
        return lead + toggle + renderArtIconCard({ showIconsOnly: true });
      }
      if (stepKey === 'font')     return lead + toggle + renderArtTextCard({ showFontOnly: true });
      if (stepKey === 'position') return lead + renderArtPositionBody();
    }
    return '';
  }

  function renderPopover() {
    const popover = document.getElementById('fd-popover');
    if (!popover) return;
    const m = ui.mobilePopover;
    popover.classList.toggle('is-open', m.open);
    popover.setAttribute('aria-hidden', m.open ? 'false' : 'true');
    if (!m.open || !m.tab) return;

    const tab = m.tab;
    const steps = tabSteps(tab);
    if (steps.length === 0) { closePopover(); return; }
    if (m.stepIdx >= steps.length) m.stepIdx = steps.length - 1;
    if (m.stepIdx < 0) m.stepIdx = 0;
    const step = steps[m.stepIdx];

    renderPopoverChrome();
    document.getElementById('fd-popover-body').innerHTML = renderPopoverBody(tab, step.key);

    const popoverBody = document.getElementById('fd-popover-body');
    if (tab === 'art' && step.key === 'content' && ui.mobilePopover.artSubPill === 'text') {
      bindTextInputDebounce(popoverBody);
    }
    if (tab === 'art' && step.key === 'content' && ui.mobilePopover.artSubPill === 'badge') bindBadgeInput(popoverBody);
    if (tab === 'art' && step.key === 'position') {
      bindYSliders(popoverBody);
    }
    if (step.key === 'color') alignSwatchGrid(popoverBody);

    // Sheet height can change between steps (different content). After the
    // layout settles, re-apply the canvas viewport so the bottle re-fits the
    // updated visible area above the sheet.
    if (m.open) requestAnimationFrame(() => applyMobileSheetViewport());
  }

  // Chrome only (tab pill, pager, error, CTA) — body untouched so text
  // input keeps focus while typing.
  function renderPopoverChrome() {
    const m = ui.mobilePopover;
    if (!m.open || !m.tab) return;
    const steps = tabSteps(m.tab);
    if (steps.length === 0) return;
    if (m.stepIdx >= steps.length) m.stepIdx = steps.length - 1;
    if (m.stepIdx < 0) m.stepIdx = 0;
    // Art pill: shows the active mode — "Engrave" or "Print" — anywhere except
    // the mode (Personalisation) step itself, which reads "Art". When only one
    // mode is available there's no mode step, so it always shows the mode name.
    let pillLabel = tabConfig(m.tab).pillLabel;
    if (m.tab === 'art' && steps[m.stepIdx]?.key !== 'mode') {
      pillLabel = printState.mode === 'engraving' ? 'Engrave'
                : printState.mode === 'uv_print'  ? 'Colour Print' : pillLabel;
    }
    document.getElementById('fd-popover-tab-pill').textContent = pillLabel;
    renderPopoverPager();
    renderPopoverCta();
  }

  // True when the Art Position step has drilled into an element's Layout /
  // Alignment detail — the carousel becomes a single Back chevron and the CTA
  // becomes the Layout/Alignment tab bar.
  function _inArtPosDetail() {
    const m = ui.mobilePopover;
    if (m.tab !== 'art' || !m.posElement) return false;
    return tabSteps('art')[m.stepIdx]?.key === 'position';
  }

  // Draggable step carousel: the current step sits centred, neighbours fade out
  // at the mask edges. Only the immediate prev/next step is clickable — each
  // step gates the next (text typed / icon picked on the content step).
  function renderPopoverPager() {
    const m = ui.mobilePopover;
    const steps = tabSteps(m.tab);
    const cur = m.stepIdx;
    const num = (i) => String(i + 1).padStart(2, '0');
    const pager = document.getElementById('fd-popover-pager');
    const track = document.getElementById('fd-popover-steps');
    pager.style.display = ''; // reset (may have been hidden in Position detail)
    // Position detail: the Back chevron lives in the body, so hide the carousel.
    if (_inArtPosDetail()) {
      pager.style.display = 'none';
      pager.dataset.tab = ''; // force a full rebuild when the carousel returns
      pager.innerHTML = '';
      return;
    }
    pager.classList.toggle('is-single', steps.length <= 1); // lock scroll when nothing to scroll to
    // Rebuild only when the carousel structure changes (tab/step-count); for a
    // step change within the same tab, update in place + smooth-scroll so the
    // carousel animates between steps instead of snapping from a fresh rebuild.
    const built = track && pager.dataset.tab === m.tab
      && track.querySelectorAll('.fd-popover__step').length === steps.length;
    if (built) {
      Array.from(track.children).forEach((btn, i) => {
        btn.textContent = `${num(i)} ${steps[i].label}`; // labels can change (e.g. Your Text ↔ Your Icon)
        btn.classList.toggle('is-current', i === cur);
        // Every visible step is reachable (its prerequisite is already met).
        btn.disabled = false;
        btn.dataset.action = 'step-jump';
        btn.dataset.stepIdx = i;
      });
      pager.querySelector('[data-action="step-prev"]').disabled = !steps[cur - 1];
      pager.querySelector('[data-action="step-next"]').disabled = !steps[cur + 1];
      centerActiveStep(true);
      return;
    }
    const items = steps.map((s, i) =>
      `<button type="button" class="fd-popover__step${i === cur ? ' is-current' : ''}" data-action="step-jump" data-step-idx="${i}">${num(i)} ${escapeText(s.label)}</button>`
    ).join('');
    pager.dataset.tab = m.tab;
    pager.innerHTML = `
      <button type="button" class="fd-popover__step-arrow" data-action="step-prev"${steps[cur - 1] ? '' : ' disabled'}>${ICON_ARROW_LEFT_S}</button>
      <div class="fd-popover__steps-wrap"><div class="fd-popover__steps" id="fd-popover-steps">${items}</div></div>
      <button type="button" class="fd-popover__step-arrow" data-action="step-next"${steps[cur + 1] ? '' : ' disabled'}>${ICON_ARROW_RIGHT_S}</button>
    `;
    bindStepCarouselSnap(document.getElementById('fd-popover-steps'));
    centerActiveStep(false);
  }

  // Carousel selection — SELECTION DRIVES THE SNAP, never the reverse.
  // The strip scrolls freely while dragging (no CSS scroll-snap); only when the
  // touch is RELEASED do we pick the nearest step, select it first (stepIdx +
  // is-current via renderPopover), and *then* centre it into view. If the nearest
  // is still the current step (dragged back and forth), we just snap back to it.
  // Acting purely on touch-release — not on scroll events — means the
  // programmatic centring can never retrigger this (which is what caused the
  // earlier 02 ↔ 01 loop).
  function bindStepCarouselSnap(track) {
    if (!track) return;
    let dragging = false;
    track.addEventListener('touchstart', () => { dragging = true; }, { passive: true });
    const onRelease = () => {
      if (!dragging) return;
      dragging = false;
      _onStepRelease(track);
    };
    track.addEventListener('touchend', onRelease, { passive: true });
    track.addEventListener('touchcancel', onRelease, { passive: true });
  }
  function _onStepRelease(track) {
    const els = track.querySelectorAll('.fd-popover__step');
    if (els.length <= 1) return;
    const tRect = track.getBoundingClientRect();
    const mid = tRect.left + tRect.width / 2;
    let nearest = 0, best = Infinity;
    els.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const d = Math.abs((r.left + r.width / 2) - mid);
      if (d < best) { best = d; nearest = i; }
    });
    const cur = ui.mobilePopover.stepIdx;
    if (nearest === cur) centerActiveStep(true); // unchanged → snap back to the selected step
    else                 popoverGoTo(nearest);   // select first (renderPopover), then it centres in
  }

  function centerActiveStep(smooth) {
    const track = document.getElementById('fd-popover-steps');
    const active = track?.querySelector('.fd-popover__step.is-current');
    if (!active) return;
    const delta = () => {
      const t = track.getBoundingClientRect();
      const a = active.getBoundingClientRect();
      return (a.left + a.width / 2) - (t.left + t.width / 2);
    };
    if (smooth) {
      track.scrollBy({ left: delta(), behavior: 'smooth' });
    } else {
      track.scrollLeft += delta();
      requestAnimationFrame(() => { track.scrollLeft += delta(); });
    }
  }

  function renderPopoverCta() {
    const m = ui.mobilePopover;
    if (!m.open || !m.tab) return;
    const steps = tabSteps(m.tab);
    const step = steps[m.stepIdx];
    const cta = document.getElementById('fd-popover-cta');
    // Tab-bar variant drops the top divider and left-aligns (see CSS).
    cta.classList.toggle('fd-popover__cta-wrap--tabs', _inArtPosDetail());
    // Position detail: bottom Layout / Alignment tab bar instead of a Next CTA.
    if (_inArtPosDetail()) {
      const icons = config.fallbackIcons || {};
      const dt = m.posDetailTab;
      // Hide the Alignment tab when this element has no Vertical Adjustment
      // (UV-print text). The active tab + body fall back to Layout in that case.
      const showAlign = _posHasAlignment(m.posElement);
      const effDt = (dt === 'alignment' && showAlign) ? 'alignment' : 'layout';
      cta.innerHTML = `<div class="fd-pos-tabbar">
        <button type="button" class="fd-pos-tab${effDt === 'layout' ? ' is-active' : ''}" data-action="art-pos-tab" data-tab="layout"><span class="fd-pos-tab__ic">${icons.layout || ''}</span><span class="fd-pos-tab__lbl">Layout</span></button>
        ${showAlign ? `<button type="button" class="fd-pos-tab${effDt === 'alignment' ? ' is-active' : ''}" data-action="art-pos-tab" data-tab="alignment"><span class="fd-pos-tab__ic">${icons.alignment || ''}</span><span class="fd-pos-tab__lbl">Alignment</span></button>` : ''}
      </div>
      <button type="button" class="fd-cta-btn is-pink fd-pos-done" data-action="close-popover">I'm done</button>`;
      return;
    }
    // Position card-select (Screen A) is just a Text/Icon picker — no CTA. The
    // bar keeps its height via the .fd-popover__cta-wrap min-height (CSS).
    if (m.tab === 'art' && step?.key === 'position') {
      cta.innerHTML = '';
      return;
    }
    // Mode step with nothing chosen yet → no "Next: Continue" until the user
    // picks Engrave or Print (the bar keeps its height via CSS min-height).
    if (m.tab === 'art' && step?.key === 'mode' && printState.mode === 'none') {
      cta.innerHTML = '';
      return;
    }
    const isLast = m.stepIdx === steps.length - 1;
    // Disable Next on the Art content step until the active sub-toggle's
    // selection exists (text typed / icon picked) — the disabled button is the affordance.
    let nextDisabled = false;
    if (!isLast && m.tab === 'art' && step.key === 'content') {
      const sub = m.artSubPill;
      if (sub === 'text' && !artHasText()) nextDisabled = true;
      if (sub === 'icon' && !artHasIcon()) nextDisabled = true;
      if (sub === 'badge' && !artBadgeDone()) nextDisabled = true;
    }
    if (isLast) {
      const nt = nextVisibleTab(m.tab);
      const label = m.tab === 'art' || !nt ? 'Done' : `Next: ${tabConfig(nt).label}`;
      cta.innerHTML = `<button type="button" class="fd-cta-btn is-pink" data-action="popover-final">
        <span>${escapeText(label)}</span>${(m.tab === 'art' || !nt) ? '' : `<span class="fd-cta-btn__arrow">${ICON_ARROW_RIGHT}</span>`}
      </button>`;
    } else {
      cta.innerHTML = `<button type="button" class="fd-cta-btn" data-action="popover-next"${nextDisabled ? ' disabled' : ''}>
        <span>Next: ${escapeText(step.cta || 'Continue')}</span>
        <span class="fd-cta-btn__arrow">${ICON_ARROW_RIGHT}</span>
      </button>`;
    }
  }

  function openPopover(tab) {
    if (!visibleTabs().includes(tab)) return;
    if (tab !== 'art') _dropEmptyBadge();
    ui.mobilePopover.open = true;
    ui.mobilePopover.tab  = tab;
    ui.mobilePopover.stepIdx = 0;
    ui.mobilePopover.error = '';
    ui.mobilePopover.posElement = null;
    ui.mobilePopover.posDetailTab = 'layout';
    // Once the user has moved past the Engrave/Print (mode) step, reopening Art
    // skips straight to the text/icon setup step. The mode step stays in the
    // carousel, so they can still go back to change it.
    if (tab === 'art' && ui.mobilePopover.artModeAcknowledged) {
      const ci = tabSteps('art').findIndex(s => s.key === 'content');
      if (ci > 0) ui.mobilePopover.stepIdx = ci;
    }
    ui.activeTab = tab;
    renderTabStrip();
    showActivePane();
    renderActivePane();
    renderPopover();
    // The pager keeps its previous scroll across close/reopen and the on-render
    // centering gets cut short by the sheet slide-in, so hard-center the active
    // step once layout has settled (a snap is invisible during the open anim).
    requestAnimationFrame(() => centerActiveStep(false));
    updateGuideVisibility();
    // Always zoom to the tab's focus on open — the model re-centres on close,
    // so reopening the same tab must zoom in again.
    setTimeout(() => _afterSheetTransition(tab), 320);
  }
  function closePopover() {
    _dropEmptyBadge();
    ui.mobilePopover.open = false;
    ui.mobilePopover.error = '';
    renderPopover();
    updateGuideVisibility();
    // Close: zoom back to bottle framing (size only — camera direction is
    // preserved by the non-art branch of zoomToTab).
    setTimeout(() => _afterSheetTransition('bottle'), 320);
  }
  function _afterSheetTransition(tabToFocus) {
    applyMobileSheetViewport();
    if (tabToFocus) zoomToTab(tabToFocus);
  }
  function popoverPrev() {
    if (ui.mobilePopover.stepIdx <= 0) return;
    ui.mobilePopover.stepIdx--;
    ui.mobilePopover.error = '';
    ui.mobilePopover.posElement = null; // re-enter Position at the card-select
    renderPopover();
  }
  // Jump directly to any VISIBLE step (carousel tap / swipe-snap). A step only
  // exists in tabSteps once its prerequisite is met (text → Font/Position only
  // after text is entered + font auto-selected; icon → Position only after an
  // icon is picked), so any in-range index is safe to land on — no gate needed.
  function popoverGoTo(idx) {
    const m = ui.mobilePopover;
    const steps = tabSteps(m.tab);
    if (idx < 0 || idx >= steps.length || idx === m.stepIdx) return;
    // Landing anywhere past the mode step counts as acknowledging it.
    if (m.tab === 'art' && idx > 0 && steps[0]?.key === 'mode') m.artModeAcknowledged = true;
    m.error = '';
    m.stepIdx = idx;
    m.posElement = null; // re-enter Position at the card-select
    renderPopover();
  }
  function popoverNext() {
    const tab = ui.mobilePopover.tab;
    const steps = tabSteps(tab);
    const stepIdx = ui.mobilePopover.stepIdx;
    const step = steps[stepIdx];
    // Art: gate progression on the active sub-pill — Text needs text, Icon needs
    // an icon. The CTA renders disabled to match (see renderPopoverCta).
    if (tab === 'art' && step.key === 'content') {
      const sub = ui.mobilePopover.artSubPill;
      if (sub === 'text' && !artHasText()) return;
      if (sub === 'icon' && !artHasIcon()) return;
      if (sub === 'badge' && !artBadgeDone()) return;
    }
    if (stepIdx >= steps.length - 1) return;
    if (tab === 'art' && step.key === 'mode') ui.mobilePopover.artModeAcknowledged = true;
    ui.mobilePopover.stepIdx++;
    ui.mobilePopover.posElement = null; // re-enter Position at the card-select
    renderPopover();
  }
  function popoverFinal() {
    const tab = ui.mobilePopover.tab;
    if (tab === 'art') { closePopover(); return; }
    const nt = nextVisibleTab(tab);
    if (nt) openPopover(nt);
    else closePopover();
  }

  // Per-tab camera zoom (tween in animate loop)
  const _zoom = { from: null, target: null, t: 1, duration: 450, startTime: 0 };

  function objectBox(obj) {
    if (!obj) return null;
    const box = new THREE.Box3().setFromObject(obj);
    return box.isEmpty() ? null : box;
  }
  function findNamedObject(name) {
    if (!name || !bottleGroup) return null;
    let hit = null;
    bottleGroup.traverse(o => { if (!hit && o.name === name) hit = o; });
    return hit;
  }

  // { center, dist }. center.x/z=0 (pivot on bottle Y axis); center.y =
  // target part's Y; dist = camera distance (close-in for cap/boot/art).
  function tabZoomTarget(tab) {
    if (!bottleGroup) return null;
    const fov = camera.fov * Math.PI / 180;
    const aspect = camera.aspect || 1;
    // Distance that fits a part of vertical size `partY` and horizontal size
    // `partX` into the view, padded by `pad`.
    const distFor = (partY, partX, pad) => {
      const distY = (partY / 2) / Math.tan(fov / 2);
      const distX = (partX / 2) / Math.tan(fov / 2) / Math.max(aspect, 0.0001);
      return Math.max(Math.max(distY, distX) * pad, 0.5);
    };

    const bottleBox  = objectBox(bottleGroup);
    if (!bottleBox) return null;
    const bottleSize = bottleBox.getSize(new THREE.Vector3());
    const bottleCtr  = bottleBox.getCenter(new THREE.Vector3());

    if (tab === 'bottle') {
      // Fit the whole bottle with a small margin. Mobile (non-fullscreen) pulls
      // back a bit more so the bottle-type dropdown doesn't overlap the base.
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      return {
        center: new THREE.Vector3(0, bottleCtr.y, 0),
        dist: distFor(bottleSize.y, bottleSize.x, (isMobile && !_fullscreenOpen) ? 1.3 : 1.1)
      };
    }

    if (tab === 'cap' || tab === 'boot') {
      const anchorName = currentAccData()[tab]?.anchor;
      const node = findNamedObject(anchorName);
      if (!node) return null;
      const wpos = new THREE.Vector3();
      node.getWorldPosition(wpos);
      // Use the attached preview/model's own box if it has size; otherwise fall
      // back to ~25% of the bottle height (a sensible cap/boot region).
      const subBox  = objectBox(node);
      let partY = 0, partX = 0;
      if (subBox && !subBox.isEmpty()) {
        const s = subBox.getSize(new THREE.Vector3());
        partY = s.y; partX = Math.max(s.x, bottleSize.x * 0.5);
      }
      if (partY < bottleSize.y * 0.05) {
        partY = bottleSize.y * 0.25;
        partX = bottleSize.x * 0.8;
      }
      // Boot sits at the bottom of the bottle, so aiming the camera at its
      // anchor leaves a lot of empty viewport below. Nudge the target up so
      // the boot lands lower in the frame.
      const centerY = tab === 'boot' ? wpos.y + partY * 0.35 : wpos.y;
      return {
        center: new THREE.Vector3(0, centerY, 0),
        dist: distFor(partY, partX, 1.4)
      };
    }

    if (tab === 'art') {
      const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
      const names = [
        ...(pers?.engraving?.positions || []).map(p => p.mesh_name),
        pers?.color_print?.mesh_name,
        'Front_Engraving',
        'Back_Engraving',
        'UV_Print'
      ].filter(Boolean);
      for (const n of names) {
        const m = findNamedObject(n);
        if (!m) continue;
        const subBox = objectBox(m);
        if (!subBox || subBox.isEmpty()) continue;
        const s = subBox.getSize(new THREE.Vector3());
        // Use the mesh's bounding-box CENTRE (in world coords) rather than the
        // mesh's pivot/origin — the pivot can sit far from the visible mesh
        // (often at the bottle's base), which made the camera focus on the
        // boot instead of the engraving region.
        const c = subBox.getCenter(new THREE.Vector3());
        return {
          center: new THREE.Vector3(0, c.y, 0),
          dist: distFor(s.y * 1.1, Math.max(s.x, bottleSize.x) * 1.05, 1.0)
        };
      }
      // Fallback: middle of the bottle, slight zoom-in vs the full view.
      return {
        center: new THREE.Vector3(0, bottleCtr.y, 0),
        dist: distFor(bottleSize.y * 0.6, bottleSize.x * 0.6, 1.0)
      };
    }
    return null;
  }

  // Stash spherical (radius/theta/phi) for both ends of the zoom tween so the
  // camera orbits AROUND the target instead of cutting through it — this is
  // what stops the front↔back rotation from feeling like a zoom in/out.
  function _setZoomTween(newCamPos, newTarget) {
    const curCamRel = new THREE.Vector3().subVectors(camera.position, controls.target);
    const newCamRel = new THREE.Vector3().subVectors(newCamPos, newTarget);
    _zoom.from   = { camera: camera.position.clone(), target: controls.target.clone() };
    _zoom.target = { camera: newCamPos.clone(),       target: newTarget.clone() };
    _zoom.fromSpherical   = new THREE.Spherical().setFromVector3(curCamRel);
    _zoom.targetSpherical = new THREE.Spherical().setFromVector3(newCamRel);
    _zoom.t = 0;
    _zoom.startTime = performance.now();
  }

  // theta=0 is +Z (front), ±π is -Z (back). Returns tween TARGET during
  // a zoom tween so option changes don't restart against a stale 'from'.
  function _currentCamSpherical() {
    const inFlight = _zoom.t < 1 && _zoom.target;
    const camPos   = inFlight ? _zoom.target.camera : camera.position;
    const tgtPos   = inFlight ? _zoom.target.target : controls.target;
    const rel = new THREE.Vector3().subVectors(camPos, tgtPos);
    if (rel.lengthSq() < 0.0001) return null;
    return new THREE.Spherical().setFromVector3(rel);
  }

  // Art-tab safe-area geometry. Camera sees a 120° cone; "fully in view"
  // iff |Δθ| + halfWidth ≤ 60°. Safe area wider than 120° → always rotate.
  const _ART_VIEW_HALF_CONE = Math.PI / 3;

  function _shortestArcDelta(from, to) {
    const TAU = Math.PI * 2;
    return ((to - from + Math.PI) % TAU + TAU) % TAU - Math.PI;
  }

  // UV: uses position.degree. Engraving: uses mesh bbox centre (NOT
  // getWorldPosition — flat decal meshes have origin at bottle centre).
  // Falls back to canonical key azimuth when degenerate.
  function getSafeAreaAzimuth(mode, positionKey) {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    if (!pers || !positionKey) return null;
    if (mode === 'uv_print') {
      const face = pers.color_print?.positions?.find(p => p.key === positionKey);
      if (!face) {
        console.warn(`[FlexiDesign] color_print position "${positionKey}" not found in metaobject`);
        return null;
      }
      if (face.degree == null) {
        console.warn(`[FlexiDesign] color_print position "${positionKey}" has no degree set`);
        return null;
      }
      return _shortestArcDelta(0, face.degree * Math.PI / 180);
    }
    if (mode === 'engraving') {
      const face = pers.engraving?.positions?.find(p => p.key === positionKey);
      if (!face) {
        console.warn(`[FlexiDesign] engraving position "${positionKey}" not found in metaobject`);
        return null;
      }
      if (!bottleGroup) return null;
      const mesh = bottleGroup.getObjectByName(face.mesh_name);
      if (!mesh) {
        console.warn(`[FlexiDesign] engraving mesh "${face.mesh_name}" not found in GLB`);
        return null;
      }
      const center = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
      const target = controls?.target || new THREE.Vector3();
      const dx = center.x - target.x, dz = center.z - target.z;
      if (dx * dx + dz * dz <= 1e-6) {
        console.warn(`[FlexiDesign] engraving mesh "${face.mesh_name}" bbox is degenerate (centred on bottle axis)`);
        return null;
      }
      return Math.atan2(dx, dz);
    }
    return null;
  }

  // Half the angular extent of the safe area on the bottle.
  // UV: π × max_width_mm / wrap_mm. Engraving: smallest enclosing arc
  // of mesh bbox corners on XZ.
  function getSafeAreaHalfWidth(mode, positionKey) {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    if (!pers || !positionKey) return 0;
    if (mode === 'uv_print') {
      const wrap = pers.color_print;
      const face = wrap?.positions?.find(p => p.key === positionKey);
      if (!face || !wrap?.width_mm) return 0;
      return Math.PI * (face.max_width_mm ?? 0) / wrap.width_mm;
    }
    if (mode === 'engraving' && bottleGroup) {
      const face = pers.engraving?.positions?.find(p => p.key === positionKey);
      if (!face) return 0;
      const mesh = bottleGroup.getObjectByName(face.mesh_name);
      if (!mesh) return 0;
      const box = new THREE.Box3().setFromObject(mesh);
      const target = controls?.target || new THREE.Vector3();
      const az = [
        Math.atan2(box.min.x - target.x, box.min.z - target.z),
        Math.atan2(box.max.x - target.x, box.min.z - target.z),
        Math.atan2(box.min.x - target.x, box.max.z - target.z),
        Math.atan2(box.max.x - target.x, box.max.z - target.z),
      ].sort((a, b) => a - b);
      let maxGap = az[0] + 2 * Math.PI - az[az.length - 1];
      for (let i = 1; i < az.length; i++) {
        const gap = az[i] - az[i - 1];
        if (gap > maxGap) maxGap = gap;
      }
      return (2 * Math.PI - maxGap) / 2;
    }
    return 0;
  }

  function isSafeAreaInView(mode, positionKey) {
    const sph = _currentCamSpherical();
    if (!sph) return true;
    const safeAz = getSafeAreaAzimuth(mode, positionKey);
    if (safeAz == null) return true;
    const halfWidth = getSafeAreaHalfWidth(mode, positionKey);
    const delta = Math.abs(_shortestArcDelta(sph.theta, safeAz));
    return delta + halfWidth <= _ART_VIEW_HALF_CONE;
  }

  // Default position key for the current mode — the position flagged default:true
  // (else the first). The camera's first Art framing aims here, matching where a
  // fresh text/icon starts.
  function _firstPositionKey() {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    return getDefaultPositionKey(pers, printState.mode);
  }
  // Among occupied positions, pick the one that appears earliest in the
  // metaobject's positions array (the author's intended priority). Falls
  // back to the first position when nothing is placed yet.
  function artFrontPriorityFace() {
    const ps = artCurrentPs();
    if (!ps) return _firstPositionKey();
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const positionKeys = getPositions(pers, printState.mode).map(p => p.key);
    const occupied = new Set();
    if ((ps.text || '').trim() && ps.textPosition) occupied.add(ps.textPosition);
    if (ps.icon && ps.iconPosition)                 occupied.add(ps.iconPosition);
    if (ps.badge && ps.badgePosition)           occupied.add(ps.badgePosition);
    for (const key of positionKeys) if (occupied.has(key)) return key;
    return _firstPositionKey();
  }

  function rotateArtCameraToFace(targetFaceKey) {
    if (!camera || !controls || ui.activeTab !== 'art' || !bottleGroup) return;
    const sph = _currentCamSpherical();
    if (!sph) return;
    const safeAz = getSafeAreaAzimuth(printState.mode, targetFaceKey);
    if (safeAz == null) return;
    const delta = _shortestArcDelta(sph.theta, safeAz);
    if (Math.abs(delta) < 1e-4) return;
    const newRel = new THREE.Vector3().setFromSpherical(
      new THREE.Spherical(sph.radius, sph.phi, sph.theta + delta)
    );
    const newCamPos = controls.target.clone().add(newRel);
    _setZoomTween(newCamPos, controls.target.clone());
  }

  function rotateArtCameraIfNotInView(targetFaceKey) {
    if (!targetFaceKey) return;
    if (isSafeAreaInView(printState.mode, targetFaceKey)) return;
    rotateArtCameraToFace(targetFaceKey);
  }

  function _artElementHasContent(element) {
    const ps = artCurrentPs();
    if (!ps) return false;
    return element === 'text' ? (ps.text || '').trim().length > 0 : element === 'badge' ? !!ps.badge : !!ps.icon;
  }

  // ForElement: conditional (120° rule). ForPositionChange: unconditional sweep.
  function rotateArtCameraForElement(element) {
    if (!_artElementHasContent(element)) return;
    const ps = artCurrentPs();
    const face = ps[(_EGV_EL[element] || _EGV_EL.icon)[0]];
    rotateArtCameraIfNotInView(face);
  }

  function rotateArtCameraForPositionChange(element, newFaceKey) {
    if (!_artElementHasContent(element)) return;
    rotateArtCameraToFace(newFaceKey);
  }

  // "Front / Back" preview button. Snaps to the nearest of Front (theta 0) and
  // Back (theta ±π); when already squared on a face (within ~2°) it flips 180° to
  // the other. Also levels the camera back to eye-level (phi = 90°) so the view
  // never stays tilted looking down. ROTATE + LEVEL ONLY — the current target
  // and radius (zoom) are kept, so a tab's focus/zoom is never repositioned.
  function rotateFrontBack() {
    if (!camera || !controls) return;
    const sph = _currentCamSpherical();
    if (!sph) return;
    const FRONT = 0, BACK = Math.PI, EPS = 2 * Math.PI / 180;
    const dFront = Math.abs(_shortestArcDelta(sph.theta, FRONT));
    const dBack  = Math.abs(_shortestArcDelta(sph.theta, BACK));
    let targetTheta;
    if (dFront <= EPS)     targetTheta = BACK;                  // on front → flip to back
    else if (dBack <= EPS) targetTheta = FRONT;                 // on back → flip to front
    else                   targetTheta = dFront <= dBack ? FRONT : BACK; // nearest face
    const newTheta = sph.theta + _shortestArcDelta(sph.theta, targetTheta);
    const newRel = new THREE.Vector3().setFromSpherical(
      new THREE.Spherical(sph.radius, Math.PI / 2, newTheta) // phi = 90° → eye-level
    );
    _setZoomTween(controls.target.clone().add(newRel), controls.target.clone());
  }

  // Live label for the "Front / Back" button. Throttled (~300ms) so a drag only
  // touches the DOM a handful of times/sec, and the label span is only rewritten
  // when the face actually flips — never affects rotation or any other state.
  let _rotateViewLabel = 'Front'; // matches the server-rendered initial (front) view
  let _rotateLabelAt = 0;
  function updateRotateViewLabel() {
    if (!camera || !controls) return;
    const now = performance.now();
    if (now - _rotateLabelAt < 300) return;
    _rotateLabelAt = now;
    const dx = camera.position.x - controls.target.x;
    const dz = camera.position.z - controls.target.z;
    const label = Math.abs(Math.atan2(dx, dz)) <= Math.PI / 2 ? 'Front' : 'Back';
    if (label === _rotateViewLabel) return;
    _rotateViewLabel = label;
    _q('.fd-rotate-view span').forEach(s => { s.textContent = label; });
  }

  function zoomToTab(tab) {
    if (!camera || !controls || !bottleGroup) return;
    // Mobile: while no tab sheet is open, always centre the whole model
    // regardless of the active tab. Desktop keeps per-tab focus (its panel
    // is always visible, so the model position never needs re-adjusting).
    if (window.matchMedia('(max-width: 1023px)').matches && !ui.mobilePopover.open) {
      zoomToWholeModel();
      return;
    }
    const target = tabZoomTarget(tab);
    if (!target) return;
    let camDir;
    if (tab === 'art') {
      const curSph = _currentCamSpherical();
      if (printState.mode === 'none') {
        // No Engrave / Colour Print chosen yet → show the BACK of the bottle (θ π).
        // Once a mode is picked, set-mode rotates to that mode's face instead.
        _artFirstEntry = false;
        camDir = new THREE.Vector3().setFromSpherical(
          new THREE.Spherical(1, curSph ? curSph.phi : Math.PI / 2, Math.PI)
        );
      } else {
        // First entry → first configured position. Else: front-priority-by-content + 120° rule.
        const face = _artFirstEntry ? _firstPositionKey() : artFrontPriorityFace();
        _artFirstEntry = false;
        const safeAz = getSafeAreaAzimuth(printState.mode, face);
        if (!curSph) {
          const theta = safeAz != null ? safeAz : 0;
          camDir = new THREE.Vector3().setFromSpherical(
            new THREE.Spherical(1, Math.PI / 2, theta)
          );
        } else {
          let theta = curSph.theta;
          if (safeAz != null && !isSafeAreaInView(printState.mode, face)) {
            theta += _shortestArcDelta(curSph.theta, safeAz);
          }
          camDir = new THREE.Vector3().setFromSpherical(
            new THREE.Spherical(1, curSph.phi, theta)
          );
        }
      }
    } else {
      // Other tabs preserve the camera's current viewing direction so the
      // user keeps facing the angle they had — only the pivot + distance move.
      camDir = new THREE.Vector3().subVectors(camera.position, controls.target);
      if (camDir.lengthSq() < 0.0001) camDir.set(0, 0, 1);
      camDir.normalize();
    }
    const newCamPos = target.center.clone().addScaledVector(camDir, target.dist);
    _setZoomTween(newCamPos, target.center);
  }

  // Fullscreen preview always frames the entire model, keeping the current
  // viewing direction; per-tab focus is restored when it closes.
  // distScale > 1 backs the camera off beyond the tight fit (smaller bottle);
  // the success screen uses it, the review modal keeps the default framing.
  function zoomToWholeModel(distScale = 1) {
    if (!camera || !controls || !bottleGroup) return;
    const target = tabZoomTarget('bottle');
    if (!target) return;
    const camDir = new THREE.Vector3().subVectors(camera.position, controls.target);
    if (camDir.lengthSq() < 0.0001) camDir.set(0, 0, 1);
    camDir.normalize();
    _setZoomTween(target.center.clone().addScaledVector(camDir, target.dist * distScale), target.center);
  }

  let _fullscreenOpen = false;
  function resizeRenderer() {
    if (!renderer) return;
    const host = document.getElementById('fd-canvas-container');
    if (!host) return;
    const w = host.clientWidth, h = host.clientHeight;
    if (w === 0 || h === 0) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    applyMobileSheetViewport();
  }

  // Mobile sheet open: render into rectangle ABOVE the sheet via WebGL
  // viewport+scissor (keeps canvas full-height; sheet hides the lower part).
  function applyMobileSheetViewport() {
    if (!renderer || !camera) return;
    const container = document.getElementById('fd-canvas-container');
    if (!container) return;
    const w = container.clientWidth, h = container.clientHeight;
    if (w === 0 || h === 0) return;
    let overlapPx = 0;
    const sheet = !_fullscreenOpen && ui?.mobilePopover?.open
      ? document.querySelector(`#shopify-section-${SID} .fd-popover.is-open .fd-popover__sheet`)
      : null;
    if (sheet) {
      const cRect = container.getBoundingClientRect();
      const sRect = sheet.getBoundingClientRect();
      overlapPx = Math.max(0, Math.min(cRect.bottom, sRect.bottom) - Math.max(cRect.top, sRect.top));
      overlapPx = Math.min(overlapPx, h);
    }
    const visibleH = Math.max(h - overlapPx, h * 0.2);
    renderer.setViewport(0, overlapPx, w, visibleH);
    renderer.setScissor(0, overlapPx, w, visibleH);
    renderer.setScissorTest(overlapPx > 0);
    camera.aspect = w / visibleH;
    camera.updateProjectionMatrix();
  }

  // FLIP-style geometry tween for the 3D container; renderer follows the
  // CSS transition via rAF so the bottle stays crisp the whole way through.
  const _FS_DURATION = 360;
  let _fsResizeRaf = null;
  function _drivenResize(durationMs) {
    if (_fsResizeRaf) cancelAnimationFrame(_fsResizeRaf);
    const start = performance.now();
    const loop = () => {
      resizeRenderer();
      if (performance.now() - start < durationMs) {
        _fsResizeRaf = requestAnimationFrame(loop);
      } else { _fsResizeRaf = null; }
    };
    _fsResizeRaf = requestAnimationFrame(loop);
  }
  function _setFsRect(c, r) {
    c.style.top    = `${r.top}px`;
    c.style.left   = `${r.left}px`;
    c.style.width  = `${r.width}px`;
    c.style.height = `${r.height}px`;
  }
  function _clearFsStyles(c) {
    c.classList.remove('is-fullscreen');
    c.style.transition = c.style.top = c.style.left = c.style.width = c.style.height = '';
  }
  function openFullscreen() {
    if (_successOpen) return; // covered UI is still keyboard-reachable
    if (_fullscreenOpen || !renderer) return;
    _fullscreenOpen = true;
    const c = document.getElementById('fd-canvas-container');
    const r = c.getBoundingClientRect();
    c.classList.add('is-fullscreen');
    c.style.transition = 'none';
    _setFsRect(c, r);
    void c.offsetWidth; // force layout commit
    c.style.transition = '';
    _setFsRect(c, { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight });
    const overlay = document.getElementById('fd-fullscreen');
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    _drivenResize(_FS_DURATION);
    zoomToWholeModel();
  }
  function closeFullscreen() {
    if (!_fullscreenOpen || !renderer) return;
    _fullscreenOpen = false;
    const c = document.getElementById('fd-canvas-container');
    // Measure the natural rect by briefly stripping fullscreen styles.
    c.style.transition = 'none';
    _clearFsStyles(c);
    const target = c.getBoundingClientRect();
    // Re-pin to viewport-fill so the transition tween has a starting state.
    c.classList.add('is-fullscreen');
    _setFsRect(c, { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight });
    void c.offsetWidth;
    c.style.transition = '';
    _setFsRect(c, target);
    const overlay = document.getElementById('fd-fullscreen');
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    setTimeout(() => _clearFsStyles(c), _FS_DURATION + 20);
    _drivenResize(_FS_DURATION);
    if (ui?.activeTab) zoomToTab(ui.activeTab);
  }

  // Money formatting (Shopify money_format placeholders)
  function formatMoney(cents) {
    if (cents == null) return '';
    const amount = (cents / 100);
    const fmt = config.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_with_space_separator|amount_no_decimals_with_comma_separator|amount_no_decimals_with_space_separator)\s*\}\}/g, (_, key) => {
      const dec = amount.toFixed(2);
      const noDec = String(Math.round(amount));
      switch (key) {
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
        case 'amount_no_decimals_with_space_separator':  return noDec;
        case 'amount_with_comma_separator':              return dec.replace('.', ',');
        case 'amount_with_space_separator':              return dec.replace('.', ' ');
        default:                                         return dec;
      }
    });
  }

  // Selected SKU resolver (used by price + add-to-cart)
  // Returns array of { id, quantity, label } for the user's current selections.
  function selectedLineItems() {
    const qty = Math.max(1, state.quantity | 0);
    const out = [];
    const bottleOk = bottleHasValidVariant();
    if (bottleOk) out.push({ id: state.bottleVariantId, quantity: qty, label: 'bottle' });
    if (accHasValidVariant('cap')) out.push({ id: state.accessories.cap.variantId, quantity: qty, label: 'cap' });
    if (accHasValidVariant('boot')) out.push({ id: state.accessories.boot.variantId, quantity: qty, label: 'boot' });
    // Art (engrave/print) — the personalisation service is ALWAYS tied to the
    // bottle. Never emit it without a valid bottle line, or it ships as an
    // orphaned, billed service with nothing to engrave. (A standalone bottle
    // WITHOUT personalisation is valid; a standalone service is never valid.)
    if (bottleOk && (printState.mode === 'engraving' || printState.mode === 'uv_print')) {
      const ps  = artCurrentPs();
      const cfg = artCurrentCfg();
      const v = _svcVariant(cfg, ps);
      if (v) out.push({ id: v.id, quantity: qty, label: 'art' });
    }
    return out;
  }

  // The ONE service variant for the selected element combination ("text",
  // "text+icon", "icon+badge", …) — null when none chosen or that combo isn't set up.
  function _svcVariant(cfg, ps) {
    if (!cfg || !ps) return null;
    const key = [(ps.text || '').trim() && 'text', ps.icon && 'icon', ps.badge && 'badge'].filter(Boolean).join('+');
    const v = key ? cfg.variants?.[key] : null;
    return v?.id && String(v.id) !== '0' ? v : null;
  }

  // Per-variant price lookup (cents)
  function priceForLine(line) {
    if (line.label === 'bottle') {
      return config.bottles[state.bottleProductId]?.variants[line.id]?.price ?? 0;
    }
    if (line.label === 'cap' || line.label === 'boot') {
      const acc = currentAccData()[line.label];
      const group = acc?.products[state.accessories[line.label]?.productId];
      return group?.variants[line.id]?.price ?? 0;
    }
    if (line.label === 'art') return _svcVariant(artCurrentCfg(), artCurrentPs())?.price ?? 0;
    return 0;
  }

  function computeTotalCents() {
    return selectedLineItems().reduce((sum, line) => sum + priceForLine(line) * line.quantity, 0);
  }

  // Visual-only discount (Shopify enforces the real price server-side). Returns
  // the amount to subtract from the subtotal, or 0 when it doesn't apply:
  // disabled, the configured amount empty/zero, or any REQUIRED item missing.
  // The require_* flags are AND: every flagged item must be in the cart (e.g.
  // require cap + boot → both must be selected; cap alone is not enough).
  function discountCents(subtotal) {
    const d = config.discount;
    if (!d || !d.enabled || subtotal <= 0) return 0;
    if (d.requireBottle && !bottleHasValidVariant()) return 0;
    if (d.requireCap && !accHasValidVariant('cap')) return 0;
    if (d.requireBoot && !accHasValidVariant('boot')) return 0;
    if (d.type === 'fixed') {
      const amt = Number(d.fixed) || 0;
      if (amt <= 0) return 0;
      return Math.min(Math.round(amt * 100), subtotal);
    }
    if (d.type === 'percentage') {
      const pct = Number(d.percentage) || 0;
      if (pct <= 0) return 0;
      return Math.round(subtotal * pct / 100);
    }
    return 0;
  }

  // Dynamic discount line for the order list, e.g. "Cap + Boot Bundle price
  // @RM40.00 only" — the required item(s) and their combined price AFTER the
  // discount (their sum minus the discount). One required item drops "Bundle".
  // Returns '' when no specific item is required (a plain order-wide discount)
  // or when there's no discount, so the caller can skip the whole block.
  function discountBundleLine(disc) {
    const d = config.discount;
    if (!d || disc <= 0) return '';
    const types = [];
    if (d.requireBottle) types.push('bottle');
    if (d.requireCap)    types.push('cap');
    if (d.requireBoot)   types.push('boot');
    if (types.length === 0) return '';
    const labelOf = (t) => t === 'bottle' ? 'Bottle' : (currentAccData()[t]?.label || tabConfig(t).pillLabel);
    const priceOf = (t) => priceForLine({
      label: t,
      id: t === 'bottle' ? state.bottleVariantId : state.accessories[t]?.variantId,
    });
    const finalPrice = Math.max(0, types.reduce((s, t) => s + priceOf(t), 0) - disc);
    const names = types.map(labelOf).map(escapeText);
    const priced = `@${formatMoney(finalPrice)} only`;
    return names.length === 1
      ? `${names[0]} price ${priced}`
      : `${names.join(' + ')} Bundle price ${priced}`;
  }

  // The price shown everywhere (subtotal minus any visual discount). Used by
  // both the pane CTA and the purchase bar so they never disagree.
  function displayTotalCents() {
    const subtotal = computeTotalCents();
    return subtotal - discountCents(subtotal);
  }

  // Render the ADD TO CART total
  function refreshPurchaseBar() {
    const price = formatMoney(displayTotalCents());
    _q('.fd-atc-price').forEach(el => el.textContent = price);
    const disabled = !bottleHasValidVariant();
    _q('[data-action="add-to-cart"]').forEach(b => b.disabled = disabled);
  }

  // Add to cart: when personalization is present, POST design image(s)
  // first, get flexiDesignId, then POST /cart/add.js with line-item
  // properties. Bottle gets the ID attr; personalization SKU gets ID
  // + per-element attrs; cap/boot untouched. Failure blocks the add.
  const _PERSONALIZATION_PROCESSING_URL = '/apps/montigo-hub/personalization-processing';

  // Export rewrites, shared by download + API upload so both get identical
  // bytes. SVG: physical mm size, white background rect, 90° CW rotation.
  // PNG: pHYs DPI chunk only — no flip, so the file matches the API upload and
  // the 3D-preview texture exactly. Stored canvases stay untouched.
  function _exportSvgBlob(stored) {
    let svg = stored.svg;
    if (stored.widthMm && stored.heightMm) {
      svg = svg.replace(
        /^<svg([^>]*?)\swidth="[^"]*"\sheight="[^"]*"/,
        `<svg$1 width="${stored.widthMm}mm" height="${stored.heightMm}mm"`
      );
    }
    svg = svg.replace(/(<rect [^>]*?)fill="none"/, '$1fill="#ffffff"');
    svg = rotateSvg90(svg, stored.widthMm, stored.heightMm);
    return new Blob([svg], { type: 'image/svg+xml' });
  }
  function _exportPngBlob(stored) {
    return injectPngDpi(stored.canvas, stored.dpi || 300);
  }

  // Build the file(s) to send for processing STRICTLY from the current
  // selection — never from _lastCanvases (which can still hold renders from a
  // face/mode the user previously visited). Each file is regenerated fresh here
  // from the live state, so the cache can never leak a stale or wrong-mode file:
  //   • engraving → one egv-<face>.svg per face the CURRENT text/icon sits on.
  //     Text alone or icon alone ⇒ exactly one file; two files only when text and
  //     icon are placed on different faces.
  //   • uv_print  → always exactly one uv-print.png (the whole 360° wrap), no
  //     matter how text/icon are placed.
  async function _collectPersonalizationFiles() {
    const out  = [];
    const mode = printState.mode;
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    if (!pers) return out;
    const dpi  = pers.dpi || 300;

    if (mode === 'engraving') {
      const ps      = printState.engraving;
      const hasText = (ps.text || '').trim().length > 0;
      const hasIcon = !!ps.icon;
      if (!hasText && !hasIcon && !ps.badge) return out;
      for (const facePos of (pers.engraving?.positions || [])) {
        const tHere = hasText && ps.textPosition === facePos.key;
        const iHere = hasIcon && ps.iconPosition === facePos.key;
        const sHere = !!ps.badge && ps.badgePosition === facePos.key;
        if (!tHere && !iHere && !sHere) continue;
        const [cw, ch] = getEngravingCanvasSize(facePos.width_mm, facePos.height_mm, dpi);
        // Fail-closed at the file source (not only via the upstream pre-flight): a
        // text face MUST produce glyphs, or this SVG would ship blank.
        if (tHere && !await prepareTextLayout(ps, ch, cw, dpi, pers, ps.textOrientation === 'vertical', 'engraving', facePos))
          throw _pErr('The engraving text could not be rendered (the font failed to load). Please choose another font or try again.');
        if (sHere) await _badgeSvgFor(ps, facePos, pers.engraving);
        const svgStr   = await buildEngravingSVG(facePos, iHere, tHere, ps, cw, ch, dpi, pers);
        const blob     = _exportSvgBlob({ svg: svgStr, widthMm: facePos.width_mm, heightMm: facePos.height_mm });
        out.push({ name: `egv-${facePos.key}.svg`, blob, facing: facePos.key });
      }
    } else if (mode === 'uv_print') {
      const ps      = printState.uvPrint;
      const uvCfg   = pers.color_print;
      const hasText = (ps.text || '').trim().length > 0;
      const hasIcon = !!ps.icon;
      if ((!hasText && !hasIcon) || !uvCfg?.width_mm || !uvCfg?.height_mm) return out;
      const cw = mmToPx(uvCfg.width_mm, dpi);
      const ch = mmToPx(uvCfg.height_mm, dpi);
      const canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext('2d', { alpha: true });
      ctx.clearRect(0, 0, cw, ch);
      let hasAny = false;
      for (const facePos of (uvCfg.positions || [])) {
        const tHere = hasText && ps.textPosition === facePos.key;
        const iHere = hasIcon && ps.iconPosition === facePos.key;
        if (!tHere && !iHere) continue;
        const centerX = Math.round(cw * (facePos.degree ?? 0) / 360);
        if (tHere && !await prepareTextLayout(ps, ch, cw, dpi, pers, true, 'uv_print', facePos))
          throw _pErr('The print text could not be rendered (the font failed to load). Please choose another font or try again.');
        await drawFaceContent(ctx, facePos, iHere, tHere, ps, 'uv_print', centerX, cw, ch, dpi, pers);
        hasAny = true;
      }
      if (hasAny) {
        const pngBlob = await _exportPngBlob({ canvas, dpi });
        out.push({ name: 'uv-print.png', blob: pngBlob, facing: 'full' });
      }
    }
    return out;
  }

  function _facePosLabel(positionKey) {
    if (!positionKey) return '';
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const face = pers && getFaceCfg(pers, printState.mode, positionKey);
    return face?.label || positionKey;
  }

  // Full design snapshot stored as one hidden `_flexidesign` property. Purely
  // additive: it re-runs the same pure, deterministic helpers the renderer uses
  // (font is already cached) so it computes the EXACT rendered geometry without
  // touching the live preview pipeline. mm is the source of truth; px is derived
  // on read via round(mm/25.4*dpi). `box` is in canvas space (pre-export-transform).
  async function _buildFlexidesignSnapshot(preprocess) {
    const mode = printState.mode;
    if (mode !== 'engraving' && mode !== 'uv_print') return null;
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const ps   = artCurrentPs();
    if (!pers || !ps) return null;

    const dpi     = pers.dpi || 300;
    const modeCfg = getModeCfg(pers, mode);
    const pxToMm  = (px) => +(px * 25.4 / dpi).toFixed(3);
    const hasText = (ps.text || '').trim().length > 0;
    const hasIcon = !!ps.icon;

    const bVar = config.bottles[state.bottleProductId]?.variants?.[state.bottleVariantId];
    const product = {
      id: String(state.bottleProductId),
      variantId: String(state.bottleVariantId),
      sku: bVar?.sku ?? null,
    };
    const cfg = artCurrentCfg();
    // Same resolver as selectedLineItems / hasPersonalizationService so the
    // snapshot never records a service SKU for a variant the cart would drop.
    const svc = _svcVariant(cfg, ps);
    const service = {
      id: cfg?.productId != null ? String(cfg.productId) : null,
      variantId: svc ? String(svc.id) : null,
      sku: svc ? (svc.sku ?? null) : null,
    };

    // Text box from the fitted glyph bbox (vertical text swaps on-canvas w/h).
    const buildText = async (faceCfg, cw, ch, centerX, textCY) => {
      const isVertical = mode === 'uv_print' || (mode === 'engraving' && ps.textOrientation === 'vertical');
      const layout = await prepareTextLayout(ps, ch, cw, dpi, pers, isVertical, mode, faceCfg);
      if (!layout) return null;
      const gw = layout.glyphRight - layout.glyphLeft;
      const gh = layout.glyphBottom - layout.glyphTop;
      const w  = isVertical ? gh : gw;
      const h  = isVertical ? gw : gh;
      const t = {
        value: ps.text.trim(),
        fontFileUrl: ps.font?.file ?? null,
        fontSizePt: +(layout.fontSize * 72 / dpi).toFixed(2),
        box: { xMm: pxToMm(centerX - w / 2), yMm: pxToMm(textCY - h / 2),
               widthMm: pxToMm(w), heightMm: pxToMm(h) },
      };
      if (mode === 'engraving') t.orientation = ps.textOrientation === 'vertical' ? 'vertical' : 'horizontal';
      else if (ps.fontColor?.color) t.colorHex = ps.fontColor.color;
      return t;
    };
    // Icon box = the centred container rect (max_width_mm × icon_height_mm) the
    // icon is fitted into (xMidYMid meet) on both paths — no image load needed.
    const buildIcon = (faceCfg, centerX, iconCY) => {
      const iconWpx = mmToPx(getIconWmm(faceCfg), dpi);
      const iconHpx = mmToPx(getIconHmm(modeCfg), dpi);
      return {
        url: ps.icon?.previewIcon ?? null,
        box: { xMm: pxToMm(centerX - iconWpx / 2), yMm: pxToMm(iconCY - iconHpx / 2),
               widthMm: pxToMm(iconWpx), heightMm: pxToMm(iconHpx) },
      };
    };

    const buildBadge = async (facePos, cx) => {
      const b = _badgeBoxPx(ps, facePos, modeCfg, dpi);
      const r = await _badgeSvgFor(ps, facePos, modeCfg);
      return {
        design: ps.badge.name,
        url: ps.badge.svg,
        text: (ps.badgeText || '').trim(),
        fontFileUrl: ps.badge.fontFile || null,
        // Adobe kit badges: the file holds live text in this font (must be installed at production).
        fontName: r.kitText ? _kitDisplayName(r.kitText.family) : null,
        fontScript: _kitHref(ps.badge),
        fontSizePt: r.fontPt,
        box: { xMm: pxToMm(cx - b.w / 2), yMm: pxToMm(b.cy - b.h / 2), widthMm: pxToMm(b.w), heightMm: pxToMm(b.h) },
      };
    };

    const templates = [];
    if (mode === 'engraving') {
      for (const facePos of (modeCfg?.positions || [])) {
        const tHere = hasText && ps.textPosition === facePos.key;
        const iHere = hasIcon && ps.iconPosition === facePos.key;
        const sHere = !!ps.badge && ps.badgePosition === facePos.key;
        if (!tHere && !iHere && !sHere) continue;
        const [cw, ch] = getEngravingCanvasSize(facePos.width_mm, facePos.height_mm, dpi);
        const cx = cw / 2;
        const { iconCY, textCY } = resolveContentPositions(facePos, iHere, tHere, ps, ch, dpi, pers, 'engraving');
        const tplText = tHere ? await buildText(facePos, cw, ch, cx, textCY) : null;
        const tplIcon = iHere ? buildIcon(facePos, cx, iconCY) : null;
        const tplBadge = sHere ? await buildBadge(facePos, cx) : null;
        // Fail-closed: a face the text/icon is placed on MUST carry content. A null
        // here means the font failed to load (or the icon is unresolved) — the file
        // would be blank, so block add-to-cart rather than ship an empty engraving.
        if (tHere && !tplText) throw _pErr('The engraving text could not be rendered (the font failed to load). Please choose another font or try again.');
        if (iHere && !tplIcon) throw _pErr('The engraving icon could not be prepared. Please re-select the icon or try again.');
        const tpl = {
          type: `egv-${facePos.key}`,
          facing: facePos.label || facePos.key || null,
          file_type: 'svg',
          size: { widthMm: facePos.width_mm, heightMm: facePos.height_mm },
          export: { rotationDeg: 90, flipHorizontal: false },
          text: tplText,
          icon: tplIcon,
          ...(tplBadge && { badge: tplBadge }),
        };
        _assertTemplateComplete(tpl, 'engraving');
        templates.push(tpl);
      }
    } else if (modeCfg?.width_mm && modeCfg?.height_mm) {
      const cw = mmToPx(modeCfg.width_mm, dpi);
      const ch = mmToPx(modeCfg.height_mm, dpi);
      let text = null, icon = null;
      if (hasText) {
        const f = getFaceCfg(pers, 'uv_print', ps.textPosition);
        if (f) {
          const centerX = Math.round(cw * (f.degree ?? 0) / 360);
          const { textCY } = resolveContentPositions(f, false, true, ps, ch, dpi, pers, 'uv_print');
          text = await buildText(f, cw, ch, centerX, textCY);
          if (text) text.facing = f.label || f.key || null;
        }
      }
      if (hasIcon) {
        const f = getFaceCfg(pers, 'uv_print', ps.iconPosition);
        if (f) {
          const centerX = Math.round(cw * (f.degree ?? 0) / 360);
          const { iconCY } = resolveContentPositions(f, true, false, ps, ch, dpi, pers, 'uv_print');
          icon = buildIcon(f, centerX, iconCY);
          if (icon) icon.facing = f.label || f.key || null;
        }
      }
      // Fail-closed: whatever was chosen MUST have rendered, else the PNG is blank.
      if (hasText && !text) throw _pErr('The print text could not be rendered (the font failed to load). Please choose another font or try again.');
      if (hasIcon && !icon) throw _pErr('The print icon could not be prepared. Please re-select the icon or try again.');
      const tpl = {
        type: 'uv-print',
        facing: 'full',
        file_type: 'png',
        size: { widthMm: modeCfg.width_mm, heightMm: modeCfg.height_mm },
        // The file uploaded to the backend is the canvas + pHYs DPI, no flip —
        // identical to the download and the 3D-preview texture.
        export: { rotationDeg: 0, flipHorizontal: false },
        text, icon,
      };
      _assertTemplateComplete(tpl, 'uv_print');
      templates.push(tpl);
    }
    if (!templates.length) return null;

    return {
      mode,
      dpi,
      product,
      service,
      templates,
      flexiDesignId: preprocess?.id != null ? String(preprocess.id) : null,
    };
  }

  // Only the *ID stays visible; everything else gets `_` so Shopify hides it.
  function _buildPersonalizationProperties(preprocess, flexiJson) {
    const ps = artCurrentPs();
    if (!ps) return {};
    const isEgv  = printState.mode === 'engraving';
    const prefix = isEgv ? 'Engraving' : 'ColorPrint';
    const props  = { [`${prefix}ID`]: preprocess.id };
    // Visible (no leading "_"): prefixed with Engraving/ColorPrint.
    const add = (k, v) => { if (v != null && v !== '') props[`${prefix}${k}`] = v; };
    // Hidden (leading "_"): NO Engraving/ColorPrint prefix. Used for the
    // file payload that fulfilment reads but shouldn't show on the order.
    const addHidden = (k, v) => {
      if (v == null || v === '') return;
      props[`_${k}`] = typeof v === 'string' ? v : JSON.stringify(v);
    };
    if ((ps.text || '').trim()) {
      add('FontFamily',   ps.font?.name);
      add('Text',         ps.text.trim());
      add('TextPosition', _facePosLabel(ps.textPosition));
      add('TextSize',     sizeLabel('text'));
      if (isEgv) add('TextOrientation', ps.textOrientation === 'vertical' ? 'Vertical' : 'Horizontal');
      else       add('TextColor',       ps.fontColor?.label);
    }
    if (ps.icon) {
      add('IconSeries',   ps.iconSeries?.name);
      add('Icon',         ps.icon?.name);
      add('IconPosition', _facePosLabel(ps.iconPosition));
      add('IconSize',     sizeLabel('icon'));
    }
    if (isEgv && ps.badge) {
      add('SpecialTextDesign',   ps.badge.name);
      add('SpecialText',         (ps.badgeText || '').trim());
      add('SpecialTextPosition', _facePosLabel(ps.badgePosition));
    }
    addHidden('RawFiles',       preprocess.rawFiles);
    addHidden('PrintableFiles', preprocess.printableFiles);
    // ALWAYS stamp the service marker (never conditional): a service line must be
    // classifiable as a service — by bl_cart-bundle-role.liquid AND the orphan
    // sweep — even if the snapshot is somehow null, so it can never be mistaken
    // for a bottle and slip past the orphan cleanup.
    props._flexidesign = flexiJson ? JSON.stringify(flexiJson) : '1';
    return props;
  }

  function _cartHasPersonalization() {
    if (printState.mode !== 'engraving' && printState.mode !== 'uv_print') return false;
    const ps = artCurrentPs();
    return !!ps && ((ps.text || '').trim().length > 0 || !!ps.icon || !!ps.badge);
  }

  // True when a personalisation SERVICE variant (text/icon/set) is actually in
  // the order — mirrors the art line in selectedLineItems. Gates the T&C row's
  // visibility and makes the agreement compulsory before add-to-cart.
  function hasPersonalizationService() {
    return !!_svcVariant(artCurrentCfg(), artCurrentPs());
  }

  // Reset / clear the T&C agreement error UI (footer message + row highlight).
  function _clearTcError() {
    _clearAtcError();
    document.getElementById('fd-rev-tc')?.classList.remove('is-error');
  }

  // A thrown Error flagged userFacing carries a message safe to show the shopper.
  function _pErr(msg) { return Object.assign(new Error(msg), { userFacing: true }); }

  // Every template must be a COMPLETE, self-generating set: the backend redraws the
  // file from the template ALONE, so each present text/icon must carry the exact
  // machine details needed to regenerate it — the font FILE url (not a human name),
  // the text value, size, orientation and the x/y/size box; for an icon, the source
  // url and its x/y/size box. A missing field here is the previous bug class (a
  // template that looks present but can't be re-rendered) — fail closed.
  function _assertTemplateComplete(tpl, mode) {
    const num   = (n) => typeof n === 'number' && isFinite(n);
    const boxOk = (b) => b && num(b.xMm) && num(b.yMm) && num(b.widthMm) && b.widthMm > 0 && num(b.heightMm) && b.heightMm > 0;
    const where = tpl.type || 'design';
    if (!tpl.text && !tpl.icon && !tpl.badge) throw _pErr(`The ${where} template has neither text nor icon.`);
    if (tpl.badge) {
      const s = tpl.badge;
      if (!s.url || !s.text || !(s.fontFileUrl || s.fontName) || !num(s.fontSizePt) || !boxOk(s.box))
        throw _pErr(`The ${where} Special Text design is incomplete — please re-select it.`);
    }
    if (tpl.text) {
      const t = tpl.text;
      if (!t.value || !String(t.value).trim())     throw _pErr(`The ${where} text value is missing.`);
      if (!t.fontFileUrl)                          throw _pErr(`The ${where} text is missing its font file — please re-select the font.`);
      if (!num(t.fontSizePt) || t.fontSizePt <= 0) throw _pErr(`The ${where} text size is invalid.`);
      if (!boxOk(t.box))                           throw _pErr(`The ${where} text position is incomplete.`);
      if (mode === 'engraving' && !t.orientation)  throw _pErr(`The ${where} text orientation is missing.`);
    }
    if (tpl.icon) {
      const i = tpl.icon;
      if (!i.url)        throw _pErr(`The ${where} icon image is missing — please re-select the icon.`);
      if (!boxOk(i.box)) throw _pErr(`The ${where} icon position is incomplete.`);
    }
  }

  // FAIL-CLOSED pre-flight — runs before ANY personalization file is generated or
  // uploaded. Whatever the customer actually chose (text and/or icon — EITHER ALONE
  // is valid) MUST be renderable, or we throw and block add-to-cart. This is
  // deliberate: a font that fails to load (or a missing font file / unloadable icon)
  // would otherwise silently ship a BLANK design while the order still records the
  // text/icon and charges for it. Never allow a paid order with an empty file.
  async function _assertContentRenders() {
    const mode = printState.mode;
    if (mode !== 'engraving' && mode !== 'uv_print') return;
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const ps   = artCurrentPs();
    if (!pers || !ps) throw _pErr('Personalization is not ready yet. Please try again.');
    // Variant-level exclusion: never sell a personalization the selected
    // variant has disabled (custom.allow_engraving / allow_uv_print = false).
    if (!artAvailableModes().includes(mode))
      throw _pErr('This personalisation is not available for the selected bottle. Please review your design.');
    const hasText = (ps.text || '').trim().length > 0;
    const hasIcon = !!ps.icon;
    if (!hasText && !hasIcon && !ps.badge) throw _pErr('Add engraving text or an icon before adding to cart.');
    if (!_repairLayout()) throw _pErr('Your design doesn’t fit the print area without overlapping. Please move or remove an element, then try again.');
    if (hasText && hasIcon && ps.badge) throw _pErr('Special Text can be combined with either Text or Icon, not both. Please remove one.');
    if (!hasPersonalizationService()) throw _pErr('This combination isn’t available right now. Please remove one of your selections and try again.');
    const dpi = pers.dpi || 300;

    if (ps.badge) {
      if (!(ps.badgeText || '').trim()) throw _pErr('Please enter the text for your Special Text design.');
      const face = (getModeCfg(pers, mode)?.positions || []).find(f => f.key === ps.badgePosition);
      if (!canFitBadgeOnFace(face, getModeCfg(pers, mode)))
        throw _pErr('The Special Text position is invalid. Please re-select its position.');
      let r = null;
      try { r = await _badgeSvgFor(ps, face, getModeCfg(pers, mode)); } catch (e) { console.warn('[FlexiDesign]', e); }
      if (!r?.fontPt) throw _pErr('Your Special Text design could not be prepared. Please try again.');
    }

    if (hasText) {
      if (!ps.font?.file) throw _pErr('The selected font is unavailable. Please choose another font.');
      try { await loadOpentypeFont(ps.font.file); }
      catch { throw _pErr('The selected font failed to load, so the text can’t be engraved. Please choose another font or try again.'); }
      // Confirm real glyphs actually lay out — not merely that the font parsed.
      const isVertical = mode === 'uv_print' || (mode === 'engraving' && ps.textOrientation === 'vertical');
      let faceCfg, cw, ch;
      if (mode === 'engraving') {
        faceCfg = (getModeCfg(pers, mode)?.positions || []).find(f => f.key === ps.textPosition);
        if (faceCfg) [cw, ch] = getEngravingCanvasSize(faceCfg.width_mm, faceCfg.height_mm, dpi);
      } else {
        const mc = getModeCfg(pers, mode);
        faceCfg = getFaceCfg(pers, 'uv_print', ps.textPosition);
        if (mc) { cw = mmToPx(mc.width_mm, dpi); ch = mmToPx(mc.height_mm, dpi); }
      }
      if (!faceCfg || !cw || !ch) throw _pErr('The selected text position is invalid. Please re-select the text position.');
      const layout = await prepareTextLayout(ps, ch, cw, dpi, pers, isVertical, mode, faceCfg);
      if (!layout) throw _pErr('The engraving text could not be rendered. Please check the text and font, then try again.');
    }

    if (hasIcon) {
      const src = ps.icon?.previewIcon;
      if (!src) throw _pErr('The selected icon is unavailable. Please choose another icon.');
      // The icon must sit on a real face of the CURRENT product — otherwise it is
      // silently dropped from the file/snapshot while the order still charges for it.
      const iconFace = mode === 'engraving'
        ? (getModeCfg(pers, mode)?.positions || []).find(f => f.key === ps.iconPosition)
        : getFaceCfg(pers, 'uv_print', ps.iconPosition);
      if (!iconFace) throw _pErr('The selected icon position is invalid. Please re-select the icon.');
      // Validate the icon the SAME way the file builder for this mode does, so we
      // never false-block a design that would actually render (and never hang):
      //   uv_print  → decoded as an <img> bitmap (drawFaceContent) — intrinsic size matters
      //   engraving → fetched + inlined (buildEngravingSVG) — only the bytes must be reachable
      let ok;
      if (mode === 'uv_print') {
        ok = await new Promise(res => {
          const im = new Image(); im.crossOrigin = 'anonymous';
          const t = setTimeout(() => res(false), 8000);
          im.onload  = () => { clearTimeout(t); res(im.naturalWidth > 0); };
          im.onerror = () => { clearTimeout(t); res(false); };
          im.src = src;
        });
      } else {
        ok = await Promise.race([
          fetch(src).then(r => r.ok).catch(() => false),
          new Promise(res => setTimeout(() => res(false), 8000)),
        ]);
      }
      if (!ok) throw _pErr('The selected icon could not be loaded. Please choose another icon or try again.');
    }
  }

  async function _runPersonalizationPreprocessing() {
    await _assertContentRenders();
    const files = await _collectPersonalizationFiles();
    if (files.length === 0) throw _pErr('There is nothing to personalize. Add text or an icon first.');
    const fd = new FormData();
    // Each file's facing is sent as an index-aligned `meta` JSON array
    // (FormData can't nest a Blob inside JSON, so file + facing travel in parallel).
    const meta = [];
    files.forEach(f => {
      fd.append('files', f.blob, f.name);
      meta.push({ facing: f.facing });
    });
    fd.append('meta', JSON.stringify(meta));
    const resp = await fetch(_PERSONALIZATION_PROCESSING_URL, { method: 'POST', body: fd });
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Preprocessing HTTP ${resp.status}${body ? ': ' + body : ''}`);
    }
    const data = await resp.json().catch(() => null);
    const root = data?.data ?? data ?? {};
    const id = root.flexiDesignId ?? root.flexi_design_id ?? root.id ?? null;
    if (!id) throw new Error('Preprocessing response missing flexiDesignId');
    return {
      id:             String(id),
      rawFiles:       root.rawFiles       ?? root.raw_files       ?? null,
      printableFiles: root.printableFiles ?? root.printable_files ?? null,
    };
  }

  // Add-to-cart button now lives in the review modal.
  const _ATC_BTN_SEL = `#shopify-section-${SID} [data-action="review-add-to-cart"]`;
  let _atcErrorTimer = null;
  // Single error line in the review footer, ABOVE the Add To Cart button (so it
  // never sits beside the button). `sticky` keeps it until cleared (T&C); the
  // transient API errors auto-clear after 6s.
  function _showAtcError(msg, sticky) {
    const el = document.getElementById('fd-review-error');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(_atcErrorTimer);
    if (!sticky) _atcErrorTimer = setTimeout(_clearAtcError, 6000);
  }
  function _clearAtcError() {
    clearTimeout(_atcErrorTimer);
    const el = document.getElementById('fd-review-error');
    if (el) { el.hidden = true; el.textContent = ''; }
  }

  // state: 'idle' | 'processing' | 'adding'. Stashes the label's
  // original text on the first non-idle transition so it can restore.
  function _setAtcState(state) {
    const transient = { processing: 'PROCESSING…', adding: 'ADDING TO CART…' };
    document.querySelectorAll(_ATC_BTN_SEL).forEach(btn => {
      btn.disabled = state !== 'idle';
      const labelEl = btn.querySelector('.fd-atc-label');
      if (!labelEl) return;
      if (state === 'idle') {
        if (labelEl.dataset.atcOriginalLabel != null) {
          labelEl.textContent = labelEl.dataset.atcOriginalLabel;
          delete labelEl.dataset.atcOriginalLabel;
        }
      } else {
        if (labelEl.dataset.atcOriginalLabel == null) {
          labelEl.dataset.atcOriginalLabel = labelEl.textContent;
        }
        labelEl.textContent = transient[state] || transient.processing;
      }
    });
  }

  let _atcInflight = false;
  async function addToCart() {
    if (_atcInflight) return;
    // When the personalisation service is in the order, the T&C agreement is
    // compulsory: block, reveal the message, and scroll it into view.
    if (hasPersonalizationService()) {
      const cb = document.getElementById('fd-tc-check');
      if (cb && !cb.checked) {
        _showAtcError('Please agree to the personalisation policy to continue.', true);
        document.getElementById('fd-rev-tc')?.classList.add('is-error');
        (document.getElementById('fd-rev-tc') || cb).scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    const items = selectedLineItems();
    if (items.length === 0) return;
    // Hard invariant (belt-and-suspenders on the selectedLineItems gate): the
    // personalisation service can never ship without its bottle line.
    if (items.some(it => it.label === 'art') && !items.some(it => it.label === 'bottle')) {
      _showAtcError('This bottle is unavailable, so the personalisation can’t be added. Please choose an available bottle.');
      return;
    }
    _atcInflight = true;
    _clearAtcError();
    try {
      let preprocess = null;
      if (_cartHasPersonalization()) {
        _setAtcState('processing');
        try { preprocess = await _runPersonalizationPreprocessing(); }
        catch (e) {
          console.error('[FlexiDesign] preprocessing failed:', e);
          _showAtcError(e?.userFacing ? e.message : 'Could not save your design. Please try again.');
          return;
        }
      }
      const idKey = printState.mode === 'engraving' ? 'EngravingID'
                  : printState.mode === 'uv_print'  ? 'ColorPrintID' : null;
      const flexiSnapshot = preprocess ? await _buildFlexidesignSnapshot(preprocess) : null;
      const cartItems = items.map(it => {
        const out = { id: it.id, quantity: it.quantity };
        if (preprocess && idKey) {
          if (it.label === 'bottle') out.properties = { [idKey]: preprocess.id };
          else if (it.label === 'art') out.properties = _buildPersonalizationProperties(preprocess, flexiSnapshot);
        }
        return out;
      });
      _setAtcState('adding');
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      });
      if (!resp.ok) {
        console.error('[FlexiDesign] add-to-cart failed:', resp.status, await resp.text());
        _showAtcError('Could not add to cart. Please try again.');
        return;
      }
      closeReview();
      // Stay on the page (no /cart redirect): let the theme + header badge
      // re-render via cart:refresh, then show the success takeover with the
      // live 3D bottle and Continue Shopping / View my cart.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      openSuccess();
    } catch (e) {
      console.error('[FlexiDesign] add-to-cart error:', e);
      _showAtcError(e?.userFacing ? e.message : 'Something went wrong. Please try again.');
    } finally {
      _atcInflight = false;
      _setAtcState('idle');
      refreshPurchaseBar();
    }
  }

  // ── Customisation review modal ─────────────────────────────────────────
  // Reuses the live 3D container: it's relocated into the modal stage on open
  // and returned to the editor on close (so no snapshot needed).
  let _reviewHome = null;
  let _reviewPrevBg = null;
  let _reviewOpen = false; // force the print-area guides off while the review is open
  function openReview() {
    // Never open beneath the success takeover (keyboard can still reach the
    // covered ATC button) — it would clobber _reviewHome/_successPrevBg.
    if (_successOpen) return;
    if (!bottleHasValidVariant()) return;
    const modal = document.getElementById('fd-review');
    const stage = document.getElementById('fd-review-stage');
    const c     = document.getElementById('fd-canvas-container');
    if (!modal || !stage || !c) return;
    _dropEmptyBadge();
    document.getElementById('fd-review-rows').innerHTML = buildReviewRows();
    _clearTcError(); // clear any lingering footer error + row highlight on open
    // T&C agreement only applies when the personalisation service is in the
    // order — show it then, reset it (unchecked) on every open.
    const tcRow = document.getElementById('fd-rev-tc');
    if (tcRow) {
      tcRow.hidden = !hasPersonalizationService();
      const cb = document.getElementById('fd-tc-check');
      if (cb) cb.checked = false;
    }
    if (c.parentNode !== stage) { _reviewHome = c.parentNode; stage.appendChild(c); }
    // Make the canvas transparent so the grey preview card shows behind the bottle.
    if (scene) { _reviewPrevBg = scene.background; scene.background = null; }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    _reviewOpen = true;
    updateGuideVisibility(); // hide the print-area guides in the review preview
    zoomToWholeModel(); // centre the whole bottle, like the Preview button
    requestAnimationFrame(() => _drivenResize(_FS_DURATION));
  }
  function closeReview() {
    const modal = document.getElementById('fd-review');
    if (!modal || !modal.classList.contains('is-open')) return;
    const c = document.getElementById('fd-canvas-container');
    if (_reviewHome && c) _reviewHome.appendChild(c);
    if (scene) scene.background = _reviewPrevBg; // restore the editor's backdrop
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    _reviewOpen = false;
    updateGuideVisibility(); // restore the print-area guides to the user's toggle
    if (ui?.activeTab) zoomToTab(ui.activeTab); // restore the editor's focus
    requestAnimationFrame(() => _drivenResize(_FS_DURATION));
    // Reset the order-list scroll to the top so a reopen starts at the top.
    // Deferred past the fade-out (0.3s) so the jump isn't visible.
    const scroll = modal.querySelector('.fd-review__scroll');
    if (scroll) setTimeout(() => { scroll.scrollTop = 0; }, 320);
  }

  // ── Added-to-cart success takeover ───────────────────────────────────────
  // Replaces the old redirect to /cart: the live 3D canvas relocates into the
  // success screen (same pattern as the review modal) so the customer sees
  // their own customised bottle slowly turning. Exits are navigations
  // (Continue Shopping / View my cart); closeSuccess exists for the
  // back/bfcache path so a restored page is never trapped under the overlay.
  let _successHome = null;
  let _successPrevBg = null;
  let _successPrevRotSpeed = null;
  let _successOpen = false;
  // Camera pull-back on the success screen (1 = tight fit; higher = smaller bottle).
  const _SUCCESS_ZOOM_OUT = 1.35;
  function openSuccess() {
    const modal = document.getElementById('fd-success');
    const stage = document.getElementById('fd-success-stage');
    const c     = document.getElementById('fd-canvas-container');
    // Markup missing (edge) → keep the old behavior so the add never dead-ends.
    if (!modal || !stage || !c) { window.location.href = config.cartUrl || '/cart'; return; }
    if (c.parentNode !== stage) { _successHome = c.parentNode; stage.appendChild(c); }
    if (scene) { _successPrevBg = scene.background; scene.background = null; }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    _successOpen = true;
    updateGuideVisibility(); // print-area guides never show on the success screen
    if (controls) {
      _successPrevRotSpeed = controls.autoRotateSpeed;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 1.8;
    }
    // The ATC button was just disabled, which dropped keyboard focus to <body> —
    // land it on the overlay's heading so Tab reaches the CTAs immediately.
    modal.querySelector('.fd-success__heading')?.focus({ preventScroll: true });
    zoomToWholeModel(_SUCCESS_ZOOM_OUT);
    requestAnimationFrame(() => _drivenResize(_FS_DURATION));
    // zoomToWholeModel above computed its distance from the PRE-relocation
    // camera aspect; once the driven resize settles on the success stage's
    // real aspect, re-fit so the bottle is framed for this layout.
    setTimeout(() => { if (_successOpen) zoomToWholeModel(_SUCCESS_ZOOM_OUT); }, _FS_DURATION);
  }
  function closeSuccess() {
    const modal = document.getElementById('fd-success');
    if (!modal || !modal.classList.contains('is-open')) return;
    const c = document.getElementById('fd-canvas-container');
    if (_successHome && c) _successHome.appendChild(c);
    if (scene) scene.background = _successPrevBg;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    _successOpen = false;
    if (controls) {
      controls.autoRotate = false;
      if (_successPrevRotSpeed != null) controls.autoRotateSpeed = _successPrevRotSpeed;
    }
    updateGuideVisibility();
    if (ui?.activeTab) zoomToTab(ui.activeTab);
    requestAnimationFrame(() => _drivenResize(_FS_DURATION));
  }
  // A bfcache restore (browser Back from the cart / continue-shopping page)
  // brings the page back with the overlay still open — dismiss it so the
  // customer lands in the editor, not a screen with no way out.
  window.addEventListener('pageshow', (e) => { if (e.persisted && _successOpen) closeSuccess(); });

  // T&C modal (only present when the agreement text has a [[ ]] trigger and the
  // configured page has content — otherwise these are harmless no-ops).
  function openTc() {
    const m = document.getElementById('fd-tc-modal');
    if (!m) return;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
  }
  function closeTc() {
    const m = document.getElementById('fd-tc-modal');
    if (!m) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
  }

  function _revSpec(k, v) {
    return v ? `<div class="fd-rev-spec"><span class="fd-rev-spec__k">${escapeText(k)}</span><span class="fd-rev-spec__v">${v}</span></div>` : '';
  }
  // Placement = the face label (e.g. Front / Back) from the position metaobject.
  // The vertical band (Top/Middle/Bottom) is intentionally NOT shown — there's no
  // such field in the data; it would only be inferred from the Y slider.
  function _revFace(which) {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const mode = printState.mode;
    const ps   = artCurrentPs();
    if (!ps || !pers) return '';
    const faceKey = ps[_EGV_EL[which][0]];
    const face    = getFaceCfg(pers, mode, faceKey);
    return escapeText(face?.label || faceKey || '');
  }

  function buildReviewRows() {
    const f  = (cents) => formatMoney(cents);
    const b  = config.bottles[state.bottleProductId];
    const ps = artCurrentPs();
    const mode = printState.mode;
    let html = '';
    let lastDetail = false; // track so the price-section divider isn't doubled up

    const bImg = b?.variants?.[state.bottleVariantId]?.image || b?.productTypeImage;
    html += `<div class="fd-rev-prod">
      ${bImg ? `<img class="fd-rev-prod__thumb" src="${escapeAttr(bImg)}" alt="">` : '<span class="fd-rev-prod__thumb"></span>'}
      <div class="fd-rev-prod__body">
        <div class="fd-rev-prod__title">${escapeText(b?.title || '')}</div>
        ${_revSpec('Size:', escapeText(b?.volume || b?.size || ''))}
      </div>
      <div class="fd-rev-prod__price">${f(priceForLine({ label: 'bottle', id: state.bottleVariantId }))}</div>
    </div>`;

    const icons = config.fallbackIcons || {};
    const hasArt = mode === 'engraving' || mode === 'uv_print';
    if (hasArt && ps && (ps.text || '').trim().length > 0) {
      html += `<div class="fd-rev-detail">
        <div class="fd-rev-detail__icon">${icons.textArt || ''}<div class="fd-rev-detail__label">Art – Text</div></div>
        <div class="fd-rev-detail__specs">
          ${_revSpec('Decorative Type:', mode === 'uv_print' ? 'Colour Print' : 'Engraving')}
          ${_revSpec('Text Input:', escapeText(ps.text.trim()))}
          ${_revSpec('Font Type:', escapeText(ps.font?.name || ''))}
          ${_revSpec('Placement:', _revFace('text'))}
          ${_revSpec('Orientation:', mode === 'engraving' ? (ps.textOrientation === 'vertical' ? 'Vertical' : 'Horizontal') : '')}
          ${_revSpec('Size:', escapeText(sizeLabel('text')))}
        </div>
      </div>`;
      lastDetail = true;
    }
    if (hasArt && ps && ps.icon) {
      const art = ps.icon.previewIcon;
      html += `<div class="fd-rev-detail">
        <div class="fd-rev-detail__icon">${icons.iconArt || ''}<div class="fd-rev-detail__label">Art – Icon</div></div>
        <div class="fd-rev-detail__specs">
          ${_revSpec('Decorative Type:', mode === 'uv_print' ? 'Colour Print' : 'Engraving')}
          ${_revSpec('Icon Series:', escapeText(ps.iconSeries?.name || ''))}
          ${_revSpec('Artwork:', `<span class="fd-rev-artwork-wrap">${art ? `<img class="fd-rev-artwork" src="${escapeAttr(art)}" alt="">` : ''}<span class="fd-rev-artwork-name">${escapeText(ps.icon.name || '')}</span></span>`)}
          ${_revSpec('Placement:', _revFace('icon'))}
          ${_revSpec('Size:', escapeText(sizeLabel('icon')))}
        </div>
      </div>`;
      lastDetail = true;
    }
    if (mode === 'engraving' && ps?.badge) {
      html += `<div class="fd-rev-detail">
        <div class="fd-rev-detail__icon">${icons.badgeArt || ''}<div class="fd-rev-detail__label">Art – Special Text</div></div>
        <div class="fd-rev-detail__specs">
          ${_revSpec('Decorative Type:', 'Engraving')}
          ${_revSpec('Design:', `<span class="fd-rev-artwork-wrap"><img class="fd-rev-artwork" src="${escapeAttr(ps.badge.svg)}" alt=""><span class="fd-rev-artwork-name">${escapeText(ps.badge.name)}</span></span>`)}
          ${_revSpec('Text Input:', escapeText((ps.badgeText || '').trim()))}
          ${_revSpec('Placement:', _revFace('badge'))}
        </div>
      </div>`;
      lastDetail = true;
    }

    ['cap', 'boot'].forEach(type => {
      if (!accHasValidVariant(type)) return;
      const acc     = currentAccData()[type];
      const cur     = state.accessories[type];
      const group   = acc?.products[cur.productId];
      const variant = group?.variants[cur.variantId];
      const accImg  = variant?.image || group?.thumbnail;
      html += `<div class="fd-rev-prod">
        ${accImg ? `<img class="fd-rev-prod__thumb" src="${escapeAttr(accImg)}" alt="">` : '<span class="fd-rev-prod__thumb"></span>'}
        <div class="fd-rev-prod__body">
          <div class="fd-rev-prod__title">${escapeText(group?.title || acc?.label || '')}</div>
          ${_revSpec('Color:', escapeText(variant?.title || ''))}
        </div>
        <div class="fd-rev-prod__price">${f(priceForLine({ label: type, id: cur.variantId }))}</div>
      </div>`;
      lastDetail = false;
    });

    // Discount (when there's a real price difference) and grand total each get
    // their own container; the list flex gap separates them by 10px.
    const subtotal = computeTotalCents();
    const disc = discountCents(subtotal);
    const ruled = lastDetail ? '' : ' fd-rev-ruled';
    if (disc > 0) {
      const bundle = discountBundleLine(disc);
      html += `<div class="fd-rev-discount-box${ruled}">`
        + (bundle ? `<div class="fd-rev-bundle">${bundle}</div>` : '')
        + `<div class="fd-rev-total__row fd-rev-discount"><span>Discounted:</span><span>-${f(disc)}</span></div></div>`;
      html += `<div class="fd-rev-total-box"><div class="fd-rev-total__row"><span>Total</span><span>${f(subtotal - disc)}</span></div></div>`;
    } else {
      html += `<div class="fd-rev-total-box${ruled}"><div class="fd-rev-total__row"><span>Total</span><span>${f(subtotal - disc)}</span></div></div>`;
    }
    return html;
  }

  // Size scaling for text/icon. M = base (scale 1, default selection); S/L scale
  // off it. Engraving and color print have independent sets, each with its own
  // text and icon scales. Hardcoded for now — edit the `scale` values to tune.
  const SIZE_OPTIONS = {
    engraving: {
      text: [{ key: 'S', label: 'S', scale: 0.8 }, { key: 'M', label: 'M', scale: 1 }, { key: 'L', label: 'L', scale: 1.2 }],
      icon: [{ key: 'S', label: 'S', scale: 0.8 }, { key: 'M', label: 'M', scale: 0.9 }, { key: 'L', label: 'L', scale: 1 }],
    },
    uv_print: {
      text: [{ key: 'S', label: 'S', scale: 0.8 }, { key: 'M', label: 'M', scale: 1 }, { key: 'L', label: 'L', scale: 1.2 }],
      icon: [{ key: 'S', label: 'S', scale: 0.8 }, { key: 'M', label: 'M', scale: 0.9 }, { key: 'L', label: 'L', scale: 1 }],
    },
  };
  function sizeOptions(mode, which) {
    return (SIZE_OPTIONS[mode] || SIZE_OPTIONS.engraving)[which] || [];
  }
  // Selected size label (e.g. S/M/L) for the current mode; blank when size
  // isn't selectable (fewer than two options) or no art is present.
  function sizeLabel(which) {
    const ps   = artCurrentPs();
    const opts = sizeOptions(printState.mode, which);
    if (!ps || opts.length < 2) return '';
    const key  = which === 'text' ? ps.textSize : ps.iconSize;
    return (opts.find(o => o.key === key) || opts.find(o => o.key === 'M'))?.label || '';
  }
  function sizeScale(mode, which) {
    const psObj = mode === 'engraving' ? printState.engraving : printState.uvPrint;
    const key   = which === 'text' ? psObj?.textSize : psObj?.iconSize;
    const opts  = sizeOptions(mode, which);
    return (opts.find(o => o.key === key) || opts.find(o => o.key === 'M') || { scale: 1 }).scale;
  }
  // Scaled icon height (mm) — drives both rendering and placement bounds so the
  // icon stays consistent with the chosen size.
  function getIconHmm(modeCfg) {
    const h = Number(modeCfg && modeCfg.icon_height_mm) || 0;
    return h * sizeScale(printState.mode, 'icon');
  }
  // Scaled icon box WIDTH (mm). S/M/L scale the icon SIZE, not the box: the box
  // (max_width_mm × icon_height_mm) is fixed and the icon fits into it at L (1.0),
  // then steps down for M/S. Scaling the width too (height already scales via
  // getIconHmm) makes a WIDE icon shrink for S/M instead of staying pinned to the
  // fixed face width. RENDER-ONLY — placement still reserves getIconHmm height,
  // which always bounds the fitted icon, so the position logic is untouched.
  function getIconWmm(faceCfg) {
    const w = Number(faceCfg && faceCfg.max_width_mm) || 0;
    return w * sizeScale(printState.mode, 'icon');
  }

  // Print state. mode='none' until user picks engraving/uv_print.
  // Position keys reference personalizationSettings.<mode>.positions[].key.
  // UV print has no textY_mm — text always spans full height.
  const printState = {
    mode: 'none',
    engraving: { font: null, text: '', textPosition: null, iconPosition: null, textY_mm: null, iconY_mm: null, textOrientation: 'horizontal', iconSeries: null, icon: null, textSize: 'M', iconSize: 'L',
                 badge: null, badgeText: '', badgePosition: null, badgeY_mm: null },
    uvPrint:   { font: null, text: '', textPosition: null, iconPosition: null,                  iconY_mm: null,                                    iconSeries: null, icon: null, fontColor: null, textSize: 'M', iconSize: 'L' }
  };

  function patchEngravingLayer(texOrNull, meshName = 'Front_Engraving') {
    const root = bottleGroup || scene;
    if (!root) return;
    root.traverse((child) => {
      if (!child.isMesh || child.name !== meshName) return;
      if (!child.material._isCloned) {
        child.material = child.material.clone();
        child.material._isCloned = true;
      }
      // This material hosts the rendered engraving/print map. Tag it so the
      // texture-skin reset (resetSkinToOrig) never restores it to the bare GLB and
      // wipes the user's personalization — even if a variant also names this mesh.
      child.material._fdEngraving = true;
      if (texOrNull) {
        if (child.material.map && child.material.map !== texOrNull) child.material.map.dispose();
        child.material.map         = texOrNull;
        child.material.transparent = true;
        child.material.opacity     = 1.0;
        child.material.depthWrite  = false;
        child.material.depthTest   = false;
        child.material.side        = THREE.FrontSide;
        child.renderOrder = 1;
        child.visible = true;
      } else {
        // Keep mesh visible so child guide overlay renders; hide content via opacity=0.
        if (child.material.map) { child.material.map.dispose(); child.material.map = null; }
        child.material.transparent = true;
        child.material.opacity     = 0;
        child.renderOrder = 0;
        child.visible = true;
      }
      child.material.needsUpdate = true;
    });
  }

  // Sibling-mesh mask overlay (shared by metallic engraving + UV gloss).
  // Disposes the previous overlay; null maskTex removes only.
  function _applyMaskOverlay(meshName, maskTex, name, matProps) {
    const root = bottleGroup || scene;
    if (!root) return;
    root.traverse((child) => {
      if (!child.isMesh || child.name !== meshName) return;
      const existing = child.getObjectByName(name);
      if (existing) {
        existing.material.alphaMap?.dispose();
        existing.material.dispose();
        child.remove(existing);
      }
      if (!maskTex) return;
      const mat = new THREE.MeshStandardMaterial({
        alphaMap: maskTex, transparent: true, alphaTest: 0.05, color: 0xffffff,
        // No depth (shares geometry → z-fight risk).
        depthWrite: false, depthTest: false, side: THREE.FrontSide,
        ...matProps,
      });
      const overlay = new THREE.Mesh(child.geometry, mat);
      overlay.name = name;
      overlay.renderOrder = 2; // above print content, below guide
      // The engraving reflects the world-fixed studio HDR, which is bright on the
      // front-facing azimuth (→ metallic) but flat on the opposite one (back → white).
      // Rotate each engraving face's env by its own facing azimuth so EVERY face
      // reflects the same bright slice the front does — even metallic look on all
      // faces, still world-relative (not glued to the object, so it behaves like the
      // current front). Front (azimuth ~0) stays at 0, untouched. Gloss/UV overlays
      // keep their symmetric env as-is.
      if (name === '__fd_metallic') {
        child.updateWorldMatrix(true, false);
        child.geometry.computeBoundingBox();
        const ctr = child.geometry.boundingBox.getCenter(new THREE.Vector3());
        child.localToWorld(ctr);
        mat.envMapRotation.y = -Math.atan2(ctr.x, ctr.z);
      }
      child.add(overlay);
    });
  }

  // Metallic engraving overlay (laser-etched look under HDR).
  function applyMetallicOverlay(meshName, maskTex) {
    _applyMaskOverlay(meshName, maskTex, '__fd_metallic', {
      metalness: 0.8, roughness: 0.6, envMap: _metalEnvMap, envMapIntensity: 1.0,
    });
  }

  // Satin clear-coat overlay for UV print. Synthetic stripe env keeps the
  // sheen axially symmetric across front/back (studio HDR was uneven).
  function applyGlossOverlay(meshName, maskTex) {
    _applyMaskOverlay(meshName, maskTex, '__fd_gloss', {
      metalness: 1.0, roughness: 0.08, envMap: _glossEnvMap, envMapIntensity: 0.03,
      blending: THREE.AdditiveBlending,
    });
  }

  // Returns canvas pixel dimensions from physical mm size at the given DPI.
  // Falls back to 1024×1024 when dimensions are not configured.
  function getEngravingCanvasSize(widthMm, heightMm, dpi) {
    if (widthMm && heightMm && dpi) return [mmToPx(widthMm, dpi), mmToPx(heightMm, dpi)];
    return [1024, 1024];
  }

  function mmToPx(mm, dpi) {
    return Math.round(mm / 25.4 * dpi);
  }

  // GPU texture cap for the live 3D preview. Full-res canvases (300 DPI, up to
  // ~3000px for a 360° UV wrap) blow up VRAM and trigger the mobile OOM reload.
  // Export is unaffected — it uses the stored canvas/SVG directly, not this.
  function _canvasTex(canvas) {
    // Desktop 3072: typical print-res canvases (engraving face ~830×2900 @600dpi,
    // UV wrap ~2600px @300dpi) fit NATIVELY — no downscale, the preview texture
    // is the full print raster. Clamped to the GPU's real limit for safety.
    // Mobile stays 1024: that cap is the guard against the OOM tab reloads.
    const max = window.matchMedia('(max-width: 1023px)').matches
      ? 1024
      : Math.min(3072, renderer?.capabilities?.maxTextureSize ?? 3072);
    let src = canvas;
    if (Math.max(canvas.width, canvas.height) > max) {
      // Stepped halving + high-quality smoothing: a single big drawImage jump
      // (e.g. 2900px → 1024px) box-filters detail away, which is where the
      // preview blur came from. Halving until within 2× of the cap keeps the
      // downscale inside the filter's quality range at the SAME final texture
      // size — no extra VRAM, small one-off CPU cost per re-render.
      let cur = canvas;
      while (Math.max(cur.width, cur.height) > max * 2) {
        const half = document.createElement('canvas');
        half.width  = Math.max(1, Math.round(cur.width  / 2));
        half.height = Math.max(1, Math.round(cur.height / 2));
        const hctx = half.getContext('2d');
        hctx.imageSmoothingQuality = 'high';
        hctx.drawImage(cur, 0, 0, half.width, half.height);
        cur = half;
      }
      const s = max / Math.max(cur.width, cur.height);
      src = document.createElement('canvas');
      src.width  = Math.round(cur.width  * s);
      src.height = Math.round(cur.height * s);
      const sctx = src.getContext('2d');
      sctx.imageSmoothingQuality = 'high';
      sctx.drawImage(cur, 0, 0, src.width, src.height);
    }
    const tex = new THREE.CanvasTexture(src);
    tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
    // Anisotropic sampling keeps the texture sharp at glancing angles on the
    // curved bottle — near-free on modern GPUs, zero extra memory.
    tex.anisotropy = renderer?.capabilities?.getMaxAnisotropy?.() ?? 1;
    tex.needsUpdate = true;
    return tex;
  }

  // Overlay mask: copy content alpha, fill RGB white (source-in) — keeps the
  // alpha byte-identical to the content canvas. null on failure.
  function _whiteMaskTex(canvas, label) {
    try {
      const mask = document.createElement('canvas');
      mask.width = canvas.width; mask.height = canvas.height;
      const mctx = mask.getContext('2d');
      mctx.drawImage(canvas, 0, 0);
      mctx.globalCompositeOperation = 'source-in';
      mctx.fillStyle = '#ffffff';
      mctx.fillRect(0, 0, mask.width, mask.height);
      return _canvasTex(mask);
    } catch (e) {
      console.warn(`[FlexiDesign] ${label} mask build failed:`, e);
      return null;
    }
  }

  const _lastCanvases = {};

  // PNG DPI helpers

  // CRC-32 lookup table — used to compute PNG chunk checksums.
  const _CRC32_TABLE = (() => {
    const t = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
      let c = i;
      for (let j = 0; j < 8; j++) c = (c & 1) ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
      t[i] = c;
    }
    return t;
  })();

  function _crc32(bytes) {
    let crc = 0xFFFFFFFF;
    for (const b of bytes) crc = _CRC32_TABLE[(crc ^ b) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
  }

  // Builds a valid PNG pHYs chunk that encodes the given DPI as pixels-per-metre.
  function _makePHYsChunk(dpi) {
    const ppm   = Math.round(dpi / 0.0254); // 1 inch = 0.0254 metres
    const chunk = new Uint8Array(21);        // 4 length + 4 type + 9 data + 4 CRC
    const view  = new DataView(chunk.buffer);
    view.setUint32(0, 9);                          // data length = 9 bytes
    chunk.set([0x70, 0x48, 0x59, 0x73], 4);       // type = "pHYs"
    view.setUint32(8,  ppm);                       // X pixels per metre
    view.setUint32(12, ppm);                       // Y pixels per metre
    chunk[16] = 1;                                 // unit specifier = metre
    view.setUint32(17, _crc32(chunk.subarray(4, 17))); // CRC over type+data
    return chunk;
  }

  // Returns a Blob of the canvas PNG with a pHYs chunk injected after the IHDR chunk.
  // PNG layout: 8-byte signature + 25-byte IHDR = 33 bytes, then remaining chunks.
  async function injectPngDpi(canvas, dpi) {
    const blob  = await new Promise(res => canvas.toBlob(res, 'image/png'));
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const phys  = _makePHYsChunk(dpi);
    const out   = new Uint8Array(bytes.length + phys.length);
    out.set(bytes.subarray(0, 33));
    out.set(phys, 33);
    out.set(bytes.subarray(33), 33 + phys.length);
    return new Blob([out], { type: 'image/png' });
  }

  function triggerDownload(link) {
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Downloads the last rendered output for the given key.
  // Engraving faces (one entry per metaobject position) export as .svg;
  // UV print ('uv') exports as .png with a pHYs chunk carrying the DPI.
  async function downloadCanvas(key = 'uv', fileName) {
    const stored = _lastCanvases[key];
    if (!stored) {
      console.warn('[FlexiDesign] No export stored for key:', key, '— available:', Object.keys(_lastCanvases));
      return;
    }
    const isSvg = stored.type === 'svg';
    const blob  = isSvg ? _exportSvgBlob(stored) : await _exportPngBlob(stored);
    const link  = document.createElement('a');
    link.href     = URL.createObjectURL(blob);
    link.download = fileName || `canvas-${key}.${isSvg ? 'svg' : 'png'}`;
    triggerDownload(link);
    URL.revokeObjectURL(link.href);
  }

  window._fd = { canvases: _lastCanvases, download: downloadCanvas };


  // Canvas drawing helpers

  function withClipX(ctx, x1, x2, fn) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x1, 0, x2 - x1, ctx.canvas.height);
    ctx.clip();
    fn();
    ctx.restore();
  }

  // Shared font & position helpers (canvas and SVG paths both use these)

  // Runs an async loader with linear-backoff retries. Used to make the
  // opentype.js / wawoff2 CDN imports tolerant of transient network blips.
  async function _retryLoader(loaderFn, label, maxAttempts = 3, baseDelayMs = 500) {
    let lastErr;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await loaderFn();
      } catch (e) {
        lastErr = e;
        console.warn(`[FlexiDesign] ${label} attempt ${i + 1}/${maxAttempts} failed:`, e?.message || e);
        if (i < maxAttempts - 1) {
          await new Promise(r => setTimeout(r, baseDelayMs * (i + 1)));
        }
      }
    }
    throw lastErr;
  }

  // opentype.js — lazy, cached. On hard failure we clear the cached
  // promise so a future call can re-attempt.
  let _otjsPromise = null;
  function loadOpentypeLib() {
    if (_otjsPromise) return _otjsPromise;
    const attempt = () => new Promise((res, rej) => {
      if (window.opentype) return res(window.opentype);
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/opentype.js@1.3.4/dist/opentype.min.js';
      s.onload  = () => res(window.opentype);
      s.onerror = () => { s.remove(); rej(new Error('Failed to load opentype.js')); };
      document.head.appendChild(s);
    });
    _otjsPromise = _retryLoader(attempt, 'opentype.js').catch(err => {
      _otjsPromise = null;          // allow a future call to try again
      throw err;
    });
    return _otjsPromise;
  }

  // WOFF2 → sfnt decompress (opentype.js v1.x can't read WOFF2 natively).
  // The theme ships its own copy (assets/bl_wawoff2.js — the wawoff2 Emscripten
  // build wrapped to expose window.__fdWawoff2) and uses it first: the CDN ESM
  // builds are rebuilt on the CDN side and have shipped broken (their decoder
  // never signals ready, so decompress() hangs forever). CDNs stay as fallback.
  let _wawoff2Promise = null;
  function loadWawoff2() {
    if (_wawoff2Promise) return _wawoff2Promise;
    const selfHosted = () => new Promise((res, rej) => {
      const url = config.wawoff2Url || new URL('bl_wawoff2.js', import.meta.url).href;
      const done = M => res({ decompress: buf => { const out = M.decompress(buf); if (out === false) throw new Error('ConvertWOFF2ToTTF failed'); return out; } });
      const t = setTimeout(() => rej(new Error('wawoff2 (self-hosted): runtime init timed out')), 15000);
      const M0 = window.__fdWawoff2;
      if (M0) { clearTimeout(t); if (M0.__fdReady) return done(M0); M0.__fdResolve = m => { done(m); }; return; }
      const s = document.createElement('script');
      s.src = url;
      s.onload  = () => { const M = window.__fdWawoff2; if (!M) { clearTimeout(t); return rej(new Error('wawoff2 (self-hosted): module missing')); } if (M.__fdReady) { clearTimeout(t); done(M); } else M.__fdResolve = m => { clearTimeout(t); done(m); }; };
      s.onerror = () => { clearTimeout(t); s.remove(); rej(new Error('wawoff2 (self-hosted): script failed to load')); };
      document.head.appendChild(s);
    });
    const sources = [
      'https://esm.sh/wawoff2@2.0.1',
      'https://esm.sh/wawoff2@2.0.1?bundle',
      'https://cdn.jsdelivr.net/npm/wawoff2@2.0.1/+esm',
      'https://unpkg.com/wawoff2@2.0.1/build/decompress_binding.js',
    ];
    const tryAll = async () => {
      let lastErr;
      try { return await selfHosted(); }
      catch (e) { lastErr = e; console.warn('[FlexiDesign] wawoff2 self-hosted failed, falling back to CDN:', e?.message || e); }
      for (const url of sources) {
        try {
          const mod = await _retryLoader(() => import(/* @vite-ignore */ url), `wawoff2 (${new URL(url).hostname})`);
          const decompress = mod.decompress || mod.default?.decompress || mod.default;
          if (typeof decompress !== 'function') {
            throw new Error('wawoff2: decompress export not found');
          }
          return { decompress };
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr || new Error('wawoff2: all CDN sources failed');
    };
    _wawoff2Promise = tryAll().catch(err => {
      _wawoff2Promise = null;       // allow a future call to try again
      throw err;
    });
    return _wawoff2Promise;
  }

  // A font load that neither succeeds nor fails within this window is treated
  // as failed, so a hung decoder can never leave the preview spinner on forever.
  const FONT_LOAD_TIMEOUT_MS = 20000;
  const _fontCache = new Map();
  async function loadOpentypeFont(url) {
    if (!url) return null;
    if (_fontCache.has(url)) return _fontCache.get(url);
    let timer;
    const p = Promise.race([(async () => {
      const otjs = await loadOpentypeLib();
      let buf = await fetch(url, { mode: 'cors' }).then(r => r.arrayBuffer());
      // Sniff the magic bytes — file extension can lie (Shopify Files sometimes
      // serves WOFF2 bytes from URLs that end in .ttf because the original
      // upload was actually WOFF2 renamed). Decompress when needed.
      const sig = new Uint8Array(buf, 0, 4);
      const isWoff2 = sig[0] === 0x77 && sig[1] === 0x4f && sig[2] === 0x46 && sig[3] === 0x32;
      if (isWoff2) {
        const { decompress } = await loadWawoff2();
        const out = await decompress(new Uint8Array(buf));
        // Copy into a fresh ArrayBuffer to strip any byteOffset.
        buf = out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength);
      }
      return otjs.parse(buf);
    })(), new Promise((_, rej) => { timer = setTimeout(() => rej(new Error(`font load timed out after ${FONT_LOAD_TIMEOUT_MS}ms: ${url}`)), FONT_LOAD_TIMEOUT_MS); })])
      .finally(() => clearTimeout(timer));
    _fontCache.set(url, p);
    try { return await p; } catch (e) { _fontCache.delete(url); throw e; }
  }
  const FONT_LOAD_FAILED_MSG = 'This font couldn’t be loaded. Please choose another font or try again.';

  // opentype layout for shared canvas+SVG geometry. null when no text.
  async function prepareTextLayout(ps, ch, cw, dpi, pers, isVertical, mode, faceCfg) {
    const text = ps.text?.trim();
    if (!text || !ps.font?.file) return null;

    let font;
    try { font = await loadOpentypeFont(ps.font.file); }
    catch (e) { console.warn('[FlexiDesign] font load failed:', e); _artNotice(FONT_LOAD_FAILED_MSG); return null; }

    const modeCfg  = getModeCfg(pers, mode);
    const maxWmm   = faceCfg?.max_width_mm  ?? 0;
    const maxHmm   = faceCfg?.max_height_mm ?? 0;
    const textHmm  = modeCfg?.text_height_mm ?? 0;

    // Start at base size (× chosen S/M/L scale), only shrink to fit (no enlarge).
    const basePt = (Number(ps.font?.baseSize) || pers?.font_size_pt || 20) * sizeScale(mode, 'text');
    let fontSize = Math.round(basePt * dpi / 72);

    // Pre-rotate text box. Engraving: max_width_mm × text_height_mm both
    // orientations. UV vertical: main bound = max_height_mm (text length),
    // cross bound = max_width_mm (letter height).
    const isUVVertical = mode === 'uv_print' && isVertical;
    const maxMainMm    = isUVVertical ? maxHmm : maxWmm;
    const maxCrossMm   = isUVVertical ? maxWmm : textHmm;
    const maxMainPx    = Math.min(
      mmToPx(maxMainMm, dpi),
      isVertical ? ch : cw
    );
    const maxCrossPx   = Math.min(
      mmToPx(maxCrossMm, dpi),
      isVertical ? cw : ch
    );

    const probe = (size) => {
      const p    = font.getPath(text, 0, 0, size);
      const bbox = p.getBoundingBox();
      return {
        width:  bbox.x2 - bbox.x1,
        height: bbox.y2 - bbox.y1,
        x1: bbox.x1, x2: bbox.x2,
        y1: bbox.y1, y2: bbox.y2,
      };
    };

    // Shrink-to-fit: glyph bbox can be slightly over the cap because metrics
    // don't scale linearly across integer font sizes. The guard counter +
    // safety multiplier make sure we converge below the cap without spinning.
    let m = probe(fontSize);
    for (let guard = 0; guard < 8 && (m.width > maxMainPx || m.height > maxCrossPx); guard++) {
      const wRatio = m.width  > maxMainPx  ? maxMainPx  / m.width  : 1;
      const hRatio = m.height > maxCrossPx ? maxCrossPx / m.height : 1;
      const ratio  = Math.min(wRatio, hRatio) * 0.995;
      const next   = Math.max(1, Math.floor(fontSize * ratio));
      if (next >= fontSize) { fontSize = Math.max(1, fontSize - 1); }
      else                  { fontSize = next; }
      m = probe(fontSize);
    }

    return {
      text, font, fontSize,
      glyphLeft:   m.x1,
      glyphRight:  m.x2,
      glyphTop:    m.y1,
      glyphBottom: m.y2,
    };
  }

  // Baseline Y for horizontal text. 'top' = put the highest glyph at cy;
  // 'center' = centre the actual glyph bbox at cy.
  function textBaselineY(layout, cy, alignMode) {
    if (alignMode === 'top') return cy - layout.glyphTop;
    return cy - (layout.glyphTop + layout.glyphBottom) / 2;
  }

  // Draws text layout as filled Path2D. Vertical = rotated -90° around origin.
  function drawTextPath(ctx, layout, cx, cy, isVertical, fillColor, alignMode) {
    if (!layout) return;
    const midX = (layout.glyphLeft + layout.glyphRight) / 2;
    const midY = (layout.glyphTop  + layout.glyphBottom) / 2;
    if (isVertical) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-Math.PI / 2);
      const startX = -midX;
      const baseY  = -midY;
      const p   = layout.font.getPath(layout.text, startX, baseY, layout.fontSize);
      const p2d = new Path2D(p.toPathData(2));
      ctx.fillStyle = fillColor;
      ctx.fill(p2d);
      ctx.restore();
    } else {
      const baseY  = textBaselineY(layout, cy, alignMode);
      const startX = cx - midX;
      const p   = layout.font.getPath(layout.text, startX, baseY, layout.fontSize);
      const p2d = new Path2D(p.toPathData(2));
      ctx.fillStyle = fillColor;
      ctx.fill(p2d);
    }
  }

  // SVG <path> equivalent of drawTextPath — same geometry, different output.
  function svgTextPath(layout, cx, cy, isVertical, fillColor, alignMode) {
    if (!layout) return '';
    const midX = (layout.glyphLeft + layout.glyphRight) / 2;
    const midY = (layout.glyphTop  + layout.glyphBottom) / 2;
    if (isVertical) {
      const startX = -midX;
      const baseY  = -midY;
      const d = layout.font.getPath(layout.text, startX, baseY, layout.fontSize).toPathData(2);
      return `<path d="${d}" fill="${fillColor}" transform="translate(${cx},${cy}) rotate(-90)"/>`;
    }
    const baseY  = textBaselineY(layout, cy, alignMode);
    const startX = cx - midX;
    const d = layout.font.getPath(layout.text, startX, baseY, layout.fontSize).toPathData(2);
    return `<path d="${d}" fill="${fillColor}"/>`;
  }

  // Returns { iconCY, textCY } in canvas px. UV text always centred; engraving
  // uses ps.textY_mm/ps.iconY_mm clamped to face's slider range.
  function resolveContentPositions(faceCfg, hasIcon, hasText, ps, ch, dpi, pers, mode) {
    const modeCfg = getModeCfg(pers, mode);
    const textHmm = getTextHmm(mode, modeCfg, ps.textOrientation, faceCfg);
    const iconHmm = getIconHmm(modeCfg);
    const { topMm, bottomMm } = safeAreaYMm(mode, faceCfg, modeCfg);
    const safeMidPx = Math.round(mmToPx((topMm + bottomMm) / 2, dpi));

    let iconCY = safeMidPx;
    let textCY = safeMidPx;

    if (hasIcon) {
      const iconRange = ySliderRangeMm(mode, faceCfg, modeCfg, iconHmm);
      const yMm = ps.iconY_mm != null ? clampMm(ps.iconY_mm, iconRange.minMm, iconRange.maxMm)
                                      : (iconRange.minMm + iconRange.maxMm) / 2;
      iconCY = Math.round(mmToPx(yMm, dpi));
    }

    if (hasText) {
      if (mode === 'uv_print') {
        // Full-height vertical text — centred in safe area regardless of icon.
        textCY = safeMidPx;
      } else {
        const textRange = ySliderRangeMm(mode, faceCfg, modeCfg, textHmm);
        const yMm = ps.textY_mm != null ? clampMm(ps.textY_mm, textRange.minMm, textRange.maxMm)
                                        : (textRange.minMm + textRange.maxMm) / 2;
        textCY = Math.round(mmToPx(yMm, dpi));
      }
    }

    return { iconCY, textCY, textAlignMode: 'center' };
  }

  async function drawFaceContent(ctx, faceCfg, hasIcon, hasText, ps, mode, centerX, cw, ch, dpi, pers) {
    const spHere = mode === 'engraving' && !!ps.badge && ps.badgePosition === faceCfg?.key;
    if (!hasIcon && !hasText && !spHere) return;

    const modeCfg    = getModeCfg(pers, mode);
    const iconHpx    = mmToPx(getIconHmm(modeCfg), dpi);
    const maxWpx     = mmToPx(faceCfg?.max_width_mm   ?? 0, dpi);
    const iconWpx    = mmToPx(getIconWmm(faceCfg), dpi); // scaled icon box width (render only)
    const fillColor  = (mode === 'uv_print' && ps.fontColor) ? ps.fontColor.color : '#1a1a1a';
    const isVertical = mode === 'uv_print' || (mode === 'engraving' && ps.textOrientation === 'vertical');

    const layout = hasText ? await prepareTextLayout(ps, ch, cw, dpi, pers, isVertical, mode, faceCfg) : null;

    let img = null;
    if (hasIcon && ps.icon?.previewIcon) {
      img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise(res => { img.onload = res; img.onerror = res; img.src = ps.icon.previewIcon; });
      if (!img.complete || img.naturalWidth === 0) img = null;
    }

    const { iconCY, textCY, textAlignMode } = resolveContentPositions(faceCfg, hasIcon, hasText, ps, ch, dpi, pers, mode);

    const halfContent = maxWpx / 2;
    // Seam-split is only meaningful for the UV print wrap canvas — engraving
    // faces are flat rectangles per mesh, so the content never wraps.
    // Decision is purely geometric: does the safe-area bbox cross x=0 or x=cw?
    const wrapsLeft  = mode === 'uv_print' && centerX - halfContent < 0;
    const wrapsRight = mode === 'uv_print' && centerX + halfContent > cw;

    const drawIconAt = (cx, cy) => {
      if (!img || !iconWpx || !iconHpx) return;
      const aspect = img.naturalWidth / img.naturalHeight;
      let dw, dh;
      if (aspect >= iconWpx / iconHpx) { dw = iconWpx; dh = iconWpx / aspect; }
      else                             { dh = iconHpx; dw = iconHpx * aspect; }
      ctx.drawImage(img, Math.round(cx - dw / 2), Math.round(cy - dh / 2), Math.round(dw), Math.round(dh));
    };
    let spImg = null, spBox = null;
    const spSrc = ps.badge?.svg;
    if (spHere) {
      spBox = _badgeBoxPx(ps, faceCfg, modeCfg, dpi);
      const svg = await _badgeSvgFor(ps, faceCfg, modeCfg).then(r => _stripKitText(r.svg)).catch(() => null);
      spImg = new Image();
      await new Promise(res => { spImg.onload = res; spImg.onerror = res; spImg.src = svg ? 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg) : spSrc; });
      if (!spImg.naturalWidth) spImg = null;
    }
    const doDraw = (cx) => {
      if (hasIcon) drawIconAt(cx, iconCY);
      if (hasText && layout) drawTextPath(ctx, layout, cx, textCY, isVertical, fillColor, textAlignMode);
      if (spImg) {
        const s = Math.min(spBox.w / spImg.naturalWidth, spBox.h / spImg.naturalHeight);
        const dw = spImg.naturalWidth * s, dh = spImg.naturalHeight * s;
        ctx.drawImage(spImg, Math.round(cx - dw / 2), Math.round(spBox.cy - dh / 2), Math.round(dw), Math.round(dh));
      }
    };

    if (wrapsLeft) {
      // Content's left edge extends past x=0 → draw the right portion in
      // place; mirror the spilled-over left portion onto the canvas's right edge.
      const wrapAmt = halfContent - centerX;
      withClipX(ctx, 0, centerX + halfContent, () => doDraw(centerX));
      withClipX(ctx, cw - wrapAmt, cw, () => doDraw(cw + centerX));
    } else if (wrapsRight) {
      // Mirror image of above for the x=cw edge.
      const wrapAmt = centerX + halfContent - cw;
      withClipX(ctx, centerX - halfContent, cw, () => doDraw(centerX));
      withClipX(ctx, 0, wrapAmt, () => doDraw(centerX - cw));
    } else {
      doDraw(centerX);
    }
    if (spImg) await _overlayBadgeKit(ctx, ps, faceCfg, modeCfg, dpi, centerX);
  }

  // SVG export helpers (engraving)

    // 90° CW rotate for export (swap viewBox + width/height).
  function rotateSvg90(svgStr, widthMm, heightMm) {
    const vbMatch = svgStr.match(/\sviewBox="0 0 ([\d.]+) ([\d.]+)"/);
    if (!vbMatch) return svgStr;
    const vbW = parseFloat(vbMatch[1]);
    const vbH = parseFloat(vbMatch[2]);
    let out = svgStr;
    out = out.replace(
      /\sviewBox="0 0 [\d.]+ [\d.]+"/,
      ` viewBox="0 0 ${vbH} ${vbW}"`
    );
    if (widthMm && heightMm) {
      out = out.replace(
        /\swidth="[^"]*"\sheight="[^"]*"/,
        ` width="${heightMm}mm" height="${widthMm}mm"`
      );
    } else {
      out = out.replace(
        /\swidth="([^"]+)"\sheight="([^"]+)"/,
        ' width="$2" height="$1"'
      );
    }
    out = out.replace(/^(<svg[^>]*>)/, `$1<g transform="translate(${vbH},0) rotate(90)">`);
    out = out.replace(/<\/svg>\s*$/, '</g></svg>');
    return out;
  }

  async function fetchDataUri(url) {
    try {
      const resp = await fetch(url);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return await new Promise(res => {
        const reader = new FileReader();
        reader.onload  = () => res(reader.result);
        reader.onerror = () => res(null);
        reader.readAsDataURL(blob);
      });
    } catch (e) { return null; }
  }

  async function svgEmbedExternal(svgStr) {
    const hrefRe   = /href="(https?:\/\/[^"]+)"/g;
    const cssUrlRe = /url\('(https?:\/\/[^']+)'\)/g;
    const urls = new Set();
    for (const m of svgStr.matchAll(hrefRe))   urls.add(m[1]);
    for (const m of svgStr.matchAll(cssUrlRe)) urls.add(m[1]);
    if (!urls.size) return svgStr;
    const map = {};
    await Promise.all([...urls].map(async url => {
      const d = await fetchDataUri(url);
      if (d) map[url] = d;
    }));
    return svgStr
      .replace(hrefRe,   (_, url) => `href="${map[url] || url}"`)
      .replace(cssUrlRe, (_, url) => `url('${map[url] || url}')`);
  }

  async function svgToCanvas(svgStr, cw, ch) {
    const embedded = await svgEmbedExternal(svgStr);
    const blob = new Blob([embedded], { type: 'image/svg+xml' });
    const url  = URL.createObjectURL(blob);
    const img  = new Image();
    img.width  = cw; img.height = ch;
    await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('[FlexiDesign] SVG render failed')); img.src = url; });
    URL.revokeObjectURL(url);
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
    return canvas;
  }

  // Badge: template SVG's #fd-text rect = the text box. Text starts at fontPt, only
  // shrinks to fit, and is centred in the rect; emitted as glyph paths so the file needs no font.
  const _svgTextCache = new Map();
  function _fetchSvgText(url) {
    if (!_svgTextCache.has(url)) _svgTextCache.set(url, fetch(url).then(r => {
      if (!r.ok) throw new Error(`[FlexiDesign] badge template ${r.status}`);
      return r.text();
    }).catch(e => { _svgTextCache.delete(url); throw e; }));
    return _svgTextCache.get(url);
  }
  // Adobe Fonts kit: inject its stylesheet exactly like the embed does, then read the
  // family it added from document.fonts. The browser renders it; the font file itself
  // is never fetched or read by this code.
  const _kitFamilies = new Map();
  function _loadKitFamily(href) {
    if (!_kitFamilies.has(href)) _kitFamilies.set(href, new Promise((res, rej) => {
      const before = new Set([...document.fonts].map(f => f.family));
      const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
      l.onload = () => {
        const fam = [...document.fonts].map(f => f.family).find(f => !before.has(f));
        fam ? res(fam.replace(/^["']|["']$/g, '')) : rej(new Error('[FlexiDesign] Adobe kit has no font'));
      };
      l.onerror = () => rej(new Error('[FlexiDesign] Adobe kit failed to load'));
      document.head.appendChild(l);
    }).catch(e => { _kitFamilies.delete(href); throw e; }));
    return _kitFamilies.get(href);
  }
  let _measureCtx = null;
  // Kit-font ink bbox at `size` (SVG units), measured by the browser (alphabetic baseline at 0).
  async function _kitBBox(family, text, size) {
    const K = 100; // measure large for precision
    await document.fonts.load(`400 ${size * K}px "${family}"`, text);
    if (!document.fonts.check(`400 ${size * K}px "${family}"`, text)) throw new Error('[FlexiDesign] Adobe font not available on this domain');
    const ctx = (_measureCtx ||= document.createElement('canvas').getContext('2d'));
    ctx.font = `400 ${size * K}px "${family}"`;
    const m = ctx.measureText(text);
    return { x1: -m.actualBoundingBoxLeft / K, x2: m.actualBoundingBoxRight / K, y1: -m.actualBoundingBoxAscent / K, y2: m.actualBoundingBoxDescent / K };
  }
  // "al-fresco" → "Al Fresco" (the name Creative Cloud installs it under) for the export.
  const _kitDisplayName = fam => fam.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // boxMm = the mm box the design is aspect-fitted into; fontPt is physical at that size.
  // Font file → text as glyph <path>s. Adobe kit → live <text data-fd-badge> (the preview
  // overlays it with fillText — see _drawBadgeKitText — since SVG-as-image can't load web fonts).
  let _badgeCache = null;
  async function buildBadgeSvg(sp, rawText, boxMm) {
    const text = (rawText || '').trim();
    const kit  = _kitHref(sp);
    const key  = `${sp.svg}|${sp.fontFile || kit}|${text}|${boxMm.w}x${boxMm.h}`;
    if (_badgeCache?.key === key) return _badgeCache.value;
    const doc   = new DOMParser().parseFromString(await _fetchSvgText(sp.svg), 'image/svg+xml');
    const svgEl = doc.querySelector('svg');
    const box   = doc.getElementById('fd-text');
    const vb    = (svgEl?.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (!svgEl || !box || vb.length !== 4 || !(vb[2] > 0)) throw new Error('[FlexiDesign] badge template needs a viewBox and #fd-text');
    const n = a => parseFloat(box.getAttribute(a)) || 0;
    const rx = n('x'), ry = n('y'), rw = n('width'), rh = n('height');
    box.remove();
    let path = '', fontPt = null, kitText = null;
    if (text) {
      const base   = Number(sp.fontPt) * 25.4 / 72 / Math.min(boxMm.w / vb[2], boxMm.h / vb[3]);
      const font   = kit ? null : await loadOpentypeFont(sp.fontFile);
      const family = kit ? await _loadKitFamily(kit) : null;
      if (!font && !family) throw new Error('[FlexiDesign] badge font missing');
      const bb = font ? font.getPath(text, 0, 0, base).getBoundingBox() : await _kitBBox(family, text, base);
      const s  = Math.min(1, rw / (bb.x2 - bb.x1), rh / (bb.y2 - bb.y1));
      const x = rx + (rw - (bb.x2 - bb.x1) * s) / 2 - bb.x1 * s;
      const y = ry + (rh - (bb.y2 - bb.y1) * s) / 2 - bb.y1 * s;
      if (font) path = `<path d="${font.getPath(text, x, y, base * s).toPathData(3)}" fill="#1a1a1a"/>`;
      else {
        kitText = { family, text, x, y, size: base * s };
        path = `<text data-fd-badge="1" x="${x.toFixed(3)}" y="${y.toFixed(3)}" font-family="${escapeAttr(`'${_kitDisplayName(family)}', '${family}'`)}" font-weight="400" font-size="${(base * s).toFixed(3)}" fill="#1a1a1a">${escapeText(text)}</text>`;
      }
      fontPt = +(Number(sp.fontPt) * s).toFixed(2);
    }
    const value = {
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb.join(' ')}" width="800" height="${Math.round(800 * vb[3] / vb[2])}">${svgEl.innerHTML}${path}</svg>`,
      fontPt, vb, kitText,
    };
    _badgeCache = { key, value };
    return value;
  }
  // Badge design container on its face (px) — centre from badgeY_mm.
  function _badgeBoxPx(ps, faceCfg, modeCfg, dpi) {
    const box = _badgeBoxMm(faceCfg, modeCfg);
    const r   = ySliderRangeMm('engraving', faceCfg, modeCfg, box.h);
    const yMm = ps.badgeY_mm != null ? clampMm(ps.badgeY_mm, r.minMm, r.maxMm) : (r.minMm + r.maxMm) / 2;
    return { cy: Math.round(mmToPx(yMm, dpi)), w: mmToPx(box.w, dpi), h: mmToPx(box.h, dpi) };
  }
  const _badgeSvgFor = (ps, faceCfg, modeCfg) => buildBadgeSvg(ps.badge, ps.badgeText, _badgeBoxMm(faceCfg, modeCfg));
  // Preview only: kit-font badge text is drawn with fillText (the browser's web font) over
  // the rasterised SVG, from which the live <text> is stripped first. Same aspect-fit geometry.
  const _stripKitText = svg => svg.replace(/<text data-fd-badge="1"[\s\S]*?<\/text>/g, '');
  async function _overlayBadgeKit(ctx, ps, faceCfg, modeCfg, dpi, cx) {
    if (!ps.badge || ps.badgePosition !== faceCfg?.key || !_kitHref(ps.badge)) return;
    const r = await _badgeSvgFor(ps, faceCfg, modeCfg).catch(() => null);
    const k = r?.kitText;
    if (!k) return;
    const b = _badgeBoxPx(ps, faceCfg, modeCfg, dpi);
    const [vx, vy, vw, vh] = r.vb;
    const s = Math.min(b.w / vw, b.h / vh);
    ctx.save();
    ctx.font = `400 ${k.size * s}px "${k.family}"`;
    ctx.fillStyle = '#1a1a1a';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(k.text, cx - vw * s / 2 + (k.x - vx) * s, b.cy - vh * s / 2 + (k.y - vy) * s);
    ctx.restore();
  }

  async function fetchInlineSVG(url, cx, cy, containerW, containerH, text = null) {
    if (text != null || /\.svg(\?|$)/i.test(url)) {
      try {
        const resp = text != null ? null : await fetch(url);
        if (text != null || resp.ok) {
          if (text == null) text = await resp.text();
          const doc  = new DOMParser().parseFromString(text, 'image/svg+xml');
          const svgEl = doc.querySelector('svg');
          if (svgEl) {
            const vb = svgEl.getAttribute('viewBox') ||
              `0 0 ${svgEl.getAttribute('width') || 100} ${svgEl.getAttribute('height') || 100}`;
            const inner = svgEl.innerHTML;
            const x = Math.round(cx - containerW / 2);
            const y = Math.round(cy - containerH / 2);
            return `<g transform="translate(${x},${y})"><svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" width="${containerW}" height="${containerH}" preserveAspectRatio="xMidYMid meet">${inner}</svg></g>`;
          }
        }
      } catch (e) {}
    }
    const imgSrc = (await fetchDataUri(url)) || url;
    return `<image href="${imgSrc}" x="${Math.round(cx - containerW / 2)}" y="${Math.round(cy - containerH / 2)}" width="${containerW}" height="${containerH}" preserveAspectRatio="xMidYMid meet"/>`;
  }

  async function buildEngravingSVG(faceCfg, hasIcon, hasText, ps, cw, ch, dpi, pers) {
    const modeCfg    = getModeCfg(pers, 'engraving');
    const iconHpx    = mmToPx(getIconHmm(modeCfg), dpi);
    const iconWpx    = mmToPx(getIconWmm(faceCfg), dpi); // scaled icon box width (render only)
    const cx         = cw / 2;
    const isVertical = ps.textOrientation === 'vertical';

    const layout = hasText ? await prepareTextLayout(ps, ch, cw, dpi, pers, isVertical, 'engraving', faceCfg) : null;
    const { iconCY, textCY, textAlignMode } = resolveContentPositions(faceCfg, hasIcon, hasText, ps, ch, dpi, pers, 'engraving');

    // fetchInlineSVG inlines SVG paths or embeds raster icons as data URIs
    let iconSvg = '';
    if (hasIcon && ps.icon?.previewIcon) {
      iconSvg = await fetchInlineSVG(ps.icon.previewIcon, cx, iconCY, iconWpx, iconHpx);
    }
    // Preview falls back to the bare design; export is fail-closed upstream (_collectPersonalizationFiles).
    const sp = ps.badge;
    if (sp && ps.badgePosition === faceCfg.key) {
      const b = _badgeBoxPx(ps, faceCfg, modeCfg, dpi);
      const s = await _badgeSvgFor(ps, faceCfg, modeCfg).then(r => r.svg)
        .catch(e => { console.warn('[FlexiDesign] badge render failed:', e); _artNotice(FONT_LOAD_FAILED_MSG); return null; });
      iconSvg += await fetchInlineSVG(sp.svg, cx, b.cy, b.w, b.h, s);
    }

    // Text is emitted as a <path> built from the actual glyph outlines, so the
    // export is font-independent (no @font-face needed) and matches the preview
    // exactly because both use the same opentype getPath() output.
    const textSvg = layout
      ? svgTextPath(layout, cx, textCY, isVertical, '#1a1a1a', textAlignMode)
      : '';

    // Invisible full-canvas rect so engraving software reads the declared viewBox
    // dimensions rather than shrinking to the content bounding box.
    const canvasRect = `<rect width="${cw}" height="${ch}" fill="none"/>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${cw}" height="${ch}" viewBox="0 0 ${cw} ${ch}">${canvasRect}${iconSvg}${textSvg}</svg>`;
  }

  // Dashed editing guide. Separate child mesh on each engraving/UV mesh —
  // doesn't touch the text/icon canvas pipeline so exports stay clean.
  const _guideTextures = new Map();

  // Transparent texture with dashed rect per safe area. `isWrap` true = UV 360°
  // wrap (seam split; a full-width area has no left/right edge); false = a flat
  // engraving face (always a bounded 4-edge rectangle).
  function _makeGuideTexture(canvasW, canvasH, rects, color, isWrap) {
    const rectsKey = rects.map(r => `${r.x}-${r.y}-${r.w}-${r.h}`).join('|');
    const key = `${canvasW}x${canvasH}_${color}_${isWrap ? 'w' : 'f'}_${rectsKey}`;
    if (_guideTextures.has(key)) return _guideTextures.get(key);
    const canvas = document.createElement('canvas');
    canvas.width  = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasW, canvasH);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.75;
    rects.forEach(r => {
      const dash = Math.max(24, Math.round(Math.min(r.w, r.h) / 18));
      const gap  = Math.max(8, Math.round(dash * 0.5));
      ctx.lineWidth = Math.max(4, Math.round(Math.min(r.w, r.h) / 120));
      ctx.setLineDash([dash, gap]);
      const y = Math.round(r.y);
      const h = Math.round(r.h);
      const w = Math.round(r.w);
      const drawSeg = (x1, y1, x2, y2) => {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      };
      // Flat engraving face: always a bounded 4-edge rectangle. Clamp into the
      // canvas and inset by the stroke width so the edges stay visible even when
      // the safe area fills (or exceeds) the whole face.
      if (!isWrap) {
        const inset = ctx.lineWidth / 2;
        const x0 = Math.max(inset, Math.round(r.x));
        const x1 = Math.min(canvasW - inset, Math.round(r.x) + w);
        const y0 = Math.max(inset, y);
        const y1 = Math.min(canvasH - inset, y + h);
        ctx.strokeRect(x0, y0, Math.max(0, x1 - x0), Math.max(0, y1 - y0));
        return;
      }
      // Normalise x into [0, canvasW) so a centre at degree=0 (x≈0) renders
      // correctly across the wrap seam.
      let nx = ((Math.round(r.x) % canvasW) + canvasW) % canvasW;
      if (w >= canvasW) {
        // Safe area spans the full wrap → no left/right edges exist
        // geometrically. Painting them would create a spurious vertical
        // line diametrically opposite the configured face.
        drawSeg(0, y,     canvasW, y);
        drawSeg(0, y + h, canvasW, y + h);
      } else if (nx + w <= canvasW) {
        ctx.strokeRect(nx, y, w, h);
      } else {
        // Wraps across the seam — draw only the outer perimeter so the
        // canvas-edge edges (which would otherwise paint a vertical dotted
        // line down the middle of the safe area on the wrap) are skipped.
        const rightW = w - (canvasW - nx);
        drawSeg(nx,     y,     nx,     y + h);   // outer-left  (right portion)
        drawSeg(rightW, y,     rightW, y + h);   // outer-right (left portion)
        drawSeg(nx,     y,     canvasW, y);       // top split across seam
        drawSeg(0,      y,     rightW,  y);
        drawSeg(nx,     y + h, canvasW, y + h);   // bottom split across seam
        drawSeg(0,      y + h, rightW,  y + h);
      }
    });
    const tex = _canvasTex(canvas);
    _guideTextures.set(key, tex);
    return tex;
  }

  // Guide colour: black, or white when the selected bottle colour is dark.
  // Image-textured variants are sampled (image wins over hex, mirroring the
  // 3D layer rule); hex/swatch is the fallback.
  const _imgLumCache = new Map(); // url → Promise<number|null>
  function _avgImageLuminance(url) {
    if (_imgLumCache.has(url)) return _imgLumCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = c.height = 16;
          const cx = c.getContext('2d');
          cx.drawImage(img, 0, 0, 16, 16);
          const d = cx.getImageData(0, 0, 16, 16).data;
          let sum = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 8) continue;
            sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
            n++;
          }
          resolve(n ? sum / n : null);
        } catch (e) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
    _imgLumCache.set(url, p);
    return p;
  }

  async function guideLineColor() {
    const v = config.bottles[state.bottleProductId]?.variants[state.bottleVariantId];
    const tex = v?.textures?.find(t => t.url) || v?.textures?.find(t => t.hexCode);
    let lum = null;
    if (tex?.url) lum = await _avgImageLuminance(tex.url);
    if (lum == null) {
      const hex = tex?.hexCode
        || (v?.swatchStyle?.match(/background-color:\s*(#[0-9a-fA-F]{6})/) || [])[1];
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (m) {
        const n = parseInt(m[1], 16);
        lum = 0.299 * (n >> 16 & 255) + 0.587 * (n >> 8 & 255) + 0.114 * (n & 255);
      }
    }
    return lum != null && lum < 128 ? '#ffffff' : '#000000';
  }

  // Attach transparent guide mesh per personalization mesh. Updates the
  // texture in place when the guide colour changes with the variant.
  async function ensureGuideOverlays() {
    if (!bottleGroup) return;
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    if (!pers) return;
    const dpi    = pers.dpi || 300;
    const egvCfg = pers.engraving;
    const uvCfg  = pers.color_print;

    // descriptor = { meshName, canvasW_mm, canvasH_mm, rects_mm: [{x, y, w, h}] }
    const descriptors = [];

    (egvCfg?.positions || []).forEach(p => {
      if (!p.mesh_name || !p.width_mm || !p.height_mm) return;
      const maxW = p.max_width_mm  ?? p.width_mm;
      const maxH = p.max_height_mm ?? p.height_mm;
      const offX = (p.width_mm  - maxW) / 2; // horizontally centred safe area
      const offY = alignmentOffsetMm(p.height_mm, maxH, p.alignment ?? 'center');
      descriptors.push({
        meshName: p.mesh_name,
        canvasW_mm: p.width_mm, canvasH_mm: p.height_mm,
        rects_mm: [{ x: offX, y: offY, w: maxW, h: maxH }],
        isWrap: false
      });
    });

    if (uvCfg?.mesh_name && uvCfg.width_mm && uvCfg.height_mm) {
      const rects_mm = (uvCfg.positions || []).map(p => {
        const maxW = p.max_width_mm  ?? uvCfg.width_mm;
        const maxH = p.max_height_mm ?? uvCfg.height_mm;
        const offY = alignmentOffsetMm(uvCfg.height_mm, maxH, p.alignment ?? 'center');
        const cx_mm = uvCfg.width_mm * ((p.degree ?? 0) / 360);
        return { x: cx_mm - maxW / 2, y: offY, w: maxW, h: maxH };
      });
      if (rects_mm.length) {
        descriptors.push({
          meshName: uvCfg.mesh_name,
          canvasW_mm: uvCfg.width_mm, canvasH_mm: uvCfg.height_mm,
          rects_mm,
          isWrap: true
        });
      }
    }

    const guideColor = await guideLineColor();
    descriptors.forEach(d => {
      const original = bottleGroup.getObjectByName(d.meshName);
      if (!original || !original.isMesh) return;
      const canvasW = mmToPx(d.canvasW_mm, dpi);
      const canvasH = mmToPx(d.canvasH_mm, dpi);
      const rects = d.rects_mm.map(r => ({
        x: mmToPx(r.x, dpi), y: mmToPx(r.y, dpi),
        w: mmToPx(r.w, dpi), h: mmToPx(r.h, dpi)
      }));
      const tex = _makeGuideTexture(canvasW, canvasH, rects, guideColor, d.isWrap);
      const existing = original.getObjectByName('__fd_guide');
      if (existing) {
        if (existing.material.map !== tex) {
          existing.material.map = tex;
          existing.material.needsUpdate = true;
        }
        return;
      }
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthTest: false,
        depthWrite: false
      });
      const guideMesh = new THREE.Mesh(original.geometry, mat);
      guideMesh.name = '__fd_guide';
      guideMesh.renderOrder = 99;
      guideMesh.visible = false;
      original.add(guideMesh);
    });
  }

  // Show / hide guides based on Art-tab + current print mode.
  function updateGuideVisibility() {
    if (!bottleGroup) return;
    // Guide visibility is driven by the toggle button — but always off while the
    // order-list (review) modal is open, so the print area never shows there. The
    // active mode still decides WHICH meshes host the guide.
    const show = !ui?.guideHidden && !_reviewOpen && !_successOpen;
    const mode = printState?.mode;
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const egvMeshes = new Set((pers?.engraving?.positions || []).map(p => p.mesh_name).filter(Boolean));
    const uvMesh    = pers?.color_print?.mesh_name;
    bottleGroup.traverse(o => {
      if (o.name !== '__fd_guide') return;
      let visible = false;
      if (show) {
        const hostName = o.parent?.name;
        if      (mode === 'engraving' && egvMeshes.has(hostName)) visible = true;
        else if (mode === 'uv_print'  && hostName === uvMesh)     visible = true;
      }
      o.visible = visible;
    });
  }

  // Threshold loader: show the spinner (and lock ADD TO CART) only if the render
  // actually takes time — i.e. a font/icon has to be fetched + parsed. A cached
  // re-render (text edit, font already loaded, colour change) finishes before the
  // delay, so there's no flash. Re-entrant-safe: each call balances its own toggle.
  async function applyEngravingPreview() {
    let shown = false;
    const t = setTimeout(() => { shown = true; toggleLoading(true); }, 120);
    try {
      return await _renderEngravingPreview();
    } finally {
      clearTimeout(t);
      if (shown) toggleLoading(false);
    }
  }
  let _previewSeq = 0;
  async function _renderEngravingPreview() {
    if (!_layoutOk() && _repairLayout() && printState.mode === 'engraving') _egvRefreshControls();
    const seq        = ++_previewSeq;
    const pers       = config.bottles[state.bottleProductId]?.personalizationSettings;
    const dpi        = pers?.dpi || 300;
    const egvCfg     = pers?.engraving;
    const uvCfg      = pers?.color_print;
    const egvPositions = egvCfg?.positions || [];
    const uvPositions  = uvCfg?.positions  || [];
    const mode = printState.mode;

    // Clear layers for the mode(s) that aren't active.
    if (mode !== 'engraving') {
      egvPositions.forEach(p => {
        if (!p.mesh_name) return;
        patchEngravingLayer(null, p.mesh_name);
        applyMetallicOverlay(p.mesh_name, null);
      });
    }
    if (mode !== 'uv_print' && uvCfg?.mesh_name) {
      patchEngravingLayer(null, uvCfg.mesh_name);
      applyGlossOverlay(uvCfg.mesh_name, null);
    }
    if (mode === 'none') return;

    if (mode === 'uv_print') {
      const ps = printState.uvPrint;
      if (!uvCfg?.width_mm || !uvCfg?.height_mm || !uvCfg.mesh_name) return;
      const cw = mmToPx(uvCfg.width_mm, dpi);
      const ch = mmToPx(uvCfg.height_mm, dpi);
      const canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      // alpha:true explicit so exported PNG keeps transparent BG.
      const ctx = canvas.getContext('2d', { alpha: true });
      ctx.clearRect(0, 0, cw, ch);

      let hasAny = false;
      for (const facePos of uvPositions) {
        const isTextHere = ps.text.trim().length > 0 && ps.textPosition === facePos.key;
        const isIconHere = ps.icon !== null            && ps.iconPosition === facePos.key;
        if (!isTextHere && !isIconHere) continue;
        const centerX = Math.round(cw * (facePos.degree ?? 0) / 360);
        await drawFaceContent(ctx, facePos, isIconHere, isTextHere, ps, 'uv_print', centerX, cw, ch, dpi, pers);
        hasAny = true;
      }

      if (seq !== _previewSeq) return;
      _lastCanvases.uv = hasAny ? { type: 'canvas', canvas, dpi } : null;
      if (hasAny) {
        patchEngravingLayer(_canvasTex(canvas), uvCfg.mesh_name);
        applyGlossOverlay(uvCfg.mesh_name, _whiteMaskTex(canvas, 'Gloss'));
      } else {
        patchEngravingLayer(null, uvCfg.mesh_name);
        applyGlossOverlay(uvCfg.mesh_name, null);
      }
      return;
    }

    // Engraving: { tex, maskTex } per face. Mask derived from same canvas
    // via source-in white so alpha is byte-identical (raster icon and
    // glyph AA edge cases both go away vs. re-rasterizing a fill="white" SVG).
    const ps = printState.engraving;
    const makeEngravingTextures = async (facePos) => {
      if (!facePos) return null;
      const hasText = ps.text.trim().length > 0 && ps.textPosition === facePos.key;
      const hasIcon = ps.icon !== null            && ps.iconPosition === facePos.key;
      if (!hasText && !hasIcon && !(ps.badge && ps.badgePosition === facePos.key)) return null;
      const [cw, ch] = getEngravingCanvasSize(facePos.width_mm, facePos.height_mm, dpi);

      const svgStr = await buildEngravingSVG(facePos, hasIcon, hasText, ps, cw, ch, dpi, pers);
      _lastCanvases[facePos.key] = { type: 'svg', svg: svgStr, widthMm: facePos.width_mm, heightMm: facePos.height_mm };

      let canvas;
      try {
        canvas = await svgToCanvas(_stripKitText(svgStr), cw, ch);
        await _overlayBadgeKit(canvas.getContext('2d'), ps, facePos, egvCfg, dpi, cw / 2);
      } catch (e) {
        console.warn('[FlexiDesign] SVG rasterization failed, using canvas fallback:', e);
        canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, cw, ch);
        await drawFaceContent(ctx, facePos, hasIcon, hasText, ps, 'engraving', cw / 2, cw, ch, dpi, pers);
      }
      return { tex: _canvasTex(canvas), maskTex: _whiteMaskTex(canvas, 'Metallic') };
    };

    for (const facePos of egvPositions) {
      if (!facePos.mesh_name) continue;
      const result = await makeEngravingTextures(facePos);
      if (seq !== _previewSeq) return;
      patchEngravingLayer(result?.tex     || null, facePos.mesh_name);
      applyMetallicOverlay(facePos.mesh_name, result?.maskTex || null);
    }

    syncSliderBoundsFromState();
  }

  function syncSliderBoundsFromState() {
    const cur = artCurrentPs();
    if (!cur || printState.mode !== 'engraving') return;
    const pers    = config.bottles[state.bottleProductId]?.personalizationSettings;
    const modeCfg = getModeCfg(pers, 'engraving');
    if (!modeCfg) return;

    const updateOne = (which, faceKey, stateKey) => {
      const face = getFaceCfg(pers, 'engraving', faceKey);
      if (!face) return;
      const cfg = getSliderConfig('engraving', face, modeCfg, stateKey, cur);
      if (cfg.max <= cfg.min) return;
      // Re-clamp into intrinsic range (catches vertical-orient flip).
      const myHmm = stateKey === 'textY_mm'
        ? getTextHmm('engraving', modeCfg, cur.textOrientation, face)
        : stateKey === 'badgeY_mm' ? getBadgeHmm(modeCfg) : (getIconHmm(modeCfg));
      const intrinsic = ySliderRangeMm('engraving', face, modeCfg, myHmm);
      if (cur[stateKey] != null && intrinsic.maxMm > intrinsic.minMm) {
        cur[stateKey] = clampMm(cur[stateKey], intrinsic.minMm, intrinsic.maxMm);
      }
      document.querySelectorAll(`#shopify-section-${SID} .fd-y-slider[data-which="${which}"]`).forEach(s => {
        s.min = cfg.min.toFixed(2);
        s.max = cfg.max.toFixed(2);
        // Sync non-active thumb only — active is synced inline by bindYSliders.
        if (document.activeElement !== s && cur[stateKey] != null) {
          s.value = cur[stateKey].toFixed(2);
        }
      });
    };

    updateOne('text', cur.textPosition, 'textY_mm');
    updateOne('icon', cur.iconPosition, 'iconY_mm');
    if (cur.badge) updateOne('badge', cur.badgePosition, 'badgeY_mm');
  }

  // Thin wrapper kept under its legacy name so handlePrintAction() keeps working.
  // Delegates to the new Art renderer (which covers both desktop pane and mobile popover).
  function renderPrintsPanel() {
    if (_skipPrintRender) return;
    renderArtPane();
    if (ui.mobilePopover.open && ui.mobilePopover.tab === 'art') renderPopover();
    refreshPurchaseBar();
  }

  // In-place DOM updates to avoid pane re-renders.
  const _q = sel => document.querySelectorAll(`#shopify-section-${SID} ${sel}`);
  const _swap = (sel, pred) => _q(sel).forEach(el => el.classList.toggle('is-active', pred(el)));
  const _txt = (sel, t) => _q(sel).forEach(el => { el.textContent = t; });
  const _chk = (sel, on) => _q(sel).forEach(el => el.classList.toggle('is-on', on));
  // Enable/disable the Position sub-pill in place (text typing / icon pick must
  // not re-render the popover body — that would drop input focus).
  const _syncArtPosPill = () => {
    const hasAny = artHasText() || artHasIcon() || artHasBadge();
    _q('.fd-art-subtoggle-btn[data-action="art-goto-position"]').forEach(b => { b.disabled = !hasAny; });
  };
  const _iconBtnHtml = (ic, activeName) =>
    `<button type="button" class="fd-icon-btn${ic.name === activeName ? ' is-active' : ''}"${_artBlocked('icon') ? ' disabled' : ''} data-print-action="set-icon" data-icon-name="${encodeURIComponent(ic.name)}" title="${escapeAttr(ic.name)}"><img src="${escapeAttr(ic.previewIcon)}" alt="${escapeAttr(ic.name)}" loading="lazy"></button>`;

  // Face-dependent control HTML: returns '' when the face can't host the control.
  // Used by initial render AND by surgical face-change updates so the source of
  // truth for visibility is identical.
  function _placementHtml(which) {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const mode = printState.mode;
    const modeCfg = getModeCfg(pers, mode);
    const ps = artCurrentPs();
    if (!ps || !modeCfg) return '';
    if (which === 'text' && mode !== 'engraving') return '';
    if (which === 'badge' && (mode !== 'engraving' || !ps.badge)) return '';
    const [posKey, stateKey] = _EGV_EL[which];
    const faceKey  = ps[posKey];
    const faceCfg  = getFaceCfg(pers, mode, faceKey);
    if (!faceCfg) return '';
    const cfg = getSliderConfig(mode, faceCfg, modeCfg, stateKey, ps);
    if (cfg.max <= cfg.min) return '';
    const curMm = ps[stateKey] != null ? clampMm(ps[stateKey], cfg.min, cfg.max) : (cfg.min + cfg.max) / 2;
    // Ticks at the knob's centre positions (2px block padding + 8px radius =
    // 10px inset), matching the 0/25/50/75/100% magnet snaps in bindYSliders.
    const ticks = [0, 0.25, 0.5, 0.75, 1].map(p =>
      `<span class="fd-vplace__tick" style="top: calc(10px + (100% - 20px) * ${p})"></span>`
    ).join('');
    return `<div class="fd-position-group fd-vplace" data-placement="${which}">
      <span class="fd-vplace__label">Vertical Adjustment</span>
      <div class="fd-vplace__track">
        ${ticks}
        <input type="range" class="fd-y-slider"
               min="${cfg.min.toFixed(2)}" max="${cfg.max.toFixed(2)}" step="0.1"
               value="${curMm.toFixed(2)}"
               data-print-action="set-${which}-y" data-which="${which}">
      </div>
      <span class="fd-vplace__hint"><span class="fd-vplace__hint-txt">click &amp; drag</span>${(config.fallbackIcons || {}).switchVertical || ''}</span>
    </div>`;
  }
  function _orientationHtml() {
    if (printState.mode !== 'engraving') return '';
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const modeCfg = getModeCfg(pers, 'engraving');
    const ps = artCurrentPs();
    if (!ps || !modeCfg) return '';
    const tFace = getFaceCfg(pers, 'engraving', ps.textPosition);
    if (!tFace || !canFitVerticalText(tFace, modeCfg)) return '';
    const iconOnSame = ps.icon != null && ps.iconPosition === ps.textPosition;
    const vDisabled  = (iconOnSame && !canFitVerticalTextAndIcon(tFace, modeCfg)) || !_egvVerticalFits(ps, modeCfg, tFace);
    return `<div class="fd-position-group" data-orientation-group>
      <div class="fd-position-group__label">Orientation</div>
      <div class="fd-position-btns">
        <button type="button" class="fd-position-btn${ps.textOrientation !== 'vertical' ? ' is-active' : ''}"
                data-print-action="set-text-orientation" data-orientation="horizontal">Horizontal</button>
        <button type="button" class="fd-position-btn${ps.textOrientation === 'vertical' ? ' is-active' : ''}${vDisabled ? ' is-disabled' : ''}"
                ${vDisabled ? 'disabled' : ''}
                data-print-action="set-text-orientation" data-orientation="vertical">Vertical</button>
      </div>
    </div>`;
  }

  // Surgical add / replace / remove of a face-dependent control inside each
  // art-card body (covers desktop pane + mobile popover step view).
  function _updatePlacement(which) {
    const html = _placementHtml(which);
    _q(`.fd-art-card[data-art="${which}"] .fd-art-card__body`).forEach(body => {
      const existing = body.querySelector(`[data-placement="${which}"]`);
      if (html) {
        if (existing) existing.outerHTML = html;
        else          body.insertAdjacentHTML('beforeend', html);
        const fresh = body.querySelector(`[data-placement="${which}"]`);
        if (fresh) bindYSliders(fresh);
      } else if (existing) {
        existing.remove();
      }
    });
  }
  function _updateOrientation() {
    const html = _orientationHtml();
    _q('.fd-art-card[data-art="text"] .fd-art-card__body').forEach(body => {
      const existing = body.querySelector('[data-orientation-group]');
      if (html) {
        if (existing) existing.outerHTML = html;
        else {
          // Insert before placement if present; otherwise append.
          const placement = body.querySelector('[data-placement="text"]');
          if (placement) placement.insertAdjacentHTML('beforebegin', html);
          else          body.insertAdjacentHTML('beforeend', html);
        }
      } else if (existing) {
        existing.remove();
      }
    });
  }

  // Personalization schema helpers (engraving / color_print, positions[]).
  function getModeCfg(pers, mode) {
    if (mode === 'engraving') return pers?.engraving   ?? null;
    if (mode === 'uv_print')  return pers?.color_print ?? null;
    return null;
  }
  function getPositions(pers, mode) {
    return getModeCfg(pers, mode)?.positions ?? [];
  }
  function getFaceCfg(pers, mode, key) {
    if (key == null) return null;
    return getPositions(pers, mode).find(p => p.key === key) ?? null;
  }
  // First position whose key !== currentKey — used by UV face exclusivity auto-swap.
  function getOtherPositionKey(pers, mode, currentKey) {
    return getPositions(pers, mode).find(p => p.key !== currentKey)?.key ?? null;
  }
  // The position flagged default:true in the metaobject (at most one), else the
  // first position. Drives where a fresh text/icon starts and the camera's first
  // Art framing. Existing rules still apply on top (e.g. UV face exclusivity).
  function getDefaultPositionKey(pers, mode) {
    const positions = getPositions(pers, mode);
    return (positions.find(p => p.default)?.key) ?? positions[0]?.key ?? null;
  }
  function clampMm(v, lo, hi) {
    if (lo > hi) return lo;
    return Math.max(lo, Math.min(hi, v));
  }
  // How far down (mm) the safe area sits within its container (mesh / wrap canvas).
  function alignmentOffsetMm(containerHmm, safeHmm, alignment) {
    const slack = (containerHmm || 0) - (safeHmm || 0);
    if (slack <= 0) return 0;
    if (alignment === 'top')    return 0;
    if (alignment === 'bottom') return slack;
    return slack / 2; // center (default)
  }
  // Vertical safe-area bounds (mm) for a given face within its container.
  // Engraving: the face object carries its own height_mm.
  // UV print:  the container is the shared wrap canvas at modeCfg.height_mm.
  function safeAreaYMm(mode, faceCfg, modeCfg) {
    if (!faceCfg) return { topMm: 0, bottomMm: 0 };
    const containerH = mode === 'uv_print' ? (modeCfg?.height_mm ?? 0) : (faceCfg.height_mm ?? 0);
    const maxH       = faceCfg.max_height_mm ?? containerH;
    const offset     = alignmentOffsetMm(containerH, maxH, faceCfg.alignment ?? 'center');
    return { topMm: offset, bottomMm: offset + maxH };
  }
  // Y slider range (mm) for a given element on a face. Returns the element's
  // centre-Y min/max within the safe area, accounting for its own height.
  function ySliderRangeMm(mode, faceCfg, modeCfg, elemHmm) {
    const { topMm, bottomMm } = safeAreaYMm(mode, faceCfg, modeCfg);
    const halfH = (elemHmm || 0) / 2;
    return { minMm: topMm + halfH, maxMm: bottomMm - halfH };
  }
  // Text height in mm for layout. Vertical engraving → face.max_width_mm.
  function getTextHmm(mode, modeCfg, orientation, faceCfg) {
    if (mode === 'engraving' && orientation === 'vertical') {
      return faceCfg?.max_width_mm ?? modeCfg?.text_height_mm ?? 0;
    }
    return modeCfg?.text_height_mm ?? 0;
  }

  // Whether the vertical-text BOX fits inside a face's safe area.
  // Vertical text box (post-rotate): width = text_height_mm, height = max_width_mm.
  // Safe area:                       width = max_width_mm,   height = max_height_mm.
  function canFitVerticalText(faceCfg, modeCfg) {
    const maxW  = faceCfg?.max_width_mm  ?? 0;
    const maxH  = faceCfg?.max_height_mm ?? 0;
    const textW = modeCfg?.text_height_mm ?? 0;
    return textW > 0 && maxW > 0 && maxH > 0
        && textW <= maxW
        && maxW  <= maxH;
  }

  // Whether the vertical text box AND the icon, stacked with gap, would
  // fit on the face's safe area. Pure face-level check — does NOT consider
  // current element positions.
  function canFitVerticalTextAndIcon(faceCfg, modeCfg) {
    const maxH      = faceCfg?.max_height_mm ?? 0;
    const verticalH = faceCfg?.max_width_mm  ?? 0;
    const iconH     = getIconHmm(modeCfg);
    const gap       = modeCfg?.gap_height_mm  ?? 0;
    return verticalH > 0 && iconH > 0 && maxH > 0
        && (verticalH + gap + iconH) <= maxH;
  }

  // Face can hold both text + icon in either orientation. Used by swap logic.
  function canFitBothOnFace(faceCfg, modeCfg) {
    const maxH      = faceCfg?.max_height_mm ?? 0;
    const iconH     = getIconHmm(modeCfg);
    const gap       = modeCfg?.gap_height_mm  ?? 0;
    const textH     = modeCfg?.text_height_mm ?? 0; // horizontal text height
    const verticalH = faceCfg?.max_width_mm  ?? 0;  // vertical text height
    if (maxH <= 0 || iconH <= 0) return false;
    return ((textH > 0     && textH     + gap + iconH <= maxH))
        || ((verticalH > 0 && verticalH + gap + iconH <= maxH));
  }

  // Engraving: face safe area tall enough for the icon box (icon_height_mm — the
  // fixed L box S/M render inside). Shorter faces are text-only.
  function canFitIconOnFace(faceCfg, modeCfg) {
    if (!faceCfg) return false;
    const boxH = Number(modeCfg?.icon_height_mm) || 0;
    if (boxH <= 0) return true;
    const { topMm, bottomMm } = safeAreaYMm('engraving', faceCfg, modeCfg);
    return bottomMm - topMm >= boxH;
  }
  // Engraving icon face: preferredKey if it can host the icon, else the default
  // icon-capable face, else the first. null when no face can host it.
  function getIconFaceKey(pers, preferredKey) {
    const modeCfg = getModeCfg(pers, 'engraving');
    const fits = getPositions(pers, 'engraving').filter(p => canFitIconOnFace(p, modeCfg));
    return (fits.find(p => p.key === preferredKey) || fits.find(p => p.default) || fits[0])?.key ?? null;
  }

  // Vertical-text flip layout. Preserves top/bottom order. Tries to fit
  // text on its side (icon stays); else pushes icon away; else extremes.
  function placeTextAndIconForVertical(ps, faceCfg, modeCfg) {
    const verticalH = faceCfg.max_width_mm  ?? 0;
    const iconH    = getIconHmm(modeCfg);
    const gap      = modeCfg.gap_height_mm  ?? 0;
    const safe     = safeAreaYMm('engraving', faceCfg, modeCfg);
    const textLo   = safe.topMm    + verticalH / 2;
    const textHi   = safe.bottomMm - verticalH / 2;
    const iconLo   = safe.topMm    + iconH / 2;
    const iconHi   = safe.bottomMm - iconH / 2;

    const originalTextY = ps.textY_mm;
    let desiredTextY    = originalTextY != null ? originalTextY : (textLo + textHi) / 2;
    desiredTextY        = clampMm(desiredTextY, textLo, textHi);

    const iconOnSame = ps.icon != null && ps.iconPosition === ps.textPosition && ps.iconY_mm != null;
    if (!iconOnSame) { ps.textY_mm = desiredTextY; return; }

    const iconY = ps.iconY_mm;
    // Use PRE-clamp text Y so side preservation survives vertical clamping.
    const sideAnchor   = originalTextY != null ? originalTextY : desiredTextY;
    const textWasAbove = sideAnchor < iconY;

    if (textWasAbove) {
      // Vertical text should stay above the icon.
      const tAboveHi = Math.min(iconY - iconH/2 - gap - verticalH/2, textHi);
      if (textLo <= tAboveHi) {
        // Fits above icon — slide text to the closest valid Y, icon
        // unchanged.
        ps.textY_mm = clampMm(desiredTextY, textLo, tAboveHi);
        return;
      }
      // No room above icon for vertical text. Push icon DOWN to keep
      // text on top.
      const iconLoForPush = desiredTextY + verticalH/2 + gap + iconH/2;
      if (iconLoForPush <= iconHi) {
        ps.textY_mm = desiredTextY;
        ps.iconY_mm = clampMm(iconY, iconLoForPush, iconHi);
        return;
      }
    } else {
      // Vertical text should stay below the icon.
      const tBelowLo = Math.max(iconY + iconH/2 + gap + verticalH/2, textLo);
      if (tBelowLo <= textHi) {
        ps.textY_mm = clampMm(desiredTextY, tBelowLo, textHi);
        return;
      }
      // No room below icon for vertical text. Push icon UP.
      const iconHiForPush = desiredTextY - verticalH/2 - gap - iconH/2;
      if (iconLo <= iconHiForPush) {
        ps.textY_mm = desiredTextY;
        ps.iconY_mm = clampMm(iconY, iconLo, iconHiForPush);
        return;
      }
    }

    // Extreme fallback: even side-preserving push can't fit. Lay out
    // from safe top. Only reachable when the face barely fits both
    // (button-enabled precondition still guarantees they fit *somehow*).
    ps.textY_mm = textLo;
    ps.iconY_mm = clampMm(textLo + verticalH/2 + gap + iconH/2, iconLo, iconHi);
  }

  // Whether the current text/icon configuration allows vertical text.
  function isVerticalAllowed(ps, pers, mode) {
    if (mode !== 'engraving') return false;
    const modeCfg = getModeCfg(pers, mode);
    const tFace   = getFaceCfg(pers, mode, ps.textPosition);
    if (!modeCfg || !tFace) return false;
    if (!canFitVerticalText(tFace, modeCfg)) return false;
    const iconOnSameFace = ps.icon != null && ps.iconPosition === ps.textPosition;
    if (iconOnSameFace && !canFitVerticalTextAndIcon(tFace, modeCfg)) return false;
    return _egvVerticalFits(ps, modeCfg, tFace);
  }
  // With a Badge design on the text face, vertical text + everything there must still stack.
  function _egvVerticalFits(ps, modeCfg, tFace) {
    if (!ps.badge || ps.badgePosition !== ps.textPosition) return true;
    const v = { ...ps, textOrientation: 'vertical' };
    return _egvFits(v, modeCfg, tFace, [..._egvOnFace(v, ps.textPosition, 'text'), 'text']);
  }

  // { min, max, myHmm, otherHmm }. Overlap handled by placeForSameFaceDrag.
  function getSliderConfig(mode, faceCfg, modeCfg, myKey, ps) {
    const isText   = myKey === 'textY_mm';
    const textHmm  = getTextHmm(mode, modeCfg, ps.textOrientation, faceCfg);
    const iconHmm  = getIconHmm(modeCfg);
    const myHmm    = myKey === 'badgeY_mm' ? getBadgeHmm(modeCfg) : isText ? textHmm : iconHmm;
    const otherHmm = isText ? iconHmm : textHmm;
    const range    = ySliderRangeMm(mode, faceCfg, modeCfg, myHmm);
    return { min: range.minMm, max: range.maxMm, myHmm, otherHmm };
  }

  // mm of overshoot past touch boundary before swap fires (detent).
  const _ART_SNAP_BREAK_MM = 2;

  // Same-face slider placement. Snap detent at touch point T; past
  // _ART_SNAP_BREAK_MM, the other element switches to the side the
  // dragged came from. otherY=null when no same-face constraint
  // applies. See AGENTS / earlier turns for the full ruleset.
  function placeForSameFaceDrag(ps, modeCfg, faceCfg, dragged, intentY) {
    const isText    = dragged === 'text';
    const myKey     = isText ? 'textY_mm' : 'iconY_mm';
    const otherKey  = isText ? 'iconY_mm' : 'textY_mm';
    const myHmm     = isText ? getTextHmm('engraving', modeCfg, ps.textOrientation, faceCfg)
                             : (getIconHmm(modeCfg));
    const otherHmm  = isText ? (getIconHmm(modeCfg))
                             : getTextHmm('engraving', modeCfg, ps.textOrientation, faceCfg);
    const range     = ySliderRangeMm('engraving', faceCfg, modeCfg, myHmm);
    const myYClamped = clampMm(intentY, range.minMm, range.maxMm);

    const samePos = ps.textPosition && ps.textPosition === ps.iconPosition;
    const hasText = (ps.text || '').trim().length > 0;
    const hasIcon = ps.icon != null;
    if (!samePos || !hasText || !hasIcon) return { myY: myYClamped, otherY: null };
    const otherY = ps[otherKey];
    if (otherY == null) return { myY: myYClamped, otherY: null };

    const gapMm  = modeCfg?.gap_height_mm ?? 0;
    const minGap = myHmm/2 + gapMm + otherHmm/2;

    // No overlap at intent → other stays, dragged commits at intent.
    if (Math.abs(myYClamped - otherY) >= minGap) {
      return { myY: myYClamped, otherY };
    }

    // Side rule: other switches to where dragged came FROM (use pre-update Y).
    const prevDragged = ps[myKey];
    const draggedWasAbove = prevDragged != null && prevDragged !== otherY
      ? prevDragged < otherY
      : myYClamped < otherY;

    // T = touch position. Snap detent pins dragged here within break range.
    const T = draggedWasAbove ? otherY - minGap : otherY + minGap;
    const overshoot = draggedWasAbove ? (myYClamped - T) : (T - myYClamped);
    if (overshoot < _ART_SNAP_BREAK_MM) {
      return { myY: clampMm(T, range.minMm, range.maxMm), otherY };
    }

    // Past break → swap if fits, else hard-stop at T (no icon push during drag).
    const tentativeOther = draggedWasAbove ? myYClamped - minGap
                                           : myYClamped + minGap;
    const safe     = safeAreaYMm('engraving', faceCfg, modeCfg);
    const otherMin = safe.topMm    + otherHmm/2;
    const otherMax = safe.bottomMm - otherHmm/2;

    if (tentativeOther >= otherMin && tentativeOther <= otherMax) {
      return { myY: myYClamped, otherY: tentativeOther };
    }

    // Swap side can't fit the other element. Hard-pin dragged at T,
    // leave other untouched.
    return { myY: clampMm(T, range.minMm, range.maxMm), otherY };
  }

  // Engraving-only. 2nd element on same face: if face fits both, push
  // the existing one only when it blocks the new; if face can't fit
  // both, move the existing to another face. UV print skips (face exclusivity).
  function avoidOverlapOnAdd(ps, mode, pers, added) {
    if (mode !== 'engraving') return;
    if (ps.badge) { _egvArrange(ps, pers, added, ps[_EGV_EL[added][1]]); return; }
    const hasText = (ps.text || '').trim().length > 0;
    const hasIcon = ps.icon != null;
    if (!hasText || !hasIcon) return;
    if (!ps.textPosition || ps.textPosition !== ps.iconPosition) return;
    const modeCfg = getModeCfg(pers, mode);
    const faceCfg = getFaceCfg(pers, mode, ps.textPosition);
    if (!modeCfg || !faceCfg) return;

    const isText      = added === 'text';
    const newKey      = isText ? 'textY_mm'     : 'iconY_mm';
    const otherKey    = isText ? 'iconY_mm'     : 'textY_mm';
    const otherPosKey = isText ? 'iconPosition' : 'textPosition';
    const newHmm      = isText
      ? getTextHmm(mode, modeCfg, ps.textOrientation, faceCfg)
      : (getIconHmm(modeCfg));
    const otherHmm    = isText
      ? (getIconHmm(modeCfg))
      : getTextHmm(mode, modeCfg, ps.textOrientation, faceCfg);
    const gapMm    = modeCfg.gap_height_mm ?? 0;
    const newRange = ySliderRangeMm(mode, faceCfg, modeCfg, newHmm);

    // A: can't fit both → move existing to another face.
    if (!canFitBothOnFace(faceCfg, modeCfg)) {
      const altKey  = getOtherPositionKey(pers, mode, ps.textPosition);
      // The icon can't move to a text-only face — move the new text there instead.
      if (altKey && isText && !canFitIconOnFace(getFaceCfg(pers, mode, altKey), modeCfg)) {
        const altSafe = safeAreaYMm(mode, getFaceCfg(pers, mode, altKey), modeCfg);
        ps.textPosition = altKey;
        ps.textY_mm     = (altSafe.topMm + altSafe.bottomMm) / 2;
        return;
      }
      if (altKey) {
        ps[otherPosKey] = altKey;
        const altFace = getFaceCfg(pers, mode, altKey);
        if (altFace) {
          const altRange = ySliderRangeMm(mode, altFace, modeCfg, otherHmm);
          if (altRange.maxMm >= altRange.minMm) {
            ps[otherKey] = (altRange.minMm + altRange.maxMm) / 2;
          }
        }
      }
      ps[newKey] = (newRange.minMm + newRange.maxMm) / 2;
      return;
    }

    // B: face fits both.
    const otherY = ps[otherKey];
    if (otherY == null) {
      ps[newKey] = (newRange.minMm + newRange.maxMm) / 2;
      return;
    }
    const centerY = (newRange.minMm + newRange.maxMm) / 2;
    const blockLo = otherY - otherHmm/2 - gapMm - newHmm/2;
    const blockHi = otherY + otherHmm/2 + gapMm + newHmm/2;

    // B1: centre is clear.
    if (centerY <= blockLo || centerY >= blockHi) {
      ps[newKey] = centerY;
      return;
    }

    // B2: nearest free edge fits.
    const aboveFits = blockLo >= newRange.minMm;
    const belowFits = blockHi <= newRange.maxMm;
    if (aboveFits || belowFits) {
      ps[newKey] = (aboveFits && belowFits)
        ? (Math.abs(centerY - blockLo) <= Math.abs(centerY - blockHi) ? blockLo : blockHi)
        : (aboveFits ? blockLo : blockHi);
      return;
    }

    // B3: tight face — push existing to its boundary, stack new opposite.
    // Tie-break (otherY === centerY) → existing keeps the top.
    const otherRange = ySliderRangeMm(mode, faceCfg, modeCfg, otherHmm);
    const tryStack = (otherAtTop) => {
      const oY = otherAtTop ? otherRange.minMm : otherRange.maxMm;
      const nY = otherAtTop
        ? oY + otherHmm/2 + gapMm + newHmm/2
        : oY - otherHmm/2 - gapMm - newHmm/2;
      return (nY >= newRange.minMm && nY <= newRange.maxMm) ? { oY, nY } : null;
    };
    const otherWasAbove = otherY <= centerY;
    const arrangement = (otherWasAbove ? tryStack(true)  : tryStack(false))
                     || (otherWasAbove ? tryStack(false) : tryStack(true));
    if (arrangement) {
      ps[otherKey] = arrangement.oY;
      ps[newKey]   = arrangement.nY;
    }
    // else: doesn't fit in current orientation → caller's revert handles it.
  }

  // UV print keeps text & icon on opposite faces. With only one face configured
  // there's no opposite slot, so the two become mutually exclusive: adding /
  // selecting one removes the other (latest wins) instead of overlapping.
  // `added` is the element being kept. Returns true when it removed the other.
  function enforceUvSingleFaceExclusivity(ps, mode, pers, added) {
    if (mode !== 'uv_print') return false;
    if (getPositions(pers, mode).length >= 2) return false;
    const hasText = (ps.text || '').trim().length > 0;
    const hasIcon = ps.icon != null;
    if (!hasText || !hasIcon) return false;
    if (added === 'text') ps.icon = null;
    else                  ps.text = '';
    return true;
  }

  // Engraving layout once a Badge design is placed: the same fit / gap /
  // no-overlap / move-to-other-face / drag snap+swap rules as text↔icon, but for
  // any number of elements. Without a Badge design the pairwise code runs as before.
  const _EGV_EL = { text: ['textPosition', 'textY_mm'], icon: ['iconPosition', 'iconY_mm'], badge: ['badgePosition', 'badgeY_mm'] };
  const _egvHas = (ps, k) => k === 'text' ? (ps.text || '').trim().length > 0 : k === 'icon' ? ps.icon != null : !!ps.badge;
  const _egvH = (ps, k, modeCfg, face) => k === 'text' ? getTextHmm('engraving', modeCfg, ps.textOrientation, face)
    : k === 'icon' ? getIconHmm(modeCfg) : getBadgeHmm(modeCfg);
  const _egvOnFace = (ps, key, except) => Object.keys(_EGV_EL).filter(k => k !== except && _egvHas(ps, k) && ps[_EGV_EL[k][0]] === key);
  const _egvSnap = ps => Object.values(_EGV_EL).flat().reduce((o, f) => (o[f] = ps[f], o), { textOrientation: ps.textOrientation });
  // Badge designs use the icon box at full (L) size — max_width_mm × icon_height_mm,
  // aspect-fitted, so never distorted — and ignore the icon's S/M/L choice.
  function getBadgeHmm(modeCfg) { return Number(modeCfg?.icon_height_mm) || 0; }
  function _badgeBoxMm(faceCfg, modeCfg) { return { w: Number(faceCfg?.max_width_mm) || 0, h: getBadgeHmm(modeCfg) }; }
  function canFitBadgeOnFace(faceCfg, modeCfg) {
    return !!faceCfg && (faceCfg.max_width_mm ?? 0) > 0 && canFitIconOnFace(faceCfg, modeCfg);
  }
  function _egvFits(ps, modeCfg, face, keys) {
    if (keys.includes('badge') && !canFitBadgeOnFace(face, modeCfg)) return false;
    if (keys.includes('icon') && !canFitIconOnFace(face, modeCfg)) return false;
    const { topMm, bottomMm } = safeAreaYMm('engraving', face, modeCfg);
    const gap = modeCfg?.gap_height_mm ?? 0;
    return keys.reduce((s, k) => s + _egvH(ps, k, modeCfg, face), 0) + gap * (keys.length - 1) <= bottomMm - topMm + 1e-6;
  }
  // k nearest wantY (default centre) without moving the others; else stack all in their current order.
  function _egvPlace(ps, modeCfg, face, k, others, wantY = null) {
    const gap = modeCfg?.gap_height_mm ?? 0;
    const yk  = _EGV_EL[k][1];
    const hk  = _egvH(ps, k, modeCfg, face);
    const r   = ySliderRangeMm('engraving', face, modeCfg, hk);
    const want = wantY != null ? clampMm(wantY, r.minMm, r.maxMm) : (r.minMm + r.maxMm) / 2;
    const blocks = others.map(o => {
      const h = _egvH(ps, o, modeCfg, face), y = ps[_EGV_EL[o][1]];
      return [y - h / 2 - gap - hk / 2, y + h / 2 + gap + hk / 2];
    });
    const free = y => y >= r.minMm - 1e-6 && y <= r.maxMm + 1e-6 && blocks.every(([lo, hi]) => y <= lo + 1e-6 || y >= hi - 1e-6);
    const best = [want, ...blocks.flat()].filter(free).sort((a, b) => Math.abs(a - want) - Math.abs(b - want))[0];
    if (best != null) { ps[yk] = best; return; }
    const all = [...others, k].map(e => ({ e, h: _egvH(ps, e, modeCfg, face), y: e === k ? want : ps[_EGV_EL[e][1]] }))
      .sort((a, b) => a.y - b.y);
    const { topMm, bottomMm } = safeAreaYMm('engraving', face, modeCfg);
    let edge = topMm;
    for (const a of all) { a.y = Math.max(a.y, edge + a.h / 2); edge = a.y + a.h / 2 + gap; }
    edge = bottomMm;
    for (let i = all.length - 1; i >= 0; i--) { const a = all[i]; a.y = Math.min(a.y, edge - a.h / 2); edge = a.y - a.h / 2 - gap; }
    all.forEach(a => { ps[_EGV_EL[a.e][1]] = a.y; });
  }
  // Settle k on its face. Face too small for everything → move the fewest others to a
  // face that can take them; failing that (selfMove) move k instead. false = no valid layout.
  function _egvArrange(ps, pers, k, wantY = null, selfMove = true) {
    const modeCfg = getModeCfg(pers, 'engraving');
    const key  = ps[_EGV_EL[k][0]];
    const face = getFaceCfg(pers, 'engraving', key);
    if (!modeCfg || !face) return false;
    const others = _egvOnFace(ps, key, k);
    const relocate = (e) => {
      const alt = getPositions(pers, 'engraving')
        .find(p => p.key !== key && _egvFits(ps, modeCfg, p, [..._egvOnFace(ps, p.key, e), e]));
      if (!alt) return false;
      ps[_EGV_EL[e][0]] = alt.key;
      _egvPlace(ps, modeCfg, alt, e, _egvOnFace(ps, alt.key, e));
      return true;
    };
    for (const off of [[], ...others.map(o => [o]), ...(others.length > 1 ? [others] : [])]) {
      if (!_egvFits(ps, modeCfg, face, [...others.filter(o => !off.includes(o)), k])) continue;
      const snap = _egvSnap(ps);
      if (off.every(relocate)) {
        _egvPlace(ps, modeCfg, face, k, _egvOnFace(ps, key, k), wantY);
        return true;
      }
      Object.assign(ps, snap);
    }
    return selfMove && relocate(k);
  }
  // Position change for any element while a Badge design is placed. Refused when
  // the target face can't hold the element at all or no valid layout exists.
  function _egvMoveTo(ps, pers, k, key) {
    const modeCfg = getModeCfg(pers, 'engraving');
    if (!_egvFits(ps, modeCfg, getFaceCfg(pers, 'engraving', key), [k])) return;
    const snap = _egvSnap(ps);
    ps[_EGV_EL[k][0]] = key;
    if (ps.textOrientation === 'vertical' && !isVerticalAllowed(ps, pers, 'engraving')) ps.textOrientation = 'horizontal';
    if (!_egvArrange(ps, pers, k, null, false)) { Object.assign(ps, snap); return; }
    _egvRefreshControls();
    applyEngravingPreview();
    rotateArtCameraForPositionChange(k, key);
  }
  function _egvRefreshControls() {
    const ps = printState.engraving;
    Object.entries(_EGV_EL).forEach(([k, [pk]]) => _swap(`[data-print-action="set-${k}-position"]`, el => el.dataset.position === ps[pk]));
    _updateOrientation();
    ['text', 'icon', 'badge'].forEach(_updatePlacement);
  }
  // Same-face drag: free → commit; within the snap detent → pin at the touch point;
  // past it → the blocker swaps to the side k came from, if that slot is free.
  function _egvDrag(ps, pers, k, intent) {
    const modeCfg = getModeCfg(pers, 'engraving');
    const [pk, yk] = _EGV_EL[k];
    const face = getFaceCfg(pers, 'engraving', ps[pk]);
    if (!modeCfg || !face) return;
    const gap = modeCfg.gap_height_mm ?? 0;
    const h   = e => _egvH(ps, e, modeCfg, face);
    const y   = e => ps[_EGV_EL[e][1]];
    const r   = ySliderRangeMm('engraving', face, modeCfg, h(k));
    const my  = clampMm(intent, r.minMm, r.maxMm);
    const others = _egvOnFace(ps, ps[pk], k).filter(o => y(o) != null);
    const dist = (a, b) => h(a) / 2 + gap + h(b) / 2;
    const clearAt = (yy, e, skip) => others.every(o => o === e || o === skip || Math.abs(yy - y(o)) >= dist(e, o) - 1e-6);
    if (clearAt(my, k)) { ps[yk] = my; return; }
    const b  = others.filter(o => Math.abs(my - y(o)) < dist(k, o)).sort((p, q) => Math.abs(my - y(p)) - Math.abs(my - y(q)))[0];
    const yb = y(b);
    const prev = ps[yk];
    const wasAbove = prev != null && prev !== yb ? prev < yb : my < yb;
    const T  = clampMm(wasAbove ? yb - dist(k, b) : yb + dist(k, b), r.minMm, r.maxMm);
    const pin = () => { if (clearAt(T, k)) ps[yk] = T; };
    if ((wasAbove ? my - T : T - my) < _ART_SNAP_BREAK_MM) return pin();
    const rb = ySliderRangeMm('engraving', face, modeCfg, h(b));
    const nb = wasAbove ? my - dist(k, b) : my + dist(k, b);
    if (nb >= rb.minMm && nb <= rb.maxMm && clearAt(my, k, b) && clearAt(nb, b, k)) { ps[yk] = my; ps[_EGV_EL[b][1]] = nb; return; }
    pin();
  }

  // Hard layout rule, every print mode: each element fully inside its face's safe
  // area (as rendered — Y clamped into range) and never overlapping another, gap
  // kept. Checked after every change; a broken layout is re-arranged, else undone.
  const _layoutSnap = () => ({ mode: printState.mode, engraving: { ...printState.engraving }, uvPrint: { ...printState.uvPrint } });
  function _layoutOk() {
    const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
    const mode = printState.mode;
    const mc   = getModeCfg(pers, mode);
    const fits = (face, h) => { const s = safeAreaYMm(mode, face, mc); return !!face && h <= s.bottomMm - s.topMm + 1e-6; };
    if (mode === 'uv_print') {
      const ps = printState.uvPrint;
      if ((ps.text || '').trim() && ps.icon && ps.textPosition === ps.iconPosition && getPositions(pers, mode).length > 1) return false;
      return !ps.icon || fits(getFaceCfg(pers, mode, ps.iconPosition), getIconHmm(mc));
    }
    if (mode !== 'engraving') return true;
    const ps = printState.engraving, gap = mc?.gap_height_mm ?? 0;
    const els = Object.keys(_EGV_EL).filter(k => _egvHas(ps, k)).map(k => {
      const face = getFaceCfg(pers, mode, ps[_EGV_EL[k][0]]), h = _egvH(ps, k, mc, face), y = ps[_EGV_EL[k][1]];
      const r = ySliderRangeMm(mode, face, mc, h);
      return { face, h, y: y != null ? clampMm(y, r.minMm, r.maxMm) : (r.minMm + r.maxMm) / 2 };
    });
    return els.every(a => fits(a.face, a.h) && els.every(b => a === b || a.face !== b.face
      || Math.abs(a.y - b.y) >= a.h / 2 + b.h / 2 + gap - 1e-6));
  }
  // Re-arrange (k first) until the layout is valid; true when it is.
  function _repairLayout(k) {
    const ps = printState.engraving;
    if (printState.mode === 'engraving') {
      const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
      for (const e of [...new Set([k, ...Object.keys(_EGV_EL)])].filter(e => _EGV_EL[e] && _egvHas(ps, e))) {
        if (_layoutOk()) break;
        _egvArrange(ps, pers, e, ps[_EGV_EL[e][1]]);
      }
    }
    return _layoutOk();
  }
  // true when the change had to be undone.
  function _guardLayout(before, k) {
    if (_layoutOk()) return false;
    const undone = !_repairLayout(k);
    if (!undone) {
      if (printState.mode === 'engraving') _egvRefreshControls();
    } else {
      printState.mode = before.mode;
      Object.assign(printState.engraving, before.engraving);
      Object.assign(printState.uvPrint, before.uvPrint);
      renderPrintsPanel();
      renderTabStrip();
    }
    applyEngravingPreview();
    return undone;
  }

  // Tells the customer when space decided the outcome: a move refused because
  // another element is there, an element moved / placed on another side to make
  // room, or a change undone. Shown in every .fd-art-notice, auto-hidden.
  let _artNoticeTimer = null;
  function _artNotice(msg) {
    ui.artNotice = msg;
    _q('.fd-art-notice').forEach(el => { el.textContent = msg; el.hidden = !msg; });
    clearTimeout(_artNoticeTimer);
    if (msg) _artNoticeTimer = setTimeout(() => _artNotice(''), 6000);
  }
  const _artNoticeHtml = () => `<div class="fd-art-notice" role="status" aria-live="polite" style="margin:0 0 12px;font-size:12px;color:#d0342c;"${ui.artNotice ? '' : ' hidden'}>${escapeText(ui.artNotice || '')}</div>`;
  function _spaceNotice(before, action, data, undone) {
    const mode = printState.mode;
    const key  = mode === 'engraving' ? 'engraving' : mode === 'uv_print' ? 'uvPrint' : null;
    if (action === 'set-mode') return _artNotice('');
    if (!key || before.mode !== mode) return;
    const a = before[key], b = printState[key];
    const els  = mode === 'engraving' ? ['text', 'icon', 'badge'] : ['text', 'icon'];
    const name = { text: 'text', icon: 'icon', badge: 'Special Text' };
    const has  = (ps, e) => e === 'text' ? (ps.text || '').trim().length > 0 : e === 'icon' ? !!ps.icon : !!ps.badge;
    const pos  = (ps, e) => ps[`${e}Position`];
    const side = k => `the ${_facePosLabel(k)}`;
    const target = (action.match(/text|icon|badge/) || [])[0];
    const isMove = /-position$/.test(action);
    if (isMove && has(b, target) && pos(a, target) !== data.position && pos(b, target) !== data.position) {
      const there = els.filter(e => e !== target && has(b, e) && pos(b, e) === data.position).map(e => `the ${name[e]}`);
      return _artNotice(there.length
        ? `There isn’t enough space on ${side(data.position)} for your ${name[target]} — ${there.join(' and ')} ${there.length > 1 ? 'are' : 'is'} already there.`
        : `Your ${name[target]} doesn’t fit on ${side(data.position)}.`);
    }
    if (undone) {
      const art    = e => (e === 'icon' ? 'an icon' : name[e]);
      const others = els.filter(e => e !== target && has(b, e)).map(e => `your ${name[e]}`).join(' and ');
      const what = action === 'set-text-orientation' ? 'vertical text here' : action === 'set-icon-size' ? 'a bigger icon here' : 'that';
      return _artNotice(target && !has(b, target) && others
        ? `There isn’t enough space on this bottle for ${art(target)} together with ${others}. Remove ${others} to add ${art(target)}.`
        : `There isn’t enough space for ${what}. Move or remove another element first.`);
    }
    if (action === 'set-icon' && mode === 'engraving' && !a.icon && !b.icon && !_artBlocked('icon'))
      return _artNotice('There isn’t enough space on this bottle for an icon.');
    for (const e of els) {
      if (!has(b, e) || pos(a, e) == null || pos(a, e) === pos(b, e) || (isMove && e === target)) continue;
      return _artNotice(has(a, e)
        ? `There wasn’t enough space on ${side(pos(a, e))}, so your ${name[e]} was moved to ${side(pos(b, e))}.`
        : `There isn’t enough space on ${side(pos(a, e))}, so your ${name[e]} was placed on ${side(pos(b, e))}.`);
    }
    if (mode === 'engraving' && has(b, 'text') && a.textOrientation === 'vertical' && b.textOrientation !== 'vertical'
        && action !== 'set-text-orientation')
      _artNotice('There isn’t enough space for vertical text here, so your text was switched to horizontal.');
  }

  function handlePrintAction(action, data) {
    const before = _layoutSnap();
    _runPrintAction(action, data);
    _spaceNotice(before, action, data, _guardLayout(before, (action.match(/text|icon|badge/) || [])[0]));
  }

  function _runPrintAction(action, data) {
    const mode  = printState.mode;
    const isEgv = mode === 'engraving';
    const isUV  = mode === 'uv_print';
    const ps    = isEgv ? printState.engraving : isUV ? printState.uvPrint : null;

    if (action === 'set-mode') {
      if (printState.mode === data.mode) return;
      // Defense-in-depth for the per-variant exclusion: every legit caller only
      // offers available modes, but never accept a disallowed one regardless.
      if (!artAvailableModes().includes(data.mode)) return;
      printState.mode = data.mode;
      ui.iconViewedSeries = null; // each mode has its own series list
      if (data.mode !== 'engraving') {
        if (ui.artCardOpen === 'badge') ui.artCardOpen = 'text';
        if (ui.mobilePopover.artSubPill === 'badge') ui.mobilePopover.artSubPill = 'text';
        if (ui.mobilePopover.posElement === 'badge') ui.mobilePopover.posElement = null;
      }

      const newPs  = data.mode === 'engraving' ? printState.engraving : data.mode === 'uv_print' ? printState.uvPrint : null;
      const newCfg = data.mode === 'engraving' ? config.prints.engraving : data.mode === 'uv_print' ? config.prints.uvPrint : null;
      if (newPs && newCfg) {
        // Pre-select defaults for options that don't require user input
        if (!newPs.font && newCfg.fonts?.length)            newPs.font       = newCfg.fonts[0];
        if (!newPs.iconSeries && newCfg.iconSeries?.length) newPs.iconSeries = newCfg.iconSeries[0];
        if (data.mode === 'uv_print' && !newPs.fontColor && newCfg.fontColors?.length)
          newPs.fontColor = newCfg.fontColors[0];
        const pers      = config.bottles[state.bottleProductId]?.personalizationSettings;
        const modeCfg   = getModeCfg(pers, data.mode);
        const positions = getPositions(pers, data.mode);

        // Resolve text/icon positions to a valid key, defaulting to the position
        // flagged default:true (else the first). Engraving: both start on the
        // default face, except the icon skips text-only faces (too short for the
        // icon box). UV print: text takes the default, icon takes a DIFFERENT
        // face — text & icon can't share a face, so the exclusivity rule wins.
        const defKey = getDefaultPositionKey(pers, data.mode);
        if (!getFaceCfg(pers, data.mode, newPs.textPosition)) newPs.textPosition = defKey;
        if (data.mode === 'uv_print') {
          // UV: enforce different positions for text vs icon.
          newPs.iconPosition = getOtherPositionKey(pers, data.mode, newPs.textPosition) ?? newPs.textPosition;
        } else if (!canFitIconOnFace(getFaceCfg(pers, data.mode, newPs.iconPosition), modeCfg)) {
          newPs.iconPosition = getIconFaceKey(pers, defKey) ?? defKey;
        }

        // Initialise / clamp Y placements within the new face's safe area.
        // UV text has no Y (always full-height) → keep null.
        const tFace = getFaceCfg(pers, data.mode, newPs.textPosition);
        const iFace = getFaceCfg(pers, data.mode, newPs.iconPosition);
        if (data.mode === 'engraving' && tFace) {
          const range = ySliderRangeMm('engraving', tFace, modeCfg, getTextHmm('engraving', modeCfg, newPs.textOrientation, tFace));
          newPs.textY_mm = newPs.textY_mm == null
            ? (range.minMm + range.maxMm) / 2
            : clampMm(newPs.textY_mm, range.minMm, range.maxMm);
        } else if (data.mode === 'uv_print') {
          newPs.textY_mm = null;
        }
        if (iFace) {
          const range = ySliderRangeMm(data.mode, iFace, modeCfg, getIconHmm(modeCfg));
          newPs.iconY_mm = newPs.iconY_mm == null
            ? (range.minMm + range.maxMm) / 2
            : clampMm(newPs.iconY_mm, range.minMm, range.maxMm);
        }
      }
      renderPrintsPanel();
      // Switching mode must not scroll the carousel forward — keep the current
      // step (e.g. Personalisation) centred; the next step only advances on Next.
      if (ui.mobilePopover.open && ui.mobilePopover.tab === 'art') {
        requestAnimationFrame(() => centerActiveStep(false));
      }
      applyEngravingPreview();
      // Mode selected → rotate to the position's face (the default face when empty,
      // else the occupied face) EVEN with no content yet, so picking Engrave /
      // Colour Print turns the bottle from the no-mode "back" view to the facing
      // where the personalization will go.
      rotateArtCameraToFace(artFrontPriorityFace());
      updateGuideVisibility();
      return;
    }
    // set-text-position and set-icon-position share the same shape. Reclamp
    // the element's Y into the new face's safe area; on UV print, auto-swap
    // the *other* element to a different position to maintain face exclusivity.
    if (action === 'set-text-position' || action === 'set-icon-position') {
      const cur    = isEgv ? printState.engraving : isUV ? printState.uvPrint : null;
      if (!cur) return;
      const isText    = action === 'set-text-position';
      const posKey    = isText ? 'textPosition' : 'iconPosition';
      const yKey      = isText ? 'textY_mm'     : 'iconY_mm';
      const otherKey  = isText ? 'iconPosition' : 'textPosition';
      const otherYKey = isText ? 'iconY_mm'     : 'textY_mm';
      if (cur[posKey] === data.position) return;
      const pers       = config.bottles[state.bottleProductId]?.personalizationSettings;
      const modeCfg    = getModeCfg(pers, mode);
      const newFaceCfg = getFaceCfg(pers, mode, data.position);
      if (isEgv && cur.badge) { _egvMoveTo(cur, pers, isText ? 'text' : 'icon', data.position); return; }
      // Engraving text-only faces: the icon can't move there, and the swap below
      // can't push it there — refused with an icon placed, else swap skipped.
      let keepIconFace = false;
      if (isEgv) {
        if (!isText && !canFitIconOnFace(newFaceCfg, modeCfg)) return;
        if (isText && cur.textPosition != null && cur.iconPosition === data.position
            && !canFitBothOnFace(newFaceCfg, modeCfg)
            && !canFitIconOnFace(getFaceCfg(pers, mode, cur.textPosition), modeCfg)) {
          if (cur.icon != null) return;
          keepIconFace = true;
        }
      }
      const oldPos = cur[posKey];
      cur[posKey]  = data.position;

      // Engraving cross-face swap when face can't hold both elements.
      let egvSwapped = false;
      if (isEgv && !keepIconFace && oldPos != null && cur[otherKey] === data.position
          && !canFitBothOnFace(newFaceCfg, modeCfg)) {
        cur[otherKey] = oldPos;
        egvSwapped = true;
        const oldFace = getFaceCfg(pers, mode, oldPos);
        if (oldFace) {
          const swappedHmm = otherKey === 'textPosition'
            ? getTextHmm(mode, modeCfg, cur.textOrientation, oldFace)
            : (getIconHmm(modeCfg));
          const range = ySliderRangeMm(mode, oldFace, modeCfg, swappedHmm);
          if (otherKey === 'textPosition' && isUV) {
            cur.textY_mm = null;
          } else {
            cur[otherYKey] = cur[otherYKey] == null
              ? (range.minMm + range.maxMm) / 2
              : clampMm(cur[otherYKey], range.minMm, range.maxMm);
          }
        }
      }

      // Reclamp / initialise the moved element's Y into the new safe area.
      if (isText && isUV) {
        cur.textY_mm = null; // UV text is always full-vertical
      } else if (newFaceCfg) {
        const elemHmm = isText
          ? getTextHmm(mode, modeCfg, cur.textOrientation, newFaceCfg)
          : (getIconHmm(modeCfg));
        const range   = ySliderRangeMm(mode, newFaceCfg, modeCfg, elemHmm);
        cur[yKey] = cur[yKey] == null
          ? (range.minMm + range.maxMm) / 2
          : clampMm(cur[yKey], range.minMm, range.maxMm);
      }

      // Revert text to horizontal FIRST if the move makes vertical invalid, so
      // the overlap avoidance below works against the final text orientation
      // (otherwise the icon is placed for the vertical block, then they overlap).
      if (isEgv && cur.textOrientation === 'vertical'
          && !isVerticalAllowed(cur, pers, mode)) {
        cur.textOrientation = 'horizontal';
      }

      // Engraving same-face arrangement: if the move lands the element on the
      // OTHER's face and the face holds both (egvSwapped didn't fire), call
      // the same Case B routine used on add so they don't overlap.
      if (isEgv && !egvSwapped && cur.textPosition === cur.iconPosition) {
        avoidOverlapOnAdd(cur, mode, pers, isText ? 'text' : 'icon');
      }

      // UV print face exclusivity: if the other element is on the same key,
      // auto-swap it to the first other available position.
      let uvSwapped = false;
      if (isUV) {
        if (cur[otherKey] === data.position) {
          cur[otherKey] = getOtherPositionKey(pers, mode, data.position) ?? cur[otherKey];
          uvSwapped = true;
          const oppFaceCfg = getFaceCfg(pers, mode, cur[otherKey]);
          if (oppFaceCfg && otherYKey === 'iconY_mm') {
            const elemHmm = getIconHmm(modeCfg);
            const range = ySliderRangeMm(mode, oppFaceCfg, modeCfg, elemHmm);
            cur[otherYKey] = cur[otherYKey] == null
              ? (range.minMm + range.maxMm) / 2
              : clampMm(cur[otherYKey], range.minMm, range.maxMm);
          }
          // textY_mm stays null for UV print regardless.
        }
      }

      _swap('[data-print-action="set-text-position"]', el => el.dataset.position === cur.textPosition);
      _swap('[data-print-action="set-icon-position"]', el => el.dataset.position === cur.iconPosition);
      // Face-dependent controls may need to appear / disappear (slider only
      // exists when the new face has a usable range; orientation only shows
      // when the new text face fits vertical text).
      if (isEgv) {
        _updateOrientation();
        _updatePlacement('text');
      }
      _updatePlacement('icon');
      applyEngravingPreview();
      const movedKind = isText ? 'text' : 'icon';
      if (uvSwapped || egvSwapped) {
        setTimeout(() => {
          rotateArtCameraForPositionChange(movedKind, data.position);
        }, 350);
      } else {
        rotateArtCameraForPositionChange(movedKind, data.position);
      }
      return;
    }
    // Y slider drag. Raw mm, full intrinsic range. Engraving same-face
    // overlap handled by placeForSameFaceDrag (swap or hard-stop).
    if (action === 'set-text-y' || action === 'set-icon-y') {
      if (!ps) return;
      const isText   = action === 'set-text-y';
      const myKey    = isText ? 'textY_mm'     : 'iconY_mm';
      const otherKey = isText ? 'iconY_mm'     : 'textY_mm';
      const myPos    = isText ? ps.textPosition : ps.iconPosition;
      const pers     = config.bottles[state.bottleProductId]?.personalizationSettings;
      const modeCfg  = getModeCfg(pers, mode);
      const myFace   = getFaceCfg(pers, mode, myPos);
      if (!modeCfg || !myFace) return;

      const intent = parseFloat(data.value);
      if (!isFinite(intent)) return;

      if (mode === 'engraving' && ps.badge) {
        _egvDrag(ps, pers, isText ? 'text' : 'icon', intent);
      } else if (mode === 'engraving') {
        const { myY, otherY } = placeForSameFaceDrag(ps, modeCfg, myFace, isText ? 'text' : 'icon', intent);
        ps[myKey] = myY;
        if (otherY != null) ps[otherKey] = otherY;
      } else {
        const cfg = getSliderConfig(mode, myFace, modeCfg, myKey, ps);
        ps[myKey] = clampMm(intent, cfg.min, cfg.max);
      }

      schedulePreview();
      if (data.firstInput) rotateArtCameraForElement(isText ? 'text' : 'icon');
      return;
    }
    if (action === 'set-badge-position' || action === 'set-badge-y') {
      if (!isEgv || !ps.badge) return;
      const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
      if (action === 'set-badge-position') {
        if (ps.badgePosition !== data.position) _egvMoveTo(ps, pers, 'badge', data.position);
        return;
      }
      const intent = parseFloat(data.value);
      if (!isFinite(intent)) return;
      _egvDrag(ps, pers, 'badge', intent);
      schedulePreview();
      if (data.firstInput) rotateArtCameraForElement('badge');
      return;
    }
    if (action === 'set-text-orientation') {
      if (!ps || !isEgv) return;
      const orientation = data.orientation;
      if (ps.textOrientation === orientation) return;
      ps.textOrientation = orientation;

      if (orientation === 'vertical') {
        const pers    = config.bottles[state.bottleProductId]?.personalizationSettings;
        const modeCfg = getModeCfg(pers, 'engraving');
        const tFace   = getFaceCfg(pers, 'engraving', ps.textPosition);
        if (tFace && modeCfg && ps.badge) {
          _egvArrange(ps, pers, 'text', ps.textY_mm, false);
          _egvRefreshControls();
        } else if (tFace && modeCfg) {
          placeTextAndIconForVertical(ps, tFace, modeCfg);
        }
      }

      _swap('[data-print-action="set-text-orientation"]', el => el.dataset.orientation === ps.textOrientation);
      // Orientation flip changes text height → text placement slider's range
      // may collapse or expand; ensure it appears/disappears accordingly.
      _updatePlacement('text');
      applyEngravingPreview();
      rotateArtCameraForElement('text');
      return;
    }
    if (!ps) return;

    if (action === 'set-text-size') {
      if (ps.textSize === data.size) return;
      ps.textSize = data.size;
      _swap('[data-print-action="set-text-size"]', el => el.dataset.size === ps.textSize);
      applyEngravingPreview();
      rotateArtCameraForElement('text');
      return;
    }
    if (action === 'set-icon-size') {
      if (ps.iconSize === data.size) return;
      ps.iconSize = data.size;
      // A bigger/smaller icon changes its vertical placement range → re-clamp.
      const pers    = config.bottles[state.bottleProductId]?.personalizationSettings;
      const modeCfg = getModeCfg(pers, mode);
      const iFace   = getFaceCfg(pers, mode, ps.iconPosition);
      if (modeCfg && iFace && ps.iconY_mm != null) {
        const range = ySliderRangeMm(mode, iFace, modeCfg, getIconHmm(modeCfg));
        ps.iconY_mm = clampMm(ps.iconY_mm, range.minMm, range.maxMm);
      }
      if (isEgv && ps.badge && ps.icon) { _egvArrange(ps, pers, 'icon', ps.iconY_mm); _egvRefreshControls(); }
      _swap('[data-print-action="set-icon-size"]', el => el.dataset.size === ps.iconSize);
      _updatePlacement('icon');
      applyEngravingPreview();
      rotateArtCameraForElement('icon');
      return;
    }

    if (action === 'set-font') {
      const name = decodeURIComponent(data.fontName);
      const cfg  = artCurrentCfg();
      ps.font = cfg.fonts?.find(f => f.name === name) || null;
      _swap('.fd-font-btn', el => decodeURIComponent(el.dataset.fontName || '') === ps.font?.name);
      applyEngravingPreview();
      rotateArtCameraForElement('text');

    } else if (action === 'set-icon-series') {
      // Browse-only: switch which series' icons are shown. The current
      // selection (ps.icon / ps.iconSeries) is left untouched so it isn't
      // cleared — the user is just looking at a different series.
      const name   = decodeURIComponent(data.seriesName);
      const cfg    = artCurrentCfg();
      const series = cfg.iconSeries?.find(s => s.name === name) || null;
      ui.iconViewedSeries = name;
      _swap('.fd-icon-series-btn', el => decodeURIComponent(el.dataset.seriesName || '') === name);
      const icons = series?.icons || [];
      _q('.fd-icon-row').forEach(row => { row.innerHTML = icons.map(ic => _iconBtnHtml(ic, ps.icon?.name)).join(''); });

    } else if (action === 'set-icon') {
      if (_artBlocked('icon')) return;
      const name = decodeURIComponent(data.iconName);
      const cfg  = artCurrentCfg();
      // Resolve the clicked icon from the VIEWED series (the row showing it);
      // selecting records that series on ps.iconSeries (for cart/review).
      const viewedName   = ui.iconViewedSeries || ps.iconSeries?.name || cfg.iconSeries?.[0]?.name || null;
      const viewedSeries = cfg.iconSeries?.find(s => s.name === viewedName) || cfg.iconSeries?.[0] || null;
      const icon = viewedSeries?.icons?.find(i => i.name === name) || null;
      // Engraving with no face tall enough for the icon box → icon unavailable.
      if (isEgv && ps.icon?.name !== name
          && getIconFaceKey(config.bottles[state.bottleProductId]?.personalizationSettings, ps.iconPosition) == null) return;
      const wasNull = ps.icon == null;
      if (ps.icon?.name === name) { ps.icon = null; }
      else                        { ps.icon = icon; ps.iconSeries = viewedSeries; }
      const isNull = ps.icon == null;
      if (wasNull && !isNull) {
        const pers = config.bottles[state.bottleProductId]?.personalizationSettings;
        if (enforceUvSingleFaceExclusivity(ps, mode, pers, 'icon')) {
          renderPrintsPanel();
          renderTabStrip();
          applyEngravingPreview();
          rotateArtCameraForElement('icon');
          return;
        }
        // Flip vertical text to horizontal FIRST when the icon won't fit beside
        // it vertically — otherwise avoidOverlapOnAdd places the icon against the
        // tall vertical block, then the flip leaves them overlapping.
        if (isEgv && ps.textOrientation === 'vertical' && !isVerticalAllowed(ps, pers, mode)) {
          ps.textOrientation = 'horizontal';
        }
        avoidOverlapOnAdd(ps, mode, pers, 'icon');
      }
      _swap('.fd-icon-btn', el => decodeURIComponent(el.dataset.iconName || '') === ps.icon?.name);
      _swap('[data-print-action="set-text-position"]', el => el.dataset.position === ps.textPosition);
      _swap('[data-print-action="set-icon-position"]', el => el.dataset.position === ps.iconPosition);
      if (isEgv) _swap('[data-print-action="set-text-orientation"]', el => el.dataset.orientation === ps.textOrientation);
      if (wasNull !== isNull) {
        _chk('[data-art-check="icon"]', !isNull);
        _q('.fd-art-subtoggle-btn[data-which="icon"]').forEach(b => b.classList.toggle('has-check', !isNull));
        _syncArtPosPill();
        renderTabStrip();
        refreshPurchaseBar();
        if (ui.mobilePopover.open && ui.mobilePopover.tab === 'art') renderPopoverChrome();
      }
      applyEngravingPreview();
      rotateArtCameraForElement('icon');

    } else if (action === 'set-badge' && isEgv) {
      const d = artBadgeDesigns().find(x => x.name === decodeURIComponent(data.designName));
      if (!d || _artBlocked('badge')) return;
      if (ps.badge?.name === d.name) ps.badge = null;
      else {
        const pers    = config.bottles[state.bottleProductId]?.personalizationSettings;
        const modeCfg = getModeCfg(pers, 'engraving');
        const fits    = getPositions(pers, 'engraving').filter(p => canFitBadgeOnFace(p, modeCfg));
        if (!fits.length) return;
        const snap = { ..._egvSnap(ps), badge: ps.badge };
        ps.badge = d;
        if (!fits.some(p => p.key === ps.badgePosition)) {
          ps.badgePosition = (fits.find(p => p.default) || fits[0]).key;
          ps.badgeY_mm = null;
        }
        if (ps.textOrientation === 'vertical' && !isVerticalAllowed(ps, pers, mode)) ps.textOrientation = 'horizontal';
        if (!_egvArrange(ps, pers, 'badge', ps.badgeY_mm)) {
          Object.assign(ps, snap);
          const others = [(ps.text || '').trim() && 'your text', ps.icon && 'your icon'].filter(Boolean).join(' and ');
          ui.badgeMsg = others
            ? `There isn’t enough space on this bottle for Special Text together with ${others}. Remove ${others} to add Special Text.`
            : 'There isn’t enough space on this bottle for this Special Text design.';
          renderPrintsPanel();
          return;
        }
      }
      renderPrintsPanel();
      renderTabStrip();
      applyEngravingPreview();
      rotateArtCameraForElement('badge');

    } else if (action === 'set-font-color' && isUV) {
      const color = decodeURIComponent(data.color);
      const fc    = artCurrentCfg()?.fontColors?.find(c => c.color === color) || null;
      // Font colour must ALWAYS keep one option selected — selecting only ever
      // switches the colour, never toggles it off (and an unknown colour is ignored
      // rather than clearing the current one). Default is set on UV-print entry.
      if (fc) ps.fontColor = fc;
      _swap('[data-print-action="set-font-color"]', el => decodeURIComponent(el.dataset.color || '') === ps.fontColor?.color);
      _txt('.fd-fontcolor-label', ps.fontColor?.label || '');
      applyEngravingPreview();
      rotateArtCameraForElement('text');

    }
  }

  // Event delegation (root = this section only)
  const _sectionRoot = document.getElementById(`shopify-section-${SID}`);

  _sectionRoot?.addEventListener('click', (e) => {
    const target = e.target;
    const _closeAllPtMenus = () => {
      _sectionRoot.querySelectorAll('.fd-pt-btn').forEach(b => b.setAttribute('aria-expanded', 'false'));
      _sectionRoot.querySelectorAll('.fd-pt-menu, .fd-pt-overlay').forEach(m => m.hidden = true);
    };
    const ptToggle = target.closest('[data-action="toggle-pt-menu"]');
    if (ptToggle) {
      const open = ptToggle.getAttribute('aria-expanded') === 'true';
      _closeAllPtMenus();
      if (!open) {
        ptToggle.setAttribute('aria-expanded', 'true');
        const parent = ptToggle.parentElement;
        parent.querySelector('.fd-pt-menu').hidden = false;
        parent.querySelector('.fd-pt-overlay').hidden = false;
      }
      return;
    }
    if (target.classList.contains('fd-pt-overlay')) {
      _closeAllPtMenus();
      return;
    }
    const ptTypeBtn = target.closest('.fd-pt-type[data-pt-series]');
    if (ptTypeBtn) {
      const series = ptTypeBtn.dataset.ptSeries;
      const menu   = ptTypeBtn.closest('.fd-pt-menu');
      menu.querySelectorAll('.fd-pt-type').forEach(b => b.classList.toggle('is-active', b.dataset.ptSeries === series));
      menu.querySelectorAll('.fd-pt-size').forEach(s => s.hidden = s.dataset.ptSeries !== series);
      return;
    }
    if (target.closest('.fd-pt-menu .is-current')) { e.preventDefault(); return; }
    if (!target.closest('.fd-product-info')) _closeAllPtMenus();
    // Placeholder bottle-type / size links: don't navigate until real URLs are
    // wired. Action links (e.g. the T&C trigger) carry data-action and must fall
    // through to the dispatch below, so they're excluded here.
    const placeholderLink = target.closest('a[href="#"]:not([data-action])');
    if (placeholderLink) { e.preventDefault(); return; }
    const tabBtn = target.closest('.fd-tab[data-tab], .fd-mobile-tab[data-tab]');
    if (tabBtn) {
      const tab = tabBtn.dataset.tab;
      _artNotice('');
      if (window.matchMedia('(max-width: 1023px)').matches) openPopover(tab);
      else                                                     activateTab(tab);
      return;
    }
    const printEl = target.closest('[data-print-action]');
    if (printEl) {
      // Skip input clicks — text input + Y sliders are driven by their own
      // `input` events (bindTextInputDebounce / bindYSliders), and we don't
      // want a stray click on the slider track to fire handlePrintAction
      // without the dragged value.
      if (printEl.tagName === 'INPUT') return;
      handlePrintAction(printEl.dataset.printAction, printEl.dataset);
      // The grouped Position detail has no .fd-art-card, so the surgical
      // _updatePlacement/_updateOrientation refreshes no-op — re-render the
      // body so Facing/Orientation/Size stay consistent after a change.
      if (_inArtPosDetail()) renderPopover();
      return;
    }
    const actEl = target.closest('[data-action]');
    if (!actEl) return;
    const a = actEl.dataset.action;
    const ds = actEl.dataset;
    // Switching what's on screen (art tab, position screen, step) drops the last space notice.
    if (/^(art-|step-|popover-|close-popover)/.test(a)) _artNotice('');
    if      (a === 'toggle-section')     { ui.collapsed[ds.key] = !ui.collapsed[ds.key]; renderActivePane(); }
    else if (a === 'select-body-color')   selectBodyColor(ds.id);
    else if (a === 'select-acc-product')  selectAccProduct(ds.type, ds.id);
    else if (a === 'select-acc-color')    selectAccColor(ds.type, ds.id);
    else if (a === 'open-fullscreen')     openFullscreen();
    else if (a === 'toggle-guide') {
      ui.guideHidden = !ui.guideHidden;
      _q('.fd-toggle-guide').forEach(b => b.setAttribute('aria-pressed', String(ui.guideHidden)));
      updateGuideVisibility();
    }
    else if (a === 'rotate-front-back')   rotateFrontBack();
    else if (a === 'close-fullscreen')    closeFullscreen();
    else if (a === 'close-popover')       closePopover();
    else if (a === 'step-prev')           popoverPrev();
    else if (a === 'step-next')           popoverNext();
    else if (a === 'step-jump')           popoverGoTo(+actEl.dataset.stepIdx);
    else if (a === 'popover-next')        popoverNext();
    else if (a === 'popover-final')       popoverFinal();
    else if (a === 'art-subtoggle')       {
      // Flipping Text ↔ Icon resets the popover flow to that sub-toggle's
      // FIRST content step. The Mode step (when shown) is shared, so we keep
      // the user past it if they were already on Content / Font / Position.
      if (ds.which !== 'badge') _dropEmptyBadge();
      ui.mobilePopover.artSubPill = ds.which;
      ui.mobilePopover.error = '';
      ui.mobilePopover.posElement = null;
      const newSteps = tabSteps('art');
      const contentIdx = newSteps.findIndex(s => s.key === 'content');
      ui.mobilePopover.stepIdx = contentIdx >= 0 ? contentIdx : 0;
      renderPopover();
      // The Text/Icon flows can differ in step count, so the pager may rebuild
      // and reset its scroll to step 0. Re-center the active step once layout
      // has settled so the carousel follows the selection.
      requestAnimationFrame(() => centerActiveStep(false));
      // Sub-pill switch targets the now-active element's face. No-op
      // when the element is empty — there's nothing on the bottle to
      // look at on that sub-pill yet.
      rotateArtCameraForElement(ds.which);
    }
    else if (a === 'art-pos-pick' || a === 'art-pos-element') {
      // Position card-select / element toggle → drill into that element's
      // Layout/Alignment detail. Disabled buttons (no content) are no-ops.
      if (actEl.disabled) return;
      ui.mobilePopover.posElement = ds.which;
      if (a === 'art-pos-pick') ui.mobilePopover.posDetailTab = 'layout';
      renderPopover();
      rotateArtCameraForElement(ds.which);
    }
    else if (a === 'art-pos-tab')         { ui.mobilePopover.posDetailTab = ds.tab; renderPopover(); }
    else if (a === 'art-pos-back')        { ui.mobilePopover.posElement = null; renderPopover(); }
    else if (a === 'art-goto-position')   {
      // Position pill (shown on every Art step) → jump to the Position step. The
      // sequential flow only carries a position step for a sub-pill whose element
      // is filled, so if the active one is empty, switch to the sub-pill that has
      // content (the pill is only enabled when one does).
      let steps = tabSteps('art');
      let idx = steps.findIndex(s => s.key === 'position');
      for (const pill of ['text', 'icon', 'badge']) {
        if (idx >= 0) break;
        ui.mobilePopover.artSubPill = pill;
        steps = tabSteps('art');
        idx = steps.findIndex(s => s.key === 'position');
      }
      if (idx >= 0) { ui.mobilePopover.stepIdx = idx; ui.mobilePopover.posElement = null; renderPopover(); }
    }
    else if (a === 'art-card-toggle')     {
      // Desktop: only ONE Art card open at a time. Clicking the already-open
      // card keeps it open; clicking the other expands it and collapses the first.
      if (ds.card !== 'badge') _dropEmptyBadge();
      if (ui.artCardOpen !== ds.card) { ui.artCardOpen = ds.card; renderArtPane(); }
    }
    else if (a === 'art-sub-switch')      {
      // Desktop Text/Icon switch. A label sets its side; the track flips.
      const next = ds.which || (ui.artCardOpen === 'icon' ? 'text' : 'icon');
      if (next !== 'badge') _dropEmptyBadge();
      if (ui.artCardOpen !== next) { ui.artCardOpen = next; swapArtSub(next); }
    }
    else if (a === 'ar-view')             launchAR();
    else if (a === 'add-to-cart')         { if (_loadingCount === 0) openReview(); } // locked while loading
    else if (a === 'close-review')        closeReview();
    else if (a === 'review-add-to-cart')  addToCart();
    else if (a === 'toggle-tc') {
      // Whole T&C row toggles the checkbox. Skip a <label> (its `for` flips the
      // input natively) AND the input itself (clicking a `for` label also fires
      // a bubbling synthetic click on the input) — both already flip it, so
      // toggling again here would cancel out, leaving only "dead" clicks (the
      // blue background) to flip it. The T&C link has its own action, so it
      // never reaches here.
      const cb = document.getElementById('fd-tc-check');
      if (!cb || target === cb || target.closest('label')) return;
      cb.checked = !cb.checked;
      if (cb.checked) _clearTcError();
    }
    else if (a === 'open-tc')             { e.preventDefault(); openTc(); }
    else if (a === 'close-tc')            closeTc();
    else if (a === 'open-faq')            { e.preventDefault(); const fq = document.getElementById(ds.faqTarget); if (fq) fq.open = true; }
  });

  // Esc closes the T&C modal (it sits above the review modal).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const m = document.getElementById('fd-tc-modal');
    if (m && m.classList.contains('is-open')) closeTc();
  });

  // Ticking the agreement (via label or keyboard) clears the compulsory error.
  document.getElementById('fd-tc-check')?.addEventListener('change', (e) => {
    if (e.target.checked) _clearTcError();
  });

  // Drag-to-scroll (mouse) for horizontal rows. Delegated so re-rendered
  // rows keep working; the click after a real drag is suppressed in capture
  // phase so dragging never selects an option.
  const _DRAG_ROW_SEL = '.fd-icon-row, .fd-icon-series-row, .fd-font-row, .fd-pill-row__items';
  const _drag = { row: null, x: 0, left: 0, moved: false };
  _sectionRoot?.addEventListener('pointerdown', (e) => {
    _drag.moved = false;
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const row = e.target.closest(_DRAG_ROW_SEL);
    if (!row || row.scrollWidth <= row.clientWidth) return;
    _drag.row = row; _drag.x = e.clientX; _drag.left = row.scrollLeft;
    e.preventDefault();
  });
  window.addEventListener('pointermove', (e) => {
    if (!_drag.row) return;
    const dx = e.clientX - _drag.x;
    if (!_drag.moved && Math.abs(dx) < 5) return;
    _drag.moved = true;
    _drag.row.classList.add('is-dragging');
    _drag.row.scrollLeft = _drag.left - dx;
  });
  window.addEventListener('pointerup', () => {
    if (!_drag.row) return;
    _drag.row.classList.remove('is-dragging');
    _drag.row = null;
  });
  _sectionRoot?.addEventListener('click', (e) => {
    if (!_drag.moved) return;
    _drag.moved = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  // Debounce + ignore subpixel pulses (mobile address-bar). Aspect is
  // refreshed via resizeRenderer; the camera is NOT reset, so the user
  // stays at their current view.
  let _resizeTimer = null, _lastResizeSize = null;
  const resizeObserver = new ResizeObserver(entries => {
    const r = entries[0]?.contentRect;
    if (!r) return;
    const w = Math.round(r.width), h = Math.round(r.height);
    if (_lastResizeSize && Math.abs(w - _lastResizeSize.w) < 4 && Math.abs(h - _lastResizeSize.h) < 4) return;
    _lastResizeSize = { w, h };
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(() => {
      resizeRenderer();
      positionMobileTabIndicator();
    }, 120);
  });
  resizeObserver.observe(document.getElementById('fd-canvas-container'));

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
